// src/utils/reportContentGenerator.js
import EnhancedPDFGenerator from "./enhancedPdfGenerator.js";
import PDFChartIntegration from "./pdfChartIntegration.js";
import { processEventProgressionData } from "../components/EventProgression/dataProcessor.js";
import { extractElementaryActions } from "./scenarioDataNormalizer.js";
import { STATUS_LABELS } from "./ctiFilters.js";

const LMETA = {
  fr: {
    w5ReportTitle: "Rapport Atelier 5 - Traitement du Risque",
    defaultReportName: "Rapport EBIOS RM",
    riskAnalysisSubject: "Analyse de risques cybers\u00e9curit\u00e9",
    organizationFallback: "Organisation",
  },
  en: {
    w5ReportTitle: "Workshop 5 Report - Risk Treatment",
    defaultReportName: "EBIOS RM Report",
    riskAnalysisSubject: "Cybersecurity Risk Analysis",
    organizationFallback: "Organization",
  },
};

const LVIZ = {
  fr: {
    prevWorkshopsViz: "Visualisations des Ateliers Pr\u00e9c\u00e9dents",
    w2RadarTitle: "Atelier 2 - Visualisation des Couples SR/OV",
    w2RadarDesc:
      "Cette visualisation pr\u00e9sente les couples Sources de Risque / Objectifs Vis\u00e9s \u00e9valu\u00e9s dans l'Atelier 2.",
    w3RadarTitle: "Atelier 3 - Radar des Parties Prenantes",
    w3RadarDesc:
      "Cette visualisation pr\u00e9sente l'analyse des parties prenantes de l'Atelier 3.",
    stakeholderTitle: "Radar des Parties Prenantes",
    stakeholderDesc:
      "Cette visualisation pr\u00e9sente la cartographie des parties prenantes identifi\u00e9es lors de l'Atelier 3, positionn\u00e9es selon leur niveau de menace et leurs caract\u00e9ristiques de fiabilit\u00e9 cyber et d'exposition.",
    stakeholderCaption: "Radar des Parties Prenantes - Atelier 3",
    stakeholderError:
      "Note: Erreur lors de l'ajout de la visualisation radar des parties prenantes.",
    riskTitle: "Visualisation Radar des Risques",
    riskDesc:
      "Cette visualisation pr\u00e9sente la cartographie des couples Sources de Risque / Objectifs Vis\u00e9s identifi\u00e9s lors de l'Atelier 2.",
    riskCaption: "Radar de visualisation des couples SR/OV - Atelier 2",
  },
  en: {
    prevWorkshopsViz: "Visualizations from Previous Workshops",
    w2RadarTitle: "Workshop 2 - Risk Sources / Target Objectives Visualization",
    w2RadarDesc:
      "This visualization presents the Risk Sources / Target Objectives pairs evaluated in Workshop 2.",
    w3RadarTitle: "Workshop 3 - Stakeholder Radar",
    w3RadarDesc:
      "This visualization presents the stakeholder analysis from Workshop 3.",
    stakeholderTitle: "Stakeholder Radar",
    stakeholderDesc:
      "This visualization presents the mapping of stakeholders identified during Workshop 3, positioned according to their threat level and their cyber reliability and exposure characteristics.",
    stakeholderCaption: "Stakeholder Radar - Workshop 3",
    stakeholderError:
      "Note: Error while adding the stakeholder radar visualization.",
    riskTitle: "Risk Radar Visualization",
    riskDesc:
      "This visualization presents the mapping of Risk Source / Target Objective pairs identified during Workshop 2.",
    riskCaption: "RS/TO Pairs Radar Visualization - Workshop 2",
  },
};

const LCH2 = {
  fr: {
    heading: "2. ATELIER 1 : CADRAGE ET CONTEXTE",
    introTitle: "CADRAGE ET CONTEXTE",
    introText:
      "Cette section pr\u00e9sente le contexte de l'analyse, le p\u00e9rim\u00e8tre de l'\u00e9tude et le socle de s\u00e9curit\u00e9 initial.",
    s21: "2.1. FICHE D'IDENTIT\u00c9 DE L'\u00c9TUDE",
    identityHeaders: ["Information", "D\u00e9tail"],
    rOrganisation: "Organisation",
    rPerimetre: "P\u00e9rim\u00e8tre",
    rPeriode: "P\u00e9riode",
    rVersion: "Version",
    rReference: "R\u00e9f\u00e9rence",
    notSpecifiedF: "Non sp\u00e9cifi\u00e9e",
    notDefinedM: "Non d\u00e9fini",
    notDefinedF: "Non d\u00e9finie",
    s22: "2.2. MISSIONS PRINCIPALES",
    missionNoDesc: "Mission sans description",
    noneDocumented: "Aucune mission sp\u00e9cifique n'a \u00e9t\u00e9 document\u00e9e.",
    s23: "2.3. CONTEXTE M\u00c9TIER & ENJEUX",
    bvCountIntro:
      "{n} valeurs m\u00e9tier identifi\u00e9es et leurs enjeux associ\u00e9s.",
    bvHeaders: [
      "Valeur M\u00e9tier",
      "Description",
      "Criticit\u00e9",
      "\u00c9v\u00e9nements Redout\u00e9s",
    ],
    unnamed: "Sans nom",
    noneBv: "Aucune valeur m\u00e9tier identifi\u00e9e.",
    s24: "2.4. \u00c9TAT DES LIEUX DU SOCLE DE S\u00c9CURIT\u00c9",
    complianceIntro:
      "Conformit\u00e9 \u00e9valu\u00e9e sur {n} r\u00e9f\u00e9rentiel(s) de s\u00e9curit\u00e9.",
    stdHeaders: ["R\u00e9f\u00e9rentiel", "Progression", "Statut"],
    unknown: "Inconnu",
    inProgress: "En cours",
    noStandards:
      "Aucun r\u00e9f\u00e9rentiel de s\u00e9curit\u00e9 sp\u00e9cifique n'a \u00e9t\u00e9 rattach\u00e9 pour le moment.",
    deSynthesis: "Synth\u00e8se des \u00c9v\u00e9nements Redout\u00e9s",
    deHeaders: ["\u00c9v\u00e9nement Redout\u00e9", "S\u00e9v\u00e9rit\u00e9", "Impacts"],
    impactsUndefined: "Non d\u00e9finis",
  },
  en: {
    heading: "2. WORKSHOP 1: FRAMING AND CONTEXT",
    introTitle: "FRAMING AND CONTEXT",
    introText:
      "This section presents the context of the analysis, the scope of the study and the initial security baseline.",
    s21: "2.1. STUDY IDENTITY CARD",
    identityHeaders: ["Information", "Detail"],
    rOrganisation: "Organization",
    rPerimetre: "Scope",
    rPeriode: "Period",
    rVersion: "Version",
    rReference: "Reference",
    notSpecifiedF: "Not specified",
    notDefinedM: "Undefined",
    notDefinedF: "Undefined",
    s22: "2.2. MAIN MISSIONS",
    missionNoDesc: "Mission without description",
    noneDocumented: "No specific mission has been documented.",
    s23: "2.3. BUSINESS CONTEXT & STAKES",
    bvCountIntro:
      "{n} business values identified along with their associated stakes.",
    bvHeaders: [
      "Business Value",
      "Description",
      "Criticality",
      "Dreaded Events",
    ],
    unnamed: "Unnamed",
    noneBv: "No business value identified.",
    s24: "2.4. SECURITY BASELINE ASSESSMENT",
    complianceIntro: "Compliance assessed on {n} security framework(s).",
    stdHeaders: ["Framework", "Progress", "Status"],
    unknown: "Unknown",
    inProgress: "In progress",
    noStandards:
      "No specific security framework has been attached so far.",
    deSynthesis: "Summary of Dreaded Events",
    deHeaders: ["Dreaded Event", "Severity", "Impacts"],
    impactsUndefined: "Undefined",
  },
};

const LCH3 = {
  fr: {
    heading: "3. SOURCES DE RISQUE ET OBJECTIFS VIS\u00c9S",
    introTitle: "SOURCES DE RISQUE ET OBJECTIFS VIS\u00c9S",
    introText:
      "Cette section pr\u00e9sente l'analyse des sources de risque identifi\u00e9es lors de l'Atelier 2, ainsi que leur association avec les objectifs vis\u00e9s et les \u00e9v\u00e9nements redout\u00e9s d\u00e9finis lors de l'Atelier 1.",
    s31: "3.1. SOURCES DE RISQUE & OBJECTIFS VIS\u00c9S",
    rsHeaders: [
      "#",
      "Nom",
      "Cat\u00e9gorie",
      "Objectif vis\u00e9",
      "Motivation",
      "Activit\u00e9",
      "Ressources",
      "Statut",
    ],
    selected: "Retenue",
    notSelected: "Non retenue",
    detailsIntro: "D\u00e9tails suppl\u00e9mentaires des sources retenues:",
    unnamedSource: "Source non nomm\u00e9",
    fldDescription: "  Description:",
    fldCategory: "  Cat\u00e9gorie/Type:",
    fldActor: "  Acteur:",
    fldMotivation: "  Motivation:",
    fldCapabilities: "  Capacit\u00e9s:",
    fldImpact: "  Impact potentiel:",
    fldGeo: "  P\u00e9rim\u00e8tre g\u00e9ographique:",
    fldTimeline: "  Aspect temporel:",
    fldJustification: "  Justification du choix:",
    fldRiskLevel: "  Niveau de risque:",
    fldTargets: "  Objectifs cibl\u00e9s:",
    noneSources: "Aucune source de risque identifi\u00e9e.",
    s32: "3.2. SOURCES DE RISQUE PAR CAT\u00c9GORIE",
    noneToCategorize: "Aucune source de risque \u00e0 cat\u00e9goriser.",
    catHeaders: [
      "Cat\u00e9gorie",
      "Nombre de sources",
      "Sources retenues",
      "Principales menaces",
    ],
    s33: "3.3. \u00c9VALUATION DES COUPLES DE SOURCES DE RISQUES ET OBJECTIFS VIS\u00c9S",
    coupleHeaders: [
      "#",
      "Source de risque",
      "Objectif vis\u00e9",
      "\u00c9v\u00e9nement redout\u00e9",
      "Niveau",
      "Statut",
      "Justification",
    ],
    unknownSource: "Source inconnue",
    retainedM: "Retenu",
    notRetainedM: "Non retenu",
    notSpecifiedM: "Non sp\u00e9cifi\u00e9",
    noneCouples: "Aucun couple SR/OV \u00e9valu\u00e9.",
    s34: "3.4. ASSOCIATIONS SOURCES DE RISQUE / \u00c9V\u00c9NEMENTS REDOUT\u00c9S (SR/ER)",
    srerNone: "Aucune association SR/ER d\u00e9finie.",
    srerCountIntro:
      "{n} association(s) entre sources de risque et \u00e9v\u00e9nements redout\u00e9s ont \u00e9t\u00e9 \u00e9tablies.",
    srerHeaders: [
      "Source de Risque",
      "Objectif",
      "\u00c9v\u00e9nement Redout\u00e9",
      "Pilier",
      "Gravit\u00e9",
      "Capacit\u00e9s",
    ],
    unknownF: "Inconnue",
    unknownEvent: "Inconnu",
    impactsIntro: "Impacts associ\u00e9s aux \u00e9v\u00e9nements redout\u00e9s cibl\u00e9s:",
  },
  en: {
    heading: "3. RISK SOURCES AND TARGET OBJECTIVES",
    introTitle: "RISK SOURCES AND TARGET OBJECTIVES",
    introText:
      "This section presents the analysis of risk sources identified during Workshop 2, as well as their association with target objectives and dreaded events defined in Workshop 1.",
    s31: "3.1. RISK SOURCES & TARGET OBJECTIVES",
    rsHeaders: [
      "#",
      "Name",
      "Category",
      "Target Objective",
      "Motivation",
      "Activity",
      "Resources",
      "Status",
    ],
    selected: "Selected",
    notSelected: "Not Selected",
    detailsIntro: "Additional details for selected sources:",
    unnamedSource: "Unnamed source",
    fldDescription: "  Description:",
    fldCategory: "  Category/Type:",
    fldActor: "  Actor:",
    fldMotivation: "  Motivation:",
    fldCapabilities: "  Capabilities:",
    fldImpact: "  Potential impact:",
    fldGeo: "  Geographic scope:",
    fldTimeline: "  Timeline:",
    fldJustification: "  Selection rationale:",
    fldRiskLevel: "  Risk level:",
    fldTargets: "  Targeted objectives:",
    noneSources: "No risk sources identified.",
    s32: "3.2. RISK SOURCES BY CATEGORY",
    noneToCategorize: "No risk source to categorize.",
    catHeaders: [
      "Category",
      "Number of sources",
      "Selected sources",
      "Main threats",
    ],
    s33: "3.3. RISK SOURCE / TARGET OBJECTIVE PAIRS EVALUATION",
    coupleHeaders: [
      "#",
      "Risk Source",
      "Target Objective",
      "Feared Event",
      "Level",
      "Status",
      "Justification",
    ],
    unknownSource: "Unknown source",
    retainedM: "Selected",
    notRetainedM: "Not retained",
    notSpecifiedM: "Not specified",
    noneCouples: "No RS/TO pairs evaluated.",
    s34: "3.4. RISK SOURCE / DREADED EVENT ASSOCIATIONS (RS/DE)",
    srerNone: "No RS/DE association defined.",
    srerCountIntro:
      "{n} association(s) between risk sources and dreaded events have been established.",
    srerHeaders: [
      "Risk Source",
      "Objective",
      "Dreaded Event",
      "Pillar",
      "Gravity",
      "Capabilities",
    ],
    unknownF: "Unknown",
    unknownEvent: "Unknown",
    impactsIntro: "Impacts associated with targeted dreaded events:",
  },
};

const LCATS = {
  fr: {
    etatique: "\u00c9tatique",
    terroriste: "Terroriste",
    activiste: "Activiste / Id\u00e9ologique",
    criminel: "Crime Organis\u00e9",
    concurrent: "Concurrent",
    pathologique: "Pathologique / Vengeur",
    amateur: "Amateur / Opportuniste",
    interne: "Interne (Employ\u00e9)",
    "non-categorise": "Non cat\u00e9goris\u00e9",
    state: "\u00c9tatique",
    terrorist: "Terroriste",
    hacktivist: "Activiste / Hacktiviste",
    criminal: "Crime Organis\u00e9",
    competitor: "Concurrent",
    insider: "Interne",
    opportunist: "Opportuniste",
  },
  en: {
    etatique: "State",
    terroriste: "Terrorist",
    activiste: "Activist / Ideological",
    criminel: "Organized Crime",
    concurrent: "Competitor",
    pathologique: "Pathological / Vindictive",
    amateur: "Amateur / Opportunist",
    interne: "Internal (Employee)",
    "non-categorise": "Uncategorized",
    state: "State",
    terrorist: "Terrorist",
    hacktivist: "Activist / Hacktivist",
    criminal: "Organized Crime",
    competitor: "Competitor",
    insider: "Insider",
    opportunist: "Opportunist",
  },
};

const LCH4 = {
  fr: {
    heading: "4. SC\u00c9NARIOS STRAT\u00c9GIQUES",
    introTitle: "SC\u00c9NARIOS STRAT\u00c9GIQUES",
    introText:
      "Cette section d\u00e9taille les sc\u00e9narios strat\u00e9giques d\u00e9velopp\u00e9s lors de l'Atelier 3, combinant les parties prenantes critiques identifi\u00e9es avec les \u00e9v\u00e9nements redout\u00e9s et \u00e9valuant leur gravit\u00e9 respective.",
    s41: "4.1. \u00c9LABORATION DE LA CARTOGRAPHIE DE LA MENACE NUM\u00c9RIQUE ET S\u00c9LECTION DES PARTIES PRENANTES CRITIQUES",
    s411: "4.1.1. DESCRIPTION DES PARTIES PRENANTES",
    descHeaders: ["#", "Nom", "Cat\u00e9gorie", "Type/Rang", "Description"],
    noneDescribed: "Aucune partie prenante d\u00e9crite.",
    s412: "4.1.2. \u00c9VALUATION DES PARTIES PRENANTES",
    evalHeaders: [
      "Nom",
      "Menace",
      "D\u00e9pendance",
      "P\u00e9n\u00e9tration",
      "Maturit\u00e9 Cyber",
      "Confiance",
      "Retenue",
    ],
    oui: "Oui ",
    non: "Non",
    noneEval: "Aucune \u00e9valuation disponible.",
    s413:
      "4.1.3. REPR\u00c9SENTATION DE LA CARTOGRAPHIE DE LA MENACE NUM\u00c9RIQUE INITIALE DE L'\u00c9COSYST\u00c8ME",
  },
  en: {
    heading: "4. STRATEGIC SCENARIOS",
    introTitle: "STRATEGIC SCENARIOS",
    introText:
      "This section details the strategic scenarios developed during Workshop 3, combining identified critical stakeholders with dreaded events and evaluating their gravity.",
    s41:
      "4.1. DEVELOPMENT OF THE DIGITAL THREAT MAPPING AND SELECTION OF CRITICAL STAKEHOLDERS",
    s411: "4.1.1. STAKEHOLDERS DESCRIPTION",
    descHeaders: ["#", "Name", "Category", "Type/Rank", "Description"],
    noneDescribed: "No stakeholders described.",
    s412: "4.1.2. STAKEHOLDERS EVALUATION",
    evalHeaders: [
      "Name",
      "Threat",
      "Dependency",
      "Penetration",
      "Cyber Maturity",
      "Trust",
      "Retained",
    ],
    oui: "Yes ",
    non: "No",
    noneEval: "No evaluation available.",
    s413:
      "4.1.3. REPRESENTATION OF THE INITIAL DIGITAL THREAT MAPPING OF THE ECOSYSTEM",
  },
};

const LCARTO = {
  fr: {
    threatDistTitle: "R\u00c9PARTITION DES NIVEAUX DE MENACE",
    chartTotal: "Total: {n} parties prenantes analys\u00e9es",
    lblCritical: "Critique ({n})",
    lblHigh: "\u00c9lev\u00e9 ({n})",
    lblMedium: "Mod\u00e9r\u00e9 ({n})",
    lblLow: "Faible ({n})",
    lblUndefined: "Non \u00e9valu\u00e9 ({n})",
    chartError:
      "Erreur lors de la g\u00e9n\u00e9ration du graphique. Voici les donn\u00e9es brutes:",
    noChartData:
      "Aucune donn\u00e9e de menace disponible pour la visualisation.",
    noStakeholdersCarto:
      "Aucune partie prenante n'a \u00e9t\u00e9 identifi\u00e9e dans l'\u00e9cosyst\u00e8me. Cette situation n\u00e9cessite une analyse approfondie pour identifier les acteurs critiques de l'environnement num\u00e9rique.",
    recoList: "Recommandations:",
    recoItem1:
      "\u2022 Proc\u00e9der \u00e0 une identification exhaustive des parties prenantes",
    recoItem2: "\u2022 Analyser les flux de donn\u00e9es et les interconnexions",
    recoItem3:
      "\u2022 \u00c9valuer les d\u00e9pendances technologiques et organisationnelles",
    cartoIntro:
      "La cartographie de l'\u00e9cosyst\u00e8me r\u00e9v\u00e8le {n} parties prenantes identifi\u00e9es, dont {r} ont \u00e9t\u00e9 retenues pour l'analyse approfondie.",
    criticalIdentified: "PARTIES PRENANTES CRITIQUES IDENTIFI\u00c9ES",
    noneCritical: "Aucune partie prenante identifi\u00e9e comme critique.",
    unnamedStakeholder: "Partie prenante sans nom",
    statusPrefix: "   Statut: ",
    justificationPrefix: "Justification : ",
    noJustification: "Aucune justification fournie",
    statusRetained: "Retenue pour analyse",
    statusNotRetained: "Non retenue",
    exposureTitle: "SYNTH\u00c8SE DE L'EXPOSITION AUX MENACES",
    noneRetainedMsg:
      "Aucune partie prenante retenue pour l'analyse. Il est recommand\u00e9 de revoir les crit\u00e8res de s\u00e9lection.",
    exposureLevel: "Niveau d'exposition global:",
    exposureDetail:
      "{n} parties prenantes retenues, dont {h} pr\u00e9sentent un niveau de menace \u00e9lev\u00e9 ou critique ({p}%).",
    lvlLow: "FAIBLE",
    lvlModerate: "MOD\u00c9R\u00c9",
    lvlHigh: "\u00c9LEV\u00c9",
    lvlCritical: "CRITIQUE",
    recoStrategicTitle: "RECOMMANDATIONS STRAT\u00c9GIQUES",
    recoHigh1:
      "\u2022 Mise en place imm\u00e9diate de mesures de protection renforc\u00e9es",
    recoHigh2:
      "\u2022 Surveillance continue des parties prenantes \u00e0 haut risque",
    recoHigh3: "\u2022 Plan de continuit\u00e9 d'activit\u00e9 prioritaire",
    recoMid1: "\u2022 Renforcement des contr\u00f4les de s\u00e9curit\u00e9",
    recoMid2: "\u2022 \u00c9valuation r\u00e9guli\u00e8re des niveaux de menace",
    recoMid3: "\u2022 Formation et sensibilisation cibl\u00e9es",
    recoLow1: "\u2022 Surveillance p\u00e9riodique des \u00e9volutions",
    recoLow2: "\u2022 Maintien des mesures de s\u00e9curit\u00e9 existantes",
    recoDef1: "\u2022 Maintien de la vigilance",
    recoDef2: "\u2022 R\u00e9vision p\u00e9riodique de l'\u00e9valuation",
    recoAlways1:
      "\u2022 Mise en place d'un processus de r\u00e9\u00e9valuation p\u00e9riodique",
    recoAlways2:
      "\u2022 Surveillance continue des \u00e9volutions de l'\u00e9cosyst\u00e8me",
  },
  en: {
    threatDistTitle: "DISTRIBUTION OF THREAT LEVELS",
    chartTotal: "Total: {n} stakeholders analyzed",
    lblCritical: "Critical ({n})",
    lblHigh: "High ({n})",
    lblMedium: "Moderate ({n})",
    lblLow: "Low ({n})",
    lblUndefined: "Not evaluated ({n})",
    chartError: "Error generating the chart. Here is the raw data:",
    noChartData: "No threat data available for visualization.",
    noStakeholdersCarto:
      "No stakeholders have been identified in the ecosystem. This situation requires in-depth analysis to identify the critical actors of the digital environment.",
    recoList: "Recommendations:",
    recoItem1:
      "\u2022 Carry out an exhaustive identification of stakeholders",
    recoItem2: "\u2022 Analyze data flows and interconnections",
    recoItem3:
      "\u2022 Assess technological and organizational dependencies",
    cartoIntro:
      "The ecosystem mapping reveals {n} identified stakeholders, of which {r} were retained for in-depth analysis.",
    criticalIdentified: "CRITICAL STAKEHOLDERS IDENTIFIED",
    noneCritical: "No stakeholder identified as critical.",
    unnamedStakeholder: "Unnamed stakeholder",
    statusPrefix: "   Status: ",
    justificationPrefix: "Justification: ",
    noJustification: "No justification provided",
    statusRetained: "Retained for analysis",
    statusNotRetained: "Not retained",
    exposureTitle: "THREAT EXPOSURE SUMMARY",
    noneRetainedMsg:
      "No stakeholder retained for analysis. It is recommended to review the selection criteria.",
    exposureLevel: "Overall exposure level:",
    exposureDetail:
      "{n} stakeholders retained, of which {h} present a high or critical threat level ({p}%).",
    lvlLow: "LOW",
    lvlModerate: "MODERATE",
    lvlHigh: "HIGH",
    lvlCritical: "CRITICAL",
    recoStrategicTitle: "STRATEGIC RECOMMENDATIONS",
    recoHigh1:
      "\u2022 Immediate implementation of reinforced protection measures",
    recoHigh2:
      "\u2022 Continuous monitoring of high-risk stakeholders",
    recoHigh3: "\u2022 Priority business continuity plan",
    recoMid1: "\u2022 Strengthening of security controls",
    recoMid2: "\u2022 Regular assessment of threat levels",
    recoMid3: "\u2022 Targeted training and awareness",
    recoLow1: "\u2022 Periodic monitoring of changes",
    recoLow2: "\u2022 Maintaining existing security measures",
    recoDef1: "\u2022 Maintaining vigilance",
    recoDef2: "\u2022 Periodic review of the assessment",
    recoAlways1:
      "\u2022 Implementation of a periodic re-evaluation process",
    recoAlways2:
      "\u2022 Continuous monitoring of ecosystem changes",
  },
};

const LS42P = {
  fr: {
    s42Title: "4.2. SC\u00c9NARIOS STRAT\u00c9GIQUES ET MESURES DE S\u00c9CURIT\u00c9",
    s42Empty: "Aucun sc\u00e9nario strat\u00e9gique identifi\u00e9.",
    s42CountIntro:
      "{n} sc\u00e9narios strat\u00e9giques avec leurs chemins d'attaque et mesures associ\u00e9es.",
    s42Headers: [
      "#",
      "Sc\u00e9nario Strat\u00e9gique",
      "Source de Risque",
      "Objectif Vis\u00e9",
      "\u00c9v\u00e9nement Redout\u00e9",
      "CA",
      "Mesure",
      "Cat\u00e9gorie",
      "Priorit\u00e9",
      "Eff.",
      "Statut",
    ],
    untitled: "Sans titre",
    noMeasure: "Aucune mesure d\u00e9finie",
    progIntro:
      "Suivi de la progression du traitement des \u00e9v\u00e9nements redout\u00e9s \u00e0 travers les ateliers.",
    progHeaders: [
      "\u00c9v\u00e9nement Redout\u00e9",
      "Gravit\u00e9",
      "Mesures A1",
      "Mesures A3",
      "Progression",
      "Statut",
    ],
    undefinedEvent: "Non d\u00e9fini",
    enriched: "Enrichi",
    partial: "Partiel",
    notTreated: "Non trait\u00e9",
    synthesis:
      "Synth\u00e8se: {e}/{t} \u00e9v\u00e9nements enrichis (A1+A3), {p} partiellement trait\u00e9s, {n} non trait\u00e9s. Taux d'enrichissement: {rate}%.",
    heading43: "4.3. SUIVI DE PROGRESSION DES \u00c9V\u00c9NEMENTS REDOUT\u00c9S",
  },
  en: {
    s42Title: "4.2. STRATEGIC SCENARIOS AND SECURITY MEASURES",
    s42Empty: "No strategic scenario identified.",
    s42CountIntro:
      "{n} strategic scenarios with their associated attack paths and measures.",
    s42Headers: [
      "#",
      "Strategic Scenario",
      "Risk Source",
      "Target Objective",
      "Dreaded Event",
      "AP",
      "Measure",
      "Category",
      "Priority",
      "Eff.",
      "Status",
    ],
    untitled: "Untitled",
    noMeasure: "No measure defined",
    progIntro:
      "Tracking the progression of dreaded event treatment across workshops.",
    progHeaders: [
      "Dreaded Event",
      "Gravity",
      "A1 Measures",
      "A3 Measures",
      "Progression",
      "Status",
    ],
    undefinedEvent: "Undefined",
    enriched: "Enriched",
    partial: "Partial",
    notTreated: "Untreated",
    synthesis:
      "Summary: {e}/{t} events enriched (A1+A3), {p} partially treated, {n} untreated. Enrichment rate: {rate}%.",
    heading43: "4.3. DREADED EVENTS PROGRESSION TRACKING",
  },
};

const LCH5 = {
  fr: {
    ch5: {
      heading: "5. SC\u00c9NARIOS OP\u00c9RATIONNELS",
      introTitle: "SC\u00c9NARIOS OP\u00c9RATIONNELS",
      introText:
        "Cette section pr\u00e9sente les sc\u00e9narios op\u00e9rationnels d\u00e9taill\u00e9s issus de l'Atelier 4, avec les modes op\u00e9ratoires techniques sp\u00e9cifiques et l'\u00e9valuation de leur vraisemblance d'occurrence.",
      s51: "5.1. \u00c9LABORATION DES SC\u00c9NARIOS OP\u00c9RATIONNELS",
      osHeaders: [
        "#",
        "Nom du Sc\u00e9nario",
        "Chemin d'Attaque",
        "S\u00e9v\u00e9rit\u00e9",
        "Vraisemblance",
        "D\u00e9tection",
        "Comp\u00e9tences",
        "Dur\u00e9e",
        "\u00c9tapes",
      ],
      toAssess: "\u00c0 \u00e9valuer",
      toDefineSkills: "\u00c0 d\u00e9finir",
      toEstimate: "\u00c0 estimer",
      noneElab: "Aucun sc\u00e9nario op\u00e9rationnel \u00e9labor\u00e9.",
      s511:
        "5.1.1. MOD\u00c9LISATION ET \u00c9VALUATION DES MODES OP\u00c9RATOIRES DES CHEMINS D'ATTAQUE",
      modeOpPrefix: "Mode Op\u00e9ratoire",
      actionsIntro:
        "Ce mode op\u00e9ratoire comprend {n} action(s) \u00e9l\u00e9mentaire(s) r\u00e9parties sur les phases d'attaque suivantes:",
      actionHeaders: [
        "#",
        "Technique",
        "Description",
        "Prob. Succ\u00e8s",
        "Diff. Technique",
      ],
      noDescription: "Description non fournie",
      othersLabel: "\u25b8 Autres actions",
      otherHeaders: ["#", "Phase", "Technique", "Description", "PS", "DT"],
      stepsHeaders: ["Phase", "Technique", "Description", "Outils"],
      toComplete: "\u00c0 compl\u00e9ter",
      noneElementary:
        "Actions \u00e9l\u00e9mentaires \u00e0 d\u00e9finir pour ce mode op\u00e9ratoire.",
      noneModes: "Aucun mode op\u00e9ratoire mod\u00e9lis\u00e9.",
      s52: "5.2. CHEMINS D'ATTAQUE IDENTIFI\u00c9S",
      apCount:
        "{n} chemin(s) d'attaque ont \u00e9t\u00e9 identifi\u00e9s et analys\u00e9s.",
      apHeaders: [
        "R\u00e9f.",
        "Nom",
        "Source de Risque",
        "Objectif Vis\u00e9",
        "Parties Prenantes",
        "Gravit\u00e9",
      ],
      apPrefix: "CA-",
    },
    phases: {
      CONNAITRE: "Conna\u00eetre (Reconnaissance)",
      RENTRER: "Rentrer (Intrusion)",
      TROUVER: "Trouver (Exploration)",
      EXPLOITER: "Exploiter (Action sur objectif)",
    },
  },
  en: {
    ch5: {
      heading: "5. OPERATIONAL SCENARIOS",
      introTitle: "OPERATIONAL SCENARIOS",
      introText:
        "This section presents detailed operational scenarios from Workshop 4, with specific technical operating modes and the assessment of their likelihood of occurrence.",
      s51: "5.1. DEVELOPMENT OF OPERATIONAL SCENARIOS",
      osHeaders: [
        "#",
        "Scenario Name",
        "Attack Path",
        "Severity",
        "Likelihood",
        "Detection",
        "Skills",
        "Duration",
        "Steps",
      ],
      toAssess: "To assess",
      toDefineSkills: "To define",
      toEstimate: "To estimate",
      noneElab: "No operational scenario developed.",
      s511:
        "5.1.1. MODELING AND EVALUATION OF ATTACK PATH OPERATING MODES",
      modeOpPrefix: "Operating Mode",
      actionsIntro:
        "This operating mode includes {n} elementary action(s) spread across the following attack phases:",
      actionHeaders: [
        "#",
        "Technique",
        "Description",
        "Success Prob.",
        "Technical Diff.",
      ],
      noDescription: "Description not provided",
      othersLabel: "\u25b8 Other actions",
      otherHeaders: ["#", "Phase", "Technique", "Description", "SP", "TD"],
      stepsHeaders: ["Phase", "Technique", "Description", "Tools"],
      toComplete: "To complete",
      noneElementary:
        "Elementary actions to be defined for this operating mode.",
      noneModes: "No operating mode modeled.",
      s52: "5.2. IDENTIFIED ATTACK PATHS",
      apCount:
        "{n} attack path(s) have been identified and analyzed.",
      apHeaders: [
        "Ref.",
        "Name",
        "Risk Source",
        "Target Objective",
        "Stakeholders",
        "Gravity",
      ],
      apPrefix: "AP-",
    },
    phases: {
      CONNAITRE: "Know (Reconnaissance)",
      RENTRER: "Enter (Intrusion)",
      TROUVER: "Find (Exploration)",
      EXPLOITER: "Exploit (Action on objective)",
    },
  },
};

const LCH6 = {
  fr: {
    ch6: {
      heading: "6. TRAITEMENT DU RISQUE",
      introTitle: "TRAITEMENT DU RISQUE",
      introText:
        "Cette section d\u00e9crit les strat\u00e9gies de traitement des risques \u00e9tablies lors de l'Atelier 5, incluant les mesures de s\u00e9curit\u00e9 planifi\u00e9es, leurs priorit\u00e9s d'impl\u00e9mentation et l'\u00e9valuation des risques r\u00e9siduels apr\u00e8s traitement.",
      s61: "6.1. STRAT\u00c9GIE ET APP\u00c9TENCE AU RISQUE ET OBJECTIFS DE S\u00c9CURIT\u00c9",
      balancedStrategy:
        "L'approche adopt\u00e9e privil\u00e9gie une strat\u00e9gie \u00e9quilibr\u00e9e entre r\u00e9duction des risques critiques et acceptation des risques r\u00e9siduels ma\u00eetris\u00e9s.",
      secObjTitle: "OBJECTIFS DE S\u00c9CURIT\u00c9 PRIORITAIRES:",
      secObj1: "- Protection des actifs critiques et donn\u00e9es sensibles",
      secObj2: "- Continuit\u00e9 des services essentiels",
      secObj3: "- Conformit\u00e9 r\u00e9glementaire et sectorielle",
      secObj4: "- R\u00e9duction des impacts sur la r\u00e9putation",
      thresholdsTitle: "SEUILS D'ACCEPTATION:",
      thr1: "- Risques r\u00e9siduels \u2264 niveau 8/20 accept\u00e9s",
      thr2: "- Pas d'acceptation pour risques > niveau 12/20",
      thr3: "- R\u00e9vision annuelle des seuils d'app\u00e9tence",
      thr4: "- Validation par le comit\u00e9 de direction",
      s62: "6.2. SYNTH\u00c8SE DES SC\u00c9NARIOS DE RISQUES",
      synHeaders: [
        "#",
        "Sc\u00e9nario",
        "Gravit\u00e9 Initiale",
        "Vraisemblance",
        "Risque Initial",
        "Strat\u00e9gie",
        "R\u00e9duction (%)",
      ],
      toDefine: "\u00c0 d\u00e9finir",
      noneSynth: "Aucun sc\u00e9nario \u00e0 synth\u00e9tiser.",
      s63:
        "6.3. IDENTIFICATION DES MESURES DE S\u00c9CURIT\u00c9 DE TRAITEMENT DU RISQUE \u2013 PLAN D'AM\u00c9LIORATION CONTINUE DE LA S\u00c9CURIT\u00c9 (PACS)",
      s631: "6.3.1. COUVERTURE DES RISQUES PAR LES MESURES",
      covHeaders: ["Aspect", "Valeur", "Unit\u00e9"],
      covScenarios: "Sc\u00e9narios analys\u00e9s",
      covMeasures: "Mesures identifi\u00e9es",
      covMeasuresPerScenario: "Mesures par sc\u00e9nario",
      covMeasureTypes: "Types de mesures",
      covAvgEffectiveness: "Efficacit\u00e9 moyenne",
      uTotal: "total",
      uAverage: "moyenne",
      uCategories: "cat\u00e9gories",
      s632: "6.3.2. SUIVI DE LA MISE EN ŒUVRE DES MESURES DE S\u00c9CURIT\u00c9",
      implStatusLabel: "\u00c9tat d'impl\u00e9mentation:",
      statusHeaders: [
        "Type de mesure",
        "Impl\u00e9ment\u00e9es",
        "En cours",
        "Planifi\u00e9es",
        "Total",
      ],
      rowCustom: "Mesures personnalis\u00e9es",
      rowEcosystem: "Mesures \u00e9cosyst\u00e8me",
      rowDreaded: "Mesures \u00e9v\u00e9nements redout\u00e9s",
      s633: "6.3.3. TABLEAU DES RISQUES R\u00c9SIDUELS",
      resHeaders: [
        "Sc\u00e9nario",
        "Risque Initial",
        "Risque R\u00e9siduel",
        "R\u00e9duction (%)",
        "Statut d'acceptation",
        "Justification",
      ],
      accepted: "Accept\u00e9",
      toTreat: "\u00c0 traiter",
      toCompleteShort: "\u00c0 compl\u00e9ter",
      noneResidual: "Aucun risque r\u00e9siduel \u00e9valu\u00e9.",
    },
    impl: {
      implemented: "Mesures impl\u00e9ment\u00e9es: {n}",
      inProgress: "En cours d'impl\u00e9mentation: {n}",
      planned: "Mesures planifi\u00e9es: {n}",
      notPlanned: "Non planifi\u00e9es: {n}",
    },
  },
  en: {
    ch6: {
      heading: "6. RISK TREATMENT",
      introTitle: "RISK TREATMENT",
      introText:
        "This section describes the risk treatment strategies established during Workshop 5, including planned security measures, implementation priorities, and residual risk assessment after treatment.",
      s61: "6.1. RISK STRATEGY AND APPETITE AND SECURITY OBJECTIVES",
      balancedStrategy:
        "The adopted approach favors a balanced strategy between reducing critical risks and accepting controlled residual risks.",
      secObjTitle: "PRIORITY SECURITY OBJECTIVES:",
      secObj1: "- Protection of critical assets and sensitive data",
      secObj2: "- Continuity of essential services",
      secObj3: "- Regulatory and sectoral compliance",
      secObj4: "- Reduction of reputation impacts",
      thresholdsTitle: "ACCEPTANCE THRESHOLDS:",
      thr1: "- Residual risks \u2264 level 8/20 accepted",
      thr2: "- No acceptance for risks > level 12/20",
      thr3: "- Annual review of appetite thresholds",
      thr4: "- Validation by the executive committee",
      s62: "6.2. RISK SCENARIOS SYNTHESIS",
      synHeaders: [
        "#",
        "Scenario",
        "Initial Gravity",
        "Likelihood",
        "Initial Risk",
        "Strategy",
        "Reduction (%)",
      ],
      toDefine: "To define",
      noneSynth: "No scenario to synthesize.",
      s63:
        "6.3. IDENTIFICATION OF RISK TREATMENT SECURITY MEASURES \u2013 CONTINUOUS SECURITY IMPROVEMENT PLAN (CSIP)",
      s631: "6.3.1. RISK COVERAGE BY MEASURES",
      covHeaders: ["Aspect", "Value", "Unit"],
      covScenarios: "Analyzed scenarios",
      covMeasures: "Identified measures",
      covMeasuresPerScenario: "Measures per scenario",
      covMeasureTypes: "Measure types",
      covAvgEffectiveness: "Average effectiveness",
      uTotal: "total",
      uAverage: "average",
      uCategories: "categories",
      s632: "6.3.2. IMPLEMENTATION MONITORING OF SECURITY MEASURES",
      implStatusLabel: "Implementation status:",
      statusHeaders: [
        "Measure type",
        "Implemented",
        "In progress",
        "Planned",
        "Total",
      ],
      rowCustom: "Custom measures",
      rowEcosystem: "Ecosystem measures",
      rowDreaded: "Dreaded event measures",
      s633: "6.3.3. RESIDUAL RISK TABLE",
      resHeaders: [
        "Scenario",
        "Initial Risk",
        "Residual Risk",
        "Reduction (%)",
        "Acceptance Status",
        "Justification",
      ],
      accepted: "Accepted",
      toTreat: "To treat",
      toCompleteShort: "To complete",
      noneResidual: "No residual risk evaluated.",
    },
    impl: {
      implemented: "Implemented measures: {n}",
      inProgress: "In progress: {n}",
      planned: "Planned measures: {n}",
      notPlanned: "Not planned: {n}",
    },
  },
};

const LAPP1 = {
  fr: {
    annexes: "Annexes",
    a1Title: "APPENDICE I : \u00c9CHELLES DES BESOINS DE S\u00c9CURIT\u00c9",
    scalesHeaders: [
      "Pilier de s\u00e9curit\u00e9",
      "Niveau 1",
      "Niveau 2",
      "Niveau 3",
      "Niveau 4",
    ],
    scaleConf: [
      "CONFIDENTIALIT\u00c9", "Public", "Interne restreint",
      "Confidentiel", "Strictement confidentiel",
    ],
    scaleInt: [
      "INT\u00c9GRIT\u00c9", "Non critique", "Peu critique",
      "Critique", "Tr\u00e8s critique",
    ],
    scaleDis: [
      "DISPONIBILIT\u00c9", "Peu importante", "Importante",
      "Tr\u00e8s importante", "Critique",
    ],
    a2Title: "APPENDICE II : \u00c9CHELLE DE GRAVIT\u00c9",
    sevIntro:
      "Cette \u00e9chelle d\u00e9finit les niveaux d'impact des \u00e9v\u00e9nements redout\u00e9s:",
    sevHeaders: ["Niveau", "Intitul\u00e9", "Description", "Impact typique"],
    sev1: ["1", "N\u00c9GLIGEABLE", "Impact minimal sur les missions et op\u00e9rations", "Reprise en quelques heures"],
    sev2: ["2", "LIMIT\u00c9E", "Impact localis\u00e9 et temporaire sur une partie des op\u00e9rations", "Reprise en quelques jours"],
    sev3: ["3", "IMPORTANTE", "Impact significatif sur les missions principales", "Reprise en quelques semaines"],
    sev4: ["4", "CRITIQUE", "Impact majeur sur les missions essentielles", "Reprise en quelques mois"],
    sev5: ["5", "CATASTROPHIQUE", "Impact destructeur sur l'organisation", "Risque de dissolution"],
  },
  en: {
    annexes: "Appendices",
    a1Title: "APPENDIX I: SECURITY NEEDS SCALES",
    scalesHeaders: [
      "Security Pillar",
      "Level 1",
      "Level 2",
      "Level 3",
      "Level 4",
    ],
    scaleConf: [
      "CONFIDENTIALITY", "Public", "Internal Restricted",
      "Confidential", "Strictly Confidential",
    ],
    scaleInt: [
      "INTEGRITY", "Not Critical", "Low Criticality",
      "Critical", "Very Critical",
    ],
    scaleDis: [
      "AVAILABILITY", "Not Important", "Important",
      "Very Important", "Critical",
    ],
    a2Title: "APPENDIX II: SEVERITY SCALE",
    sevIntro:
      "This scale defines the impact levels of dreaded events:",
    sevHeaders: ["Level", "Title", "Description", "Typical Impact"],
    sev1: ["1", "NEGLIGIBLE", "Minimal impact on mission and operations", "Recovery in hours"],
    sev2: ["2", "LIMITED", "Localized temporary impact on part of operations", "Recovery in days"],
    sev3: ["3", "IMPORTANT", "Significant impact on main missions", "Recovery in weeks"],
    sev4: ["4", "CRITICAL", "Major impact on essential missions", "Recovery in months"],
    sev5: ["5", "CATASTROPHIC", "Destructive impact on organization", "Risk of dissolution"],
  },
};

const LAPP2 = {
  fr: {
    a3Title:
      "APPENDICE III : CAT\u00c9GORIES DE SOURCES DE RISQUE (SR) ET D'OBJECTIFS VIS\u00c9S (OV)",
    srcSub: "SOURCES DE RISQUE (SR)",
    srcHeaders: ["#", "Cat\u00e9gorie", "Description", "Motivation principale"],
    src1: ["1", "\u00c9TATIQUE", "Acteurs \u00e9tatiques (gouvernements, services secrets)", "Souverainet\u00e9, strat\u00e9gie internationale"],
    src2: ["2", "CRIMINEL", "Crime organis\u00e9 (mafias, r\u00e9seaux criminels)", "Profit financier"],
    src3: ["3", "TERRORISTE", "Groupes terroristes id\u00e9ologiques", "Impact psychologique, id\u00e9ologie"],
    src4: ["4", "ACTIVISTE", "Activistes id\u00e9ologiques ou politiques", "Changement de comportement"],
    src5: ["5", "OFFICINE", "Officines sp\u00e9cialis\u00e9es (hackers professionnels)", "Prestation de services payants"],
    src6: ["6", "AMATEUR", "Individus isol\u00e9s (script kiddies, opportunistes)", "Curiosit\u00e9, d\u00e9monstration"],
    objSub: "OBJECTIFS VIS\u00c9S (OV)",
    objHeaders: ["#", "Objectif vis\u00e9", "Description", "Impact recherch\u00e9"],
    obj1: ["1", "ESPIONNAGE", "Vol d'informations confidentielles", "Renseignement strat\u00e9gique"],
    obj2: ["2", "SABOTAGE", "Destruction ou perturbation des syst\u00e8mes", "Neutralisation op\u00e9rationnelle"],
    obj3: ["3", "EXTORSION", "Ran\u00e7on ou chantage par la menace", "Gain financier sous pression"],
    obj4: ["4", "DISRUPTION", "Perturbation des op\u00e9rations courantes", "Incapacit\u00e9 op\u00e9rationnelle temporaire"],
    a4Title: "APPENDICE IV : \u00c9CHELLE D'\u00c9VALUATION DES COUPLES SR/OV",
    srovIntro:
      "Cette \u00e9chelle d\u00e9finit les niveaux d'\u00e9valuation pour les couples Source de Risque / Objectifs Vis\u00e9s:",
    srovHeaders: [
      "Crit\u00e8re", "Niveau 1", "Niveau 2", "Niveau 3", "Niveau 4", "Description",
    ],
    srovRes: [
      "RESSOURCES", "Faibles (1)", "Moyennes (2)", "Importantes (3)",
      "Tr\u00e8s importantes (4)", "Capacit\u00e9s techniques et financi\u00e8res des acteurs",
    ],
    srovAct: [
      "ACTIVIT\u00c9", "Faible (1)", "Moyenne (2)", "\u00c9lev\u00e9e (3)",
      "Tr\u00e8s \u00e9lev\u00e9e (4)", "Intensit\u00e9 et fr\u00e9quence des actions malveillantes",
    ],
    srovMot: [
      "MOTIVATION", "Faible (1)", "Moyenne (2)", "\u00c9lev\u00e9e (3)",
      "Tr\u00e8s \u00e9lev\u00e9e (4)", "Int\u00e9r\u00eat et d\u00e9termination des acteurs",
    ],
    srovGlob: [
      "NIVEAU GLOBAL", "1", "2", "3", "4",
      "\u00c9valuation synth\u00e9tique automatique calcul\u00e9e",
    ],
  },
  en: {
    a3Title:
      "APPENDIX III: RISK SOURCE (RS) AND TARGET OBJECTIVE (TO) CATEGORIES",
    srcSub: "RISK SOURCES (RS)",
    srcHeaders: ["#", "Category", "Description", "Main Motivation"],
    src1: ["1", "STATE", "State actors (governments, intelligence services)", "Sovereignty, international strategy"],
    src2: ["2", "CRIMINAL", "Organized crime (mafias, criminal networks)", "Financial profit"],
    src3: ["3", "TERRORIST", "Ideological terrorist groups", "Psychological impact, ideology"],
    src4: ["4", "ACTIVIST", "Ideological or political activists", "Behavior change"],
    src5: ["5", "MERCENARY", "Specialized groups (professional hackers)", "Paid services"],
    src6: ["6", "AMATEUR", "Isolated individuals (script kiddies, opportunists)", "Curiosity, demonstration"],
    objSub: "TARGET OBJECTIVES (TO)",
    objHeaders: ["#", "Target Objective", "Description", "Desired Impact"],
    obj1: ["1", "ESPIONAGE", "Theft of confidential information", "Strategic intelligence"],
    obj2: ["2", "SABOTAGE", "Destruction or disruption of systems", "Operational neutralization"],
    obj3: ["3", "EXTORTION", "Ransom or blackmail through threats", "Financial gain under pressure"],
    obj4: ["4", "DISRUPTION", "Disruption of routine operations", "Temporary operational incapacity"],
    a4Title: "APPENDIX IV: RS/TO PAIRS EVALUATION SCALE",
    srovIntro:
      "This scale defines the evaluation levels for Risk Source / Target Objective pairs:",
    srovHeaders: [
      "Criterion", "Level 1", "Level 2", "Level 3", "Level 4", "Description",
    ],
    srovRes: [
      "RESOURCES", "Low (1)", "Medium (2)", "High (3)",
      "Very High (4)", "Technical and financial capabilities of actors",
    ],
    srovAct: [
      "ACTIVITY", "Low (1)", "Medium (2)", "High (3)",
      "Very High (4)", "Intensity and frequency of malicious actions",
    ],
    srovMot: [
      "MOTIVATION", "Low (1)", "Medium (2)", "High (3)",
      "Very High (4)", "Interest and determination of actors",
    ],
    srovGlob: [
      "GLOBAL LEVEL", "1", "2", "3", "4",
      "Automatically calculated synthetic evaluation",
    ],
  },
};

const LAPP3 = {
  fr: {
    a5Title: "APPENDICE V : \u00c9CHELLE D'\u00c9VALUATION DES PARTIES PRENANTES",
    stkIntro:
      "Cette \u00e9chelle d\u00e9finit les crit\u00e8res d'\u00e9valuation des parties prenantes de l'\u00e9cosyst\u00e8me:",
    stkThreat: [
      "NIVEAU DE MENACE", "Faible (1)", "Mod\u00e9r\u00e9 (2)", "\u00c9lev\u00e9 (3)",
      "Critique (4)", "Potentiel d'attaque et impact sur l'organisation",
    ],
    stkDep: [
      "D\u00c9PENDANCE", "Faible (1)", "Moyenne (2)", "\u00c9lev\u00e9e (3)",
      "Tr\u00e8s \u00e9lev\u00e9e (4)", "Niveau de d\u00e9pendance technique/commerciale",
    ],
    stkPen: [
      "P\u00c9N\u00c9TRATION", "Faible (1)", "Moyenne (2)", "\u00c9lev\u00e9e (3)",
      "Tr\u00e8s \u00e9lev\u00e9e (4)", "Capacit\u00e9 d'acc\u00e8s aux syst\u00e8mes/applications",
    ],
    stkMat: [
      "MATURIT\u00c9 CYBER", "Faible (1)", "Moyenne (2)", "\u00c9lev\u00e9e (3)",
      "Excellente (4)", "Niveau de cybers\u00e9curit\u00e9 de la partie prenante",
    ],
    stkTrust: [
      "NIVEAU DE CONFIANCE", "Faible (1)", "Moyen (2)", "\u00c9lev\u00e9 (3)",
      "Tr\u00e8s \u00e9lev\u00e9 (4)", "Fiabilit\u00e9 et confiance dans la relation",
    ],
    a6Title:
      "APPENDICE VI : \u00c9CHELLES POUR LE CALCUL DE LA VRAISEMBLANCE D'UN SC\u00c9NARIO OP\u00c9RATIONNEL",
    lklIntro:
      "Ces \u00e9chelles permettent d'\u00e9valuer la vraisemblance d'un sc\u00e9nario op\u00e9rationnel selon 4 crit\u00e8res principaux:",
    c1Title: "1. CRIT\u00c8RE: CONNAISSANCES TECHNIQUES REQUISES",
    skillHeaders: ["Niveau", "Description", "Commentaire"],
    skill1: ["1", "Connaissances g\u00e9n\u00e9rales", "Niveau d\u00e9butant, outils standard"],
    skill2: ["2", "Connaissances sp\u00e9cialis\u00e9es", "Sp\u00e9cialiste dans un domaine technique"],
    skill3: ["3", "Expertise avanc\u00e9e", "Expert multi-domaines, connaissances pouss\u00e9es"],
    skill4: ["4", "Expertise exceptionnelle", "Expert de niveau international, innovations"],
    c2Title: "2. CRIT\u00c8RE: ACC\u00c8S REQUISES",
    accessHeaders: ["Niveau", "Type d'acc\u00e8s", "Exemples"],
    acc1: ["1", "Acc\u00e8s public", "Informations ouvertement disponibles"],
    acc2: ["2", "Acc\u00e8s authentifi\u00e9", "Comptes utilisateur standards"],
    acc3: ["3", "Acc\u00e8s privil\u00e9gi\u00e9", "Comptes administrateur, comptes de service"],
    acc4: ["4", "Acc\u00e8s physique", "Acc\u00e8s direct aux mat\u00e9riels, salles serveurs"],
    c3Title: "3. CRIT\u00c8RE: TEMPS N\u00c9CESSAIRE",
    timeHeaders: ["Niveau", "Dur\u00e9e", "Type d'attaque typique"],
    time1: ["1", "Quelques minutes", "Attaques automatis\u00e9es simples"],
    time2: ["2", "Quelques heures", "Attaques avec reconnaissance initiale"],
    time3: ["3", "Quelques jours", "Attaques avec d\u00e9veloppement personnalis\u00e9"],
    time4: ["4", "Plusieurs semaines", "Campagnes d'espionnage sophistiqu\u00e9es"],
    c4Title: "4. CRIT\u00c8RE: D\u00c9TECTION ATTENDUE",
    detHeaders: ["Niveau", "Facilit\u00e9 de d\u00e9tection", "Techniques utilis\u00e9es"],
    det1: ["1", "Facilement d\u00e9tectable", "Signatures connues, comportements \u00e9vidents"],
    det2: ["2", "D\u00e9tectable avec effort", "Analyse approfondie requise"],
    det3: ["3", "Difficilement d\u00e9tectable", "Techniques d'\u00e9vasion avanc\u00e9es"],
    det4: ["4", "Tr\u00e8s difficilement d\u00e9tectable", "Attaques furtives, zero-day"],
    a7Title: "APPENDICE VII : GLOSSAIRE M\u00c9THODE EBIOS RISK MANAGER",
    gloIntro:
      "Le pr\u00e9sent glossaire d\u00e9finit les termes principaux utilis\u00e9s dans la m\u00e9thode EBIOS RM:",
    gloHeaders: ["Terme", "D\u00e9finition", "Contexte"],
  },
  en: {
    a5Title: "APPENDIX V: STAKEHOLDER EVALUATION SCALE",
    stkIntro:
      "This scale defines the evaluation criteria for ecosystem stakeholders:",
    stkThreat: [
      "THREAT LEVEL", "Low (1)", "Moderate (2)", "High (3)",
      "Critical (4)", "Attack potential and impact on the organization",
    ],
    stkDep: [
      "DEPENDENCY", "Low (1)", "Medium (2)", "High (3)",
      "Very High (4)", "Technical/commercial dependency level",
    ],
    stkPen: [
      "PENETRATION", "Low (1)", "Medium (2)", "High (3)",
      "Very High (4)", "Access capability to systems/applications",
    ],
    stkMat: [
      "CYBER MATURITY", "Low (1)", "Medium (2)", "High (3)",
      "Excellent (4)", "Cybersecurity level of the stakeholder",
    ],
    stkTrust: [
      "TRUST LEVEL", "Low (1)", "Medium (2)", "High (3)",
      "Very High (4)", "Reliability and trust in the relationship",
    ],
    a6Title:
      "APPENDIX VI: SCALES FOR CALCULATING THE LIKELIHOOD OF AN OPERATIONAL SCENARIO",
    lklIntro:
      "These scales assess the likelihood of an operational scenario according to 4 main criteria:",
    c1Title: "1. CRITERION: REQUIRED TECHNICAL KNOWLEDGE",
    skillHeaders: ["Level", "Description", "Comment"],
    skill1: ["1", "General knowledge", "Beginner level, standard tools"],
    skill2: ["2", "Specialized knowledge", "Specialist in one technical domain"],
    skill3: ["3", "Advanced expertise", "Multi-domain expert, in-depth knowledge"],
    skill4: ["4", "Exceptional expertise", "International-level expert, innovations"],
    c2Title: "2. CRITERION: REQUIRED ACCESS",
    accessHeaders: ["Level", "Access Type", "Examples"],
    acc1: ["1", "Public access", "Openly available information"],
    acc2: ["2", "Authenticated access", "Standard user accounts"],
    acc3: ["3", "Privileged access", "Administrator accounts, service accounts"],
    acc4: ["4", "Physical access", "Direct access to hardware, server rooms"],
    c3Title: "3. CRITERION: TIME REQUIRED",
    timeHeaders: ["Level", "Duration", "Typical Attack Type"],
    time1: ["1", "A few minutes", "Simple automated attacks"],
    time2: ["2", "A few hours", "Attacks with initial reconnaissance"],
    time3: ["3", "A few days", "Attacks with custom development"],
    time4: ["4", "Several weeks", "Sophisticated espionage campaigns"],
    c4Title: "4. CRITERION: EXPECTED DETECTION",
    detHeaders: ["Level", "Detection Difficulty", "Techniques Used"],
    det1: ["1", "Easily detectable", "Known signatures, obvious behaviors"],
    det2: ["2", "Detectable with effort", "In-depth analysis required"],
    det3: ["3", "Difficult to detect", "Advanced evasion techniques"],
    det4: ["4", "Very difficult to detect", "Stealth attacks, zero-days"],
    a7Title: "APPENDIX VII: EBIOS RISK MANAGER METHOD GLOSSARY",
    gloIntro:
      "This glossary defines the main terms used in the EBIOS RM method:",
    gloHeaders: ["Term", "Definition", "Context"],
  },
};

const LAPP4 = {
  fr: {
    glo1: ["BIEN SUPPORT", "\u00c9l\u00e9ment du syst\u00e8me d'information qui porte ou contribue \u00e0 une mission ou processus m\u00e9tier", "Atelier 1 - Identification des valeurs m\u00e9tier"],
    glo2: ["\u00c9V\u00c9NEMENT REDOUT\u00c9", "\u00c9v\u00e9nement que l'on souhaite \u00e9viter et qui, s'il survenait, aurait un impact sur les missions", "Atelier 1 - Analyse d'impact"],
    glo3: ["SOURCE DE RISQUE", "\u00c9l\u00e9ment qui pr\u00e9sente un potentiel intrins\u00e8que d'engendrer un risque", "Atelier 2 - Analyse de l'\u00e9cosyst\u00e8me"],
    glo4: ["PARTIE PRENANTE", "Acteur de l'\u00e9cosyst\u00e8me susceptible d'influencer ou d'\u00eatre influenc\u00e9 par l'organisation", "Atelier 3 - Cartographie de menace"],
    glo5: ["SC\u00c9NARIO STRAT\u00c9GIQUE", "Description d'une situation de risque \u00e0 haut niveau, ind\u00e9pendante des moyens techniques", "Atelier 3 - \u00c9laboration des risques"],
    glo6: ["SC\u00c9NARIO OP\u00c9RATIONNEL", "Description d\u00e9taill\u00e9e des modes op\u00e9ratoires permettant de r\u00e9aliser un sc\u00e9nario strat\u00e9gique", "Atelier 4 - Analyse technique"],
    glo7: ["TRAITEMENT DU RISQUE", "Ensemble des mesures mises en place pour r\u00e9duire, transf\u00e9rer, \u00e9viter ou accepter un risque", "Atelier 5 - Gestion du risque"],
    glo8: ["VRAISEMBLANCE", "Probabilit\u00e9 qu'un sc\u00e9nario de risque se r\u00e9alise dans un contexte donn\u00e9", "Atelier 4 - \u00c9valuation quantitative"],
    glo9: ["GRAVIT\u00c9", "Niveau d'impact potentiel d'un \u00e9v\u00e9nement redout\u00e9 sur l'organisation", "Atelier 1 - \u00c9valuation d'impact"],
    glo10: ["RISQUE R\u00c9SIDUEL", "Niveau de risque subsistant apr\u00e8s mise en place des mesures de traitement", "Atelier 5 - \u00c9valuation finale"],
  },
  en: {
    glo1: ["SUPPORT ASSET", "Information system element that carries or contributes to a business mission or process", "Workshop 1 - Business value identification"],
    glo2: ["DREADED EVENT", "Event to be avoided which, if it occurred, would impact missions", "Workshop 1 - Impact analysis"],
    glo3: ["RISK SOURCE", "Element with an intrinsic potential to cause a risk", "Workshop 2 - Ecosystem analysis"],
    glo4: ["STAKEHOLDER", "Ecosystem actor likely to influence or be influenced by the organization", "Workshop 3 - Threat mapping"],
    glo5: ["STRATEGIC SCENARIO", "Description of a high-level risk situation, independent of technical means", "Workshop 3 - Risk development"],
    glo6: ["OPERATIONAL SCENARIO", "Detailed description of operating modes to achieve a strategic scenario", "Workshop 4 - Technical analysis"],
    glo7: ["RISK TREATMENT", "Set of measures implemented to reduce, transfer, avoid or accept a risk", "Workshop 5 - Risk management"],
    glo8: ["LIKELIHOOD", "Probability that a risk scenario materializes in a given context", "Workshop 4 - Quantitative assessment"],
    glo9: ["GRAVITY", "Potential impact level of a dreaded event on the organization", "Workshop 1 - Impact assessment"],
    glo10: ["RESIDUAL RISK", "Remaining risk level after treatment measures are in place", "Workshop 5 - Final evaluation"],
  },
};

const LW5APP = {
  fr: {
    annexes: "Annexes",
    a7Title: "APPENDICE VII : GLOSSAIRE M\u00c9THODE EBIOS RISK MANAGER",
    g1: "AGENCE ANSSI: Organisation fran\u00e7aise charg\u00e9e de la s\u00e9curit\u00e9 des syst\u00e8mes d'information et r\u00e9seaux.",
    g2: "ATELIER 5: Derni\u00e8re \u00e9tape de la m\u00e9thode EBIOS RM consacr\u00e9e au traitement des risques identifi\u00e9s.",
    g3: "TRAITEMENT DU RISQUE: Ensemble des actions mises en place pour r\u00e9duire, accepter, transf\u00e9rer ou \u00e9viter un risque.",
    g4: "MESURE DE S\u00c9CURIT\u00c9: Action ou m\u00e9canisme visant \u00e0 r\u00e9duire la probabilit\u00e9 d'occurrence d'un risque.",
    g5: "RISQUE R\u00c9SIDUEL: Niveau de risque subsistant apr\u00e8s mise en place des mesures de traitement.",
    g6: "ACCEPTATION DU RISQUE: D\u00e9cision consciente de tol\u00e9rer un risque en raison de son niveau acceptable.",
    metaTitle: "M\u00e9tadonn\u00e9es du Rapport",
    mId: "ID de l'analyse",
    mName: "Nom de l'analyse",
    mCompany: "Entreprise",
    mCreatedBy: "Cr\u00e9\u00e9 par",
    mCreatedAt: "Date de cr\u00e9ation",
    mModified: "Derni\u00e8re modification",
    mVersion: "Version",
    mStatus: "Statut",
    attrValueHeaders: ["Attribut", "Valeur"],
    statsTitle: "Statistiques de G\u00e9n\u00e9ration",
    sDate: "Date de g\u00e9n\u00e9ration",
    sPages: "Nombre de pages",
    sPagesValue: "Calcul\u00e9 automatiquement",
    sServices: "Services utilis\u00e9s",
    sServicesValue: "KPI, Charts, AI, Logos",
    sGenVersion: "Version du g\u00e9n\u00e9rateur",
    statHeaders: ["Statistique", "Valeur"],
  },
  en: {
    annexes: "Appendices",
    a7Title: "APPENDIX VII: EBIOS RISK MANAGER METHOD GLOSSARY",
    g1: "ANSSI AGENCY: French organization in charge of information systems and network security.",
    g2: "WORKSHOP 5: Final step of the EBIOS RM method dedicated to the treatment of identified risks.",
    g3: "RISK TREATMENT: Set of actions implemented to reduce, accept, transfer or avoid a risk.",
    g4: "SECURITY MEASURE: Action or mechanism aimed at reducing the probability of occurrence of a risk.",
    g5: "RESIDUAL RISK: Risk level remaining after treatment measures are in place.",
    g6: "RISK ACCEPTANCE: Conscious decision to tolerate a risk due to its acceptable level.",
    metaTitle: "Report Metadata",
    mId: "Analysis ID",
    mName: "Analysis Name",
    mCompany: "Company",
    mCreatedBy: "Created By",
    mCreatedAt: "Creation Date",
    mModified: "Last Modified",
    mVersion: "Version",
    mStatus: "Status",
    attrValueHeaders: ["Attribute", "Value"],
    statsTitle: "Generation Statistics",
    sDate: "Generation date",
    sPages: "Number of pages",
    sPagesValue: "Calculated automatically",
    sServices: "Services used",
    sServicesValue: "KPI, Charts, AI, Logos",
    sGenVersion: "Generator version",
    statHeaders: ["Statistic", "Value"],
  },
};

const LHLP = {
  fr: {
    notDefined: "Non d\u00e9fini",
    notSpecified: "Non sp\u00e9cifi\u00e9",
    unspecified: "Non sp\u00e9cifi\u00e9",
    dateLocale: "fr-FR",
    pillars: {
      confidentiality: "Confidentialit\u00e9",
      integrity: "Int\u00e9grit\u00e9",
      availability: "Disponibilit\u00e9",
      authenticity: "Authenticit\u00e9",
      accountability: "Tra\u00e7abilit\u00e9",
      "non-repudiation": "Non-r\u00e9pudiation",
    },
    severities: {
      "very-low": "Tr\u00e8s faible",
      low: "Faible",
      medium: "Moyen",
      high: "\u00c9lev\u00e9",
      "very-high": "Tr\u00e8s \u00e9lev\u00e9",
      critical: "Critique",
    },
    levels: { faible: "Faible", moyen: "Moyen", eleve: "\u00c9lev\u00e9" },
    resources: {
      faibles: "Faibles",
      moyennes: "Moyennes",
      importantes: "Importantes",
    },
    sourceCategories: {
      criminel: "Criminel",
      malveillant: "Malveillant",
      activiste: "Activiste",
      amateur: "Amateur",
      etatique: "\u00c9tatique",
      terroriste: "Terroriste",
      officine: "Officine",
    },
    numLevels: {
      5: "5 - Tr\u00e8s \u00e9lev\u00e9",
      4: "4 - \u00c9lev\u00e9",
      3: "3 - Mod\u00e9r\u00e9",
      2: "2 - Faible",
      1: "1 - Tr\u00e8s faible",
    },
    outOfScaleSuffix: "Hors \u00e9chelle",
    threatLevels: {
      low: "Peu vraisemblable",
      faible: "Peu vraisemblable",
      medium: "Vraisemblable",
      "mod\u00e9r\u00e9": "Vraisemblable",
      high: "Tr\u00e8s vraisemblable",
      "\u00e9lev\u00e9": "Tr\u00e8s vraisemblable",
      critical: "Quasi certain",
      critique: "Quasi certain",
    },
    priorities: {
      high: "\u00c9lev\u00e9",
      "elev\u00e9": "\u00c9lev\u00e9",
      medium: "Mod\u00e9r\u00e9",
      "mod\u00e9r\u00e9": "Mod\u00e9r\u00e9",
      low: "Faible",
      faible: "Faible",
      critical: "Critique",
      critique: "Critique",
    },
    implStatus: {
      "not-started": "Non d\u00e9marr\u00e9",
      planned: "Planifi\u00e9",
      "in-progress": "En cours",
      in_progress: "En cours",
      completed: "Termin\u00e9",
      implemented: "Impl\u00e9ment\u00e9",
      applied: "Appliqu\u00e9",
    },
    measureCategories: {
      technical: "Technique",
      organizational: "Organisationnel",
      procedural: "Proc\u00e9dural",
      preventive: "Pr\u00e9ventif",
      detective: "D\u00e9tective",
      corrective: "Correctif",
      ecosystem: "\u00c9cosyst\u00e8me",
    },
    probLevels: {
      1: "1 - Tr\u00e8s faible",
      2: "2 - Faible",
      3: "3 - \u00c9lev\u00e9e",
      4: "4 - Tr\u00e8s \u00e9lev\u00e9e",
    },
    diffLevels: {
      1: "1 - \u00c9l\u00e9mentaire",
      2: "2 - Mod\u00e9r\u00e9e",
      3: "3 - Difficile",
      4: "4 - Expert",
    },
    stakeholderCategories: {
      client: "Client",
      partner: "Partenaire",
      provider: "Prestataire",
      technical: "Service technique",
      business: "Service m\u00e9tier",
      subsidiary: "Filiale",
      internal: "Interne",
      external: "Externe",
      competitors: "Concurrents",
      competitor: "Concurrent",
      customers: "Clients",
      customer: "Client",
      "business partners": "Partenaires d'affaires",
      "business partner": "Partenaire d'affaires",
      regulators: "R\u00e9gulateurs",
      regulator: "R\u00e9gulateur",
      suppliers: "Fournisseurs",
      supplier: "Fournisseur",
      vendors: "Fournisseurs",
      vendor: "Fournisseur",
      employees: "Employ\u00e9s",
      employee: "Employ\u00e9",
      stakeholders: "Parties prenantes",
      stakeholder: "Partie prenante",
    },
    threatHigh: "\u00c9lev\u00e9",
    threatModerate: "Mod\u00e9r\u00e9",
    threatLow: "Faible",
    defaultTrust: "3 - Mod\u00e9r\u00e9",
  },
  en: {
    notDefined: "Undefined",
    notSpecified: "Not specified",
    unspecified: "Unspecified",
    dateLocale: "en-GB",
    pillars: {
      confidentiality: "Confidentiality",
      integrity: "Integrity",
      availability: "Availability",
      authenticity: "Authenticity",
      accountability: "Accountability",
      "non-repudiation": "Non-repudiation",
    },
    severities: {
      "very-low": "Very Low",
      low: "Low",
      medium: "Medium",
      high: "High",
      "very-high": "Very High",
      critical: "Critical",
    },
    levels: { faible: "Low", moyen: "Medium", eleve: "High" },
    resources: {
      faibles: "Low",
      moyennes: "Medium",
      importantes: "High",
    },
    sourceCategories: {
      criminel: "Criminal",
      malveillant: "Malicious",
      activiste: "Activist",
      amateur: "Amateur",
      etatique: "State",
      terroriste: "Terrorist",
      officine: "Mercenary",
    },
    numLevels: {
      5: "5 - Very High",
      4: "4 - High",
      3: "3 - Moderate",
      2: "2 - Low",
      1: "1 - Very Low",
    },
    outOfScaleSuffix: "Out of scale",
    threatLevels: {
      low: "Unlikely",
      faible: "Unlikely",
      medium: "Likely",
      "mod\u00e9r\u00e9": "Likely",
      high: "Very likely",
      "\u00e9lev\u00e9": "Very likely",
      critical: "Almost certain",
      critique: "Almost certain",
    },
    priorities: {
      high: "High",
      "elev\u00e9": "High",
      medium: "Moderate",
      "mod\u00e9r\u00e9": "Moderate",
      low: "Low",
      faible: "Low",
      critical: "Critical",
      critique: "Critical",
    },
    implStatus: {
      "not-started": "Not Started",
      planned: "Planned",
      "in-progress": "In Progress",
      in_progress: "In Progress",
      completed: "Completed",
      implemented: "Implemented",
      applied: "Applied",
    },
    measureCategories: {
      technical: "Technical",
      organizational: "Organizational",
      procedural: "Procedural",
      preventive: "Preventive",
      detective: "Detective",
      corrective: "Corrective",
      ecosystem: "Ecosystem",
    },
    probLevels: {
      1: "1 - Very Low",
      2: "2 - Low",
      3: "3 - High",
      4: "4 - Very High",
    },
    diffLevels: {
      1: "1 - Elementary",
      2: "2 - Moderate",
      3: "3 - Difficult",
      4: "4 - Expert",
    },
    stakeholderCategories: {
      client: "Client",
      partner: "Partner",
      provider: "Provider",
      technical: "Technical department",
      business: "Business department",
      subsidiary: "Subsidiary",
      internal: "Internal",
      external: "External",
      competitors: "Competitors",
      competitor: "Competitor",
      customers: "Customers",
      customer: "Customer",
      "business partners": "Business partners",
      "business partner": "Business partner",
      regulators: "Regulators",
      regulator: "Regulator",
      suppliers: "Suppliers",
      supplier: "Supplier",
      vendors: "Vendors",
      vendor: "Vendor",
      employees: "Employees",
      employee: "Employee",
      stakeholders: "Stakeholders",
      stakeholder: "Stakeholder",
    },
    threatHigh: "High",
    threatModerate: "Moderate",
    threatLow: "Low",
    defaultTrust: "3 - Moderate",
  },
};

const LCOVER = {
  fr: {
    program: "Programme :",
    programValue: "Analyse de Risques \u2013 EBIOS RM",
    internal: "Interne",
    document: "Document :",
    docTypeFull: "Rapport Global d'Analyse de Risques",
    docTypeW5: "Rapport Atelier 5 - Plan de Traitement du Risque",
    version: "Version :",
    ref: "R\u00e9f. :",
    refDefault: "EBIOSRM_REPORT",
    mainTitle: "Rapport d'Analyse de Risques :",
    defaultAnalysis: "Analyse EBIOS RM",
    subtitleFull: "EBIOS RM - Analyse Compl\u00e8te",
    subtitleW5: "EBIOS RM - Atelier 5",
    tProgram: "Programme",
    tProgramValue: "Analyse de Risques - EBIOS RM",
    tOrganization: "Organisation",
    tNotSpecified: "Non sp\u00e9cifi\u00e9e",
    tClassification: "Classification",
    tClassificationValue: "Usage Interne Uniquement",
    tReviewFreq: "Fr\u00e9quence de R\u00e9vision",
    tReviewFreqValue: "Annuelle / Changements Majeurs",
    tStatus: "Statut",
    tStatusDefault: "Finalis\u00e9",
    tGenDate: "Date de G\u00e9n\u00e9ration",
    historyHeaders: [
      "Version", "Date", "Description, notes", "Auteur", "Relecteur", "Approbateur",
    ],
    initialReport: "Rapport Initial",
    system: "Syst\u00e8me",
    footerFull: "M\u00e9thodologie EBIOS RM - Analyse Globale",
    footerW5:
      "M\u00e9thodologie EBIOS RM - Atelier 5 : Traitement du Risque",
    confidential: "Document Confidentiel - Usage Interne Uniquement",
  },
  en: {
    program: "Program:",
    programValue: "Risk Analysis \u2013 EBIOS RM",
    internal: "Internal",
    document: "Document:",
    docTypeFull: "Global Risk Analysis Report",
    docTypeW5: "Workshop 5 Report - Risk Treatment Plan",
    version: "Version:",
    ref: "Ref:",
    refDefault: "EBIOSRM_REPORT",
    mainTitle: "Risk Analysis Report:",
    defaultAnalysis: "EBIOS RM Analysis",
    subtitleFull: "EBIOS RM - Complete Analysis",
    subtitleW5: "EBIOS RM - Workshop 5",
    tProgram: "Program",
    tProgramValue: "Risk Analysis - EBIOS RM",
    tOrganization: "Organization",
    tNotSpecified: "Not specified",
    tClassification: "Classification",
    tClassificationValue: "Internal Use Only",
    tReviewFreq: "Review Frequency",
    tReviewFreqValue: "Annual / Major Changes",
    tStatus: "Status",
    tStatusDefault: "Finalized",
    tGenDate: "Generation Date",
    historyHeaders: [
      "Version", "Date", "Description, notes", "Author", "Reviewer", "Approver",
    ],
    initialReport: "Initial Report",
    system: "System",
    footerFull: "EBIOS RM Methodology - Global Analysis",
    footerW5:
      "EBIOS RM Methodology - Workshop 5: Risk Treatment",
    confidential: "Confidential Document - Internal Use Only",
  },
};

const LEXEC = {
  fr: {
    title: "1. SYNTH\u00c8SE EX\u00c9CUTIVE",
    intro:
      "Cette synth\u00e8se pr\u00e9sente les r\u00e9sultats principaux de l'analyse EBIOS RM men\u00e9e pour {org}. L'analyse a \u00e9t\u00e9 conduite selon la m\u00e9thodologie EBIOS RM (Expression des Besoins et Identification des Objectifs de S\u00e9curit\u00e9 - Risk Manager) d\u00e9velopp\u00e9e par l'ANSSI, permettant une approche structur\u00e9e et syst\u00e9matique de l'\u00e9valuation des risques cybers\u00e9curit\u00e9.",
    contextTitle: "CONTEXTE DE L'ANALYSE",
    contextIntro:
      "Cette section pr\u00e9sente le contexte g\u00e9n\u00e9ral dans lequel s'inscrit l'analyse de risques. Elle d\u00e9finit les \u00e9l\u00e9ments fondamentaux qui ont guid\u00e9 la d\u00e9marche d'\u00e9valuation et les objectifs poursuivis.",
    orgFallback: "l'organisation",
    lTitle: "\u2022 Titre:",
    lTitleDefault: "Analyse EBIOS RM",
    lOrg: "\u2022 Organisation:",
    lDate: "\u2022 Date de g\u00e9n\u00e9ration:",
    lVersion: "\u2022 Version:",
    lScope: "\u2022 P\u00e9rim\u00e8tre:",
    lObjective: "\u2022 Objectif:",
    lObjectiveDefault:
      "Analyse de risques selon la m\u00e9thode EBIOS RM",
    dateLocale: "fr-FR",
  },
  en: {
    title: "1. EXECUTIVE SUMMARY",
    intro:
      "This executive summary presents the main results of the EBIOS RM analysis conducted for {org}. The analysis was conducted according to the EBIOS RM methodology (Expression des Besoins et Identification des Objectifs de S\u00e9curit\u00e9 - Risk Manager) developed by ANSSI, allowing for a structured and systematic approach to cybersecurity risk assessment.",
    contextTitle: "ANALYSIS CONTEXT",
    contextIntro:
      "This section presents the general context of the risk analysis. It defines the fundamental elements that guided the evaluation process and the objectives pursued.",
    orgFallback: "the organization",
    lTitle: "\u2022 Title:",
    lTitleDefault: "EBIOS RM Analysis",
    lOrg: "\u2022 Organization:",
    lDate: "\u2022 Generation Date:",
    lVersion: "\u2022 Version:",
    lScope: "\u2022 Scope:",
    lObjective: "\u2022 Objective:",
    lObjectiveDefault:
      "Risk analysis according to EBIOS RM method",
    dateLocale: "en-GB",
  },
};

const LABELS = {
  fr: {
    meta: LMETA.fr,
    viz: LVIZ.fr,
    ch2: LCH2.fr,
    cats: LCATS.fr,
    ch3: LCH3.fr,
    ch4: LCH4.fr,
    carto: LCARTO.fr,
    s42: LS42P.fr,
    prog: LS42P.fr,
    ch5: LCH5.fr.ch5,
    phases: LCH5.fr.phases,
    ch6: LCH6.fr.ch6,
    impl: LCH6.fr.impl,
    app: Object.assign({}, LAPP1.fr, LAPP2.fr, LAPP3.fr, LAPP4.fr),
    w5app: LW5APP.fr,
    hlp: LHLP.fr,
    cover: LCOVER.fr,
    exec: LEXEC.fr,
  },
  en: {
    meta: LMETA.en,
    viz: LVIZ.en,
    ch2: LCH2.en,
    cats: LCATS.en,
    ch3: LCH3.en,
    ch4: LCH4.en,
    carto: LCARTO.en,
    s42: LS42P.en,
    prog: LS42P.en,
    ch5: LCH5.en.ch5,
    phases: LCH5.en.phases,
    ch6: LCH6.en.ch6,
    impl: LCH6.en.impl,
    app: Object.assign({}, LAPP1.en, LAPP2.en, LAPP3.en, LAPP4.en),
    w5app: LW5APP.en,
    hlp: LHLP.en,
    cover: LCOVER.en,
    exec: LEXEC.en,
  },
};

/**
 * Comprehensive Report Content Generator
 * Combines all services to create detailed analysis sections with AI insights
 */
class ReportContentGenerator {
  constructor(
    reportData,
    kpiData,
    chartData,
    aiInsights,
    logoData,
    options = {},
    lang = "fr",
  ) {
    this.reportData = reportData;
    this.kpiData = kpiData || {};
    this.chartData = chartData || {};
    this.aiInsights = aiInsights || {};
    this.logoData = logoData || {};
    this.options = options; // Store options for report generation scope
    this.analysisId = options.analysisId || reportData?.metadata?.analysisId; // Store analysisId for radar image retrieval
    this.lang = lang === "en" ? "en" : "fr";
    const L = LABELS[this.lang];

    // Initialize PDF generator
    this.pdf = new EnhancedPDFGenerator({
      title: options.workshop5Only
        ? `${L.meta.w5ReportTitle} - ${reportData.metadata?.analysisName || "EBIOS RM"}`
        : reportData.metadata?.analysisName || L.meta.defaultReportName,
      author: reportData.metadata?.createdBy || "EBIOS RM System",
      subject: options.workshop5Only
        ? `${L.meta.w5ReportTitle} - ${reportData.metadata?.companyName || L.meta.organizationFallback}`
        : `${L.meta.riskAnalysisSubject} - ${reportData.metadata?.companyName || L.meta.organizationFallback}`,
      workshop5Only: options.workshop5Only, // Pass the workshop5Only flag
      language: this.lang,
    });

    // Set logos with fallback
    this.pdf.setLogos(this.logoData);

    // Initialize chart integration
    this.chartIntegration = new PDFChartIntegration(this.pdf);
  }

  /**
   * Generate complete enhanced report following EBIOS RM structure
   * @returns {Promise<Blob>} PDF blob
   */
  async generateCompleteReport() {
    try {
      console.log(
        "[ReportContentGenerator] Starting complete report generation",
      );

      // 1. Generate document structure (cover page + executive summary on page 2)
      if (this.lang === "en") {
        this.generateEnglishCoverPage(this.reportData);
        this.pdf.reserveTableOfContentsPages();
        this.pdf.startContentSection();
        this.generateEnglishExecutiveSummary();
      } else {
        this.pdf.generateDocumentStructure(this.reportData);
      }

      // 2. Chapter 2: CADRAGE ET SOCLE DE S\u00c9CURIT\u00c9
      await this.generateAtelier1Cadrage();

      // 3. Chapter 3: SOURCES DE RISQUE ET OBJECTIFS VIS\u00c9S
      await this.generateChapitre2();

      // 4. Chapter 4: SC\u00c9NARIOS STRAT\u00c9GIQUES
      await this.generateChapitre3();

      // 5. Chapter 5: SC\u00c9NARIOS OP\u00c9RATIONNELS
      await this.generateChapitre4();

      // 6. Chapter 6: TRAITEMENT DU RISQUE
      await this.generateChapitre5();

      // 7. Chapter 7: PLAN DE TRAITEMENT DES CONTR\u00d4LES
      await this.generateChapitre6();

      // 9. Appendices
      await this.generateAppendices();

      // 10. Table of Contents (generated after all content)
      this.pdf.generateTableOfContents();

      console.log("[ReportContentGenerator] Report generation completed");
      return this.pdf.getBlob();
    } catch (error) {
      console.error("[ReportContentGenerator] Error generating report:", error);
      throw error;
    } finally {
      // Cleanup resources
      this.chartIntegration.cleanup();
    }
  }

  /**
   * Generate workshop 5 only report
   * @returns {Promise<Blob>} PDF blob
   */
  async generateWorkshop5Report() {
    try {
      console.log(
        "[ReportContentGenerator] Starting workshop 5 only report generation",
      );

      // 1. Generate document structure (cover page + executive summary on page 2)
      if (this.lang === "en") {
        this.generateEnglishCoverPage(this.reportData);
        this.pdf.reserveTableOfContentsPages();
        this.pdf.startContentSection();
        this.generateEnglishExecutiveSummary();
      } else {
        this.pdf.generateDocumentStructure(this.reportData);
      }

      // 2. Workshop 5 Content: TRAITEMENT DU RISQUE (now chapter 6)
      await this.generateChapitre5();

      // 2.1. Add radar visualizations from previous workshops for context
      const L = this.L;
      this.pdf.addSectionHeading(L.viz.prevWorkshopsViz, 1);

      // Add Workshop 2 radar visualization
      this.pdf.addSectionHeading(L.viz.w2RadarTitle, 2);
      this.pdf.addParagraph(L.viz.w2RadarDesc);
      console.log(
        "🎯 [Workshop5Report] About to add Workshop 2 radar visualization...",
      );
      await this.addRadarVisualization(true); // Skip heading since we added it above

      // Add Workshop 3 stakeholder radar visualization
      this.pdf.addSectionHeading(L.viz.w3RadarTitle, 2);
      this.pdf.addParagraph(L.viz.w3RadarDesc);
      console.log(
        "🎯 [Workshop5Report] About to add Workshop 3 stakeholder radar visualization...",
      );
      await this.addStakeholderRadarVisualization(true); // Skip heading since we added it above

      // 3. Appendices relevant to workshop 5
      this.generateWorkshop5Appendices();

      // 4. Table of Contents (generated after all content with correct page numbers)
      this.pdf.generateTableOfContents();

      console.log(
        "[ReportContentGenerator] Workshop 5 report generation completed",
      );
      return this.pdf.getBlob();
    } catch (error) {
      console.error(
        "[ReportContentGenerator] Error generating workshop 5 report:",
        error,
      );
      throw error;
    } finally {
      // Cleanup resources
      this.chartIntegration.cleanup();
    }
  }

  /**
   * Generate customized English cover page (lang === 'en' only)
   */
  generateEnglishCoverPage(reportData) {
    const L = this.L.cover;
    const doc = this.pdf.pdf;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, "F");

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(margin, 15, 180, 10, "S");

    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(L.program, margin + 2, 21);
    doc.setFont("helvetica", "normal");
    doc.text(L.programValue, margin + 30, 21);

    doc.setFillColor(200, 200, 200);
    doc.rect(135, 15, 60, 10, "FD");
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(L.internal, 155, 21);

    doc.setFillColor(255, 255, 255);
    doc.rect(margin, 25, 180, 8, "S");
    doc.setFont("helvetica", "bold");
    doc.text(L.document, margin + 2, 30);
    doc.setFont("helvetica", "normal");
    const docType = this.options.workshop5Only
      ? L.docTypeW5
      : L.docTypeFull;
    doc.text(docType, margin + 30, 30);

    doc.rect(margin, 33, 90, 8, "S");
    doc.setFont("helvetica", "bold");
    doc.text(L.version, margin + 2, 38);
    doc.setFont("helvetica", "normal");
    doc.text(reportData.metadata?.version || "1.0", margin + 22, 38);

    doc.rect(105, 33, 90, 8, "S");
    doc.setFont("helvetica", "bold");
    doc.text(L.ref, 107, 38);
    doc.setFont("helvetica", "normal");
    doc.text(
      reportData.metadata?.analysisId || L.refDefault,
      120,
      38,
    );

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(37, 99, 235);
    doc.text(L.mainTitle, pageWidth / 2, 65, { align: "center" });

    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    const analysisName =
      reportData.metadata?.analysisName ||
      reportData.metadata?.companyName ||
      L.defaultAnalysis;
    doc.text(analysisName, pageWidth / 2, 80, { align: "center" });

    doc.setFontSize(24);
    doc.setTextColor(37, 99, 235);
    const subTitle = this.options.workshop5Only
      ? L.subtitleW5
      : L.subtitleFull;
    doc.text(subTitle, pageWidth / 2, 110, { align: "center" });
    doc.setTextColor(0, 0, 0);

    const tableStartY = 135;
    const rowHeight = 8;
    const labelColWidth = 65;
    const valueColWidth = 115;

    const tableData = [
      [L.tProgram, L.tProgramValue],
      [L.tOrganization, reportData.metadata?.companyName || L.tNotSpecified],
      [L.tClassification, L.tClassificationValue],
      [L.tReviewFreq, L.tReviewFreqValue],
      [L.tStatus, reportData.metadata?.status || L.tStatusDefault],
      [L.tGenDate, new Date().toLocaleDateString(this.L.hlp.dateLocale)],
    ];

    tableData.forEach((row, index) => {
      const y = tableStartY + index * rowHeight;

      doc.setFillColor(41, 82, 163);
      doc.rect(margin, y, labelColWidth, rowHeight, "FD");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(row[0], margin + 2, y + 5.5);

      doc.setFillColor(235, 235, 235);
      doc.rect(margin + labelColWidth, y, valueColWidth, rowHeight, "FD");
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");

      const text = row[1];
      if (doc.getTextWidth(text) > valueColWidth - 5) {
        const truncated = text.substring(0, 55) + "...";
        doc.text(truncated, margin + labelColWidth + 2, y + 5.5);
      } else {
        doc.text(text, margin + labelColWidth + 2, y + 5.5);
      }
    });

    const historyY = 200;
    const headerHeight = 10;
    const dataRowHeight = 15;
    const colWidths = [18, 22, 70, 30, 20, 20];
    let xPos = margin;

    L.historyHeaders.forEach((header, i) => {
      doc.setFillColor(41, 82, 163);
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.rect(xPos, historyY, colWidths[i], headerHeight, "FD");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.text(header, xPos + 2, historyY + 6);

      xPos += colWidths[i];
    });

    xPos = margin;
    const historyData = [
      reportData.metadata?.version || "1.0",
      new Date().toLocaleDateString(this.L.hlp.dateLocale),
      L.initialReport,
      reportData.metadata?.createdBy || L.system,
      "",
      "",
    ];

    historyData.forEach((data, i) => {
      doc.setFillColor(245, 245, 245);
      doc.setDrawColor(0, 0, 0);
      doc.rect(xPos, historyY + headerHeight, colWidths[i], dataRowHeight, "FD");

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");

      const maxColWidth = colWidths[i] - 3;
      const lines = doc.splitTextToSize(data, maxColWidth);

      lines.slice(0, 3).forEach((line, idx) => {
        doc.text(line, xPos + 2, historyY + headerHeight + 5 + idx * 3);
      });

      xPos += colWidths[i];
    });

    const footerY = pageHeight - 20;
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.setFont("helvetica", "normal");
    const footerText = this.options.workshop5Only
      ? L.footerW5
      : L.footerFull;
    doc.text(footerText, pageWidth / 2, footerY, { align: "center" });
    doc.text(L.confidential, pageWidth / 2, footerY + 8, {
      align: "center",
    });
  }

  /**
   * Generate English executive summary (lang === 'en' only)
   */
  generateEnglishExecutiveSummary() {
    const L = this.L.exec;
    this.pdf.addSectionHeading(L.title, 1);
    this.pdf.addSpace(1);

    const contextInfo = (this.reportData.executiveSummary || {}).contextInfo || {};
    const metadata = this.reportData.metadata || {};

    this.pdf.addParagraph(
      L.intro.replace("{org}", contextInfo.organizationName || L.orgFallback),
    );

    this.pdf.addSpace(8);
    this.pdf.addSectionHeading(L.contextTitle, 2);
    this.pdf.addParagraph(L.contextIntro);

    this.pdf.addParagraph(`${L.lTitle} ${contextInfo.analysisName || L.lTitleDefault}`);
    this.pdf.addParagraph(`${L.lOrg} ${contextInfo.organizationName || "-"}`);
    this.pdf.addParagraph(
      `${L.lDate} ${new Date().toLocaleDateString(L.dateLocale)}`,
    );
    this.pdf.addParagraph(`${L.lVersion} ${metadata.version || "1.0"}`);
    this.pdf.addParagraph(`${L.lScope} ${contextInfo.scope || "-"}`);
    this.pdf.addParagraph(
      `${L.lObjective} ${contextInfo.analysisObjective || L.lObjectiveDefault}`,
    );
    this.pdf.addSpace(10);
  }

  /**
   * Generate manual executive summary when AI is not available
   */
  generateManualExecutiveSummary() {
    const totalRisks = this.kpiData?.riskMetrics?.totalRiskScenarios || 0;
    const highRisks = this.kpiData?.riskMetrics?.highRiskScenarios || 0;
    const companyName =
      this.reportData.metadata?.companyName || "l'organisation";

    this.pdf.addSectionHeading("Contexte de l'Analyse", 2);
    this.pdf.addParagraph(
      `Cette analyse EBIOS RM a \u00e9t\u00e9 men\u00e9e pour ${companyName} afin d'identifier, \u00e9valuer et traiter les risques cybers\u00e9curit\u00e9. ` +
        `L'analyse a permis d'identifier ${totalRisks} sc\u00e9narios de risque, dont ${highRisks} class\u00e9s \u00e0 risque \u00e9lev\u00e9.`,
    );

    this.pdf.addSectionHeading("Principales Constatations", 2);
    const findings = [
      `${totalRisks} sc\u00e9narios de risque identifi\u00e9s au total`,
      `${highRisks} sc\u00e9narios n\u00e9cessitent une attention prioritaire`,
      `${this.kpiData?.controlMetrics?.implementedControls || 0} mesures de s\u00e9curit\u00e9 planifi\u00e9es`,
      `Score de sant\u00e9 global de l'analyse: ${this.kpiData?.healthScore?.overallHealthScore || "N/A"}`,
    ];
    this.pdf.addBulletList(findings);

    this.pdf.addSectionHeading("Recommandations Principales", 2);
    const recommendations = [
      "Prioriser le traitement des sc\u00e9narios \u00e0 risque \u00e9lev\u00e9",
      "Mettre en \u0153uvre les contr\u00f4les de s\u00e9curit\u00e9 recommand\u00e9s",
      "\u00c9tablir un processus de suivi et de r\u00e9vision p\u00e9riodique",
      "Former les \u00e9quipes sur les sc\u00e9narios de risque identifi\u00e9s",
    ];
    this.pdf.addBulletList(recommendations, { bulletStyle: "💡" });
  }

  /**
   * Generate executive summary metrics
   */
  generateExecutiveSummaryMetrics() {
    this.pdf.addSectionHeading("M\u00e9triques Cl\u00e9s", 2);

    let currentX = this.pdf.margin;
    const cardSpacing = 5;

    // Total risks KPI
    const totalRisksWidth = this.pdf.addKPICard(
      "Risques Identifi\u00e9s",
      this.kpiData?.riskMetrics?.totalRiskScenarios || 0,
      { color: "#3b82f6" },
    );
    currentX += totalRisksWidth + cardSpacing;

    // High risks KPI
    const highRisksWidth = this.pdf.addKPICard(
      "Risques \u00c9lev\u00e9s",
      this.kpiData?.riskMetrics?.highRiskScenarios || 0,
      { color: "#ef4444" },
    );
    currentX += highRisksWidth + cardSpacing;

    // Controls KPI
    this.pdf.addKPICard(
      "Contr\u00f4les Planifi\u00e9s",
      this.kpiData?.controlMetrics?.implementedControls || 0,
      { color: "#10b981" },
    );

    this.pdf.addSpace(5);

    // Add detailed metrics table
    const metricsHeaders = [
      "Cat\u00e9gorie",
      "M\u00e9trique",
      "Valeur",
      "Statut",
    ];
    const metricsRows = [
      [
        "Analyse",
        "Valeurs m\u00e9tier identifi\u00e9es",
        this.kpiData?.businessMetrics?.totalBusinessValues || 0,
        "Compl\u00e9t\u00e9",
      ],
      [
        "Analyse",
        "Biens supports identifi\u00e9s",
        this.kpiData?.businessMetrics?.totalSupportAssets || 0,
        "Compl\u00e9t\u00e9",
      ],
      [
        "Analyse",
        "\u00c9v\u00e9nements redout\u00e9s",
        this.kpiData?.riskMetrics?.totalDreadedEvents || 0,
        "Compl\u00e9t\u00e9",
      ],
      [
        "Risques",
        "Sc\u00e9narios de risque",
        this.kpiData?.riskMetrics?.totalRiskScenarios || 0,
        "En cours",
      ],
      [
        "Risques",
        "Risques critiques",
        this.kpiData?.riskMetrics?.criticalRiskScenarios || 0,
        "Attention",
      ],
      [
        "Risques",
        "Risques \u00e9lev\u00e9s",
        this.kpiData?.riskMetrics?.highRiskScenarios || 0,
        "Surveillance",
      ],
      [
        "Contr\u00f4les",
        "Mesures planifi\u00e9es",
        this.kpiData?.controlMetrics?.plannedControls || 0,
        "En cours",
      ],
      [
        "Contr\u00f4les",
        "Mesures impl\u00e9ment\u00e9es",
        this.kpiData?.controlMetrics?.implementedControls || 0,
        "Suivi",
      ],
    ];

    this.pdf.addTable(metricsHeaders, metricsRows, {
      fontSize: 9,
      columnWidths: [40, 60, 30, 35], // Adjust column widths for better display
    });

    this.pdf.addSpace(10);
  }

  /**
   * Generate methodology overview
   */
  generateMethodologyOverview() {
    this.pdf.addSectionHeading("M\u00e9thodologie EBIOS RM", 1);

    this.pdf.addParagraph(
      "EBIOS RM (Expression des Besoins et Identification des Objectifs de S\u00e9curit\u00e9 - Risk Manager) " +
        "est la m\u00e9thode fran\u00e7aise de gestion des risques SSI d\u00e9velopp\u00e9e par l'ANSSI. Cette m\u00e9thode " +
        "structure l'analyse des risques en 5 ateliers compl\u00e9mentaires.",
    );

    // Workshop overview
    const workshops = [
      {
        number: 1,
        title: "P\u00e9rim\u00e8tre et socle de s\u00e9curit\u00e9",
        description:
          "D\u00e9finition du p\u00e9rim\u00e8tre d'\u00e9tude, identification des valeurs m\u00e9tier et des \u00e9v\u00e9nements redout\u00e9s",
      },
      {
        number: 2,
        title: "Sources de risque",
        description:
          "Identification des sources de risque et analyse de l'\u00e9cosyst\u00e8me",
      },
      {
        number: 3,
        title: "Sc\u00e9narios strat\u00e9giques",
        description:
          "Construction des sc\u00e9narios strat\u00e9giques et \u00e9valuation de leur gravit\u00e9",
      },
      {
        number: 4,
        title: "Sc\u00e9narios op\u00e9rationnels",
        description:
          "D\u00e9tail des sc\u00e9narios op\u00e9rationnels et \u00e9valuation de leur vraisemblance",
      },
      {
        number: 5,
        title: "Traitement du risque",
        description:
          "D\u00e9finition et planification des mesures de traitement des risques",
      },
    ];

    workshops.forEach((workshop) => {
      this.pdf.addSectionHeading(
        `Atelier ${workshop.number}: ${workshop.title}`,
        3,
      );
      this.pdf.addParagraph(workshop.description);
      this.pdf.addSpace(3);
    });

    // Methodology benefits
    this.pdf.addSectionHeading("Avantages de la M\u00e9thodologie", 2);
    const benefits = [
      "Approche structur\u00e9e et progressive",
      "Prise en compte de l'\u00e9cosyst\u00e8me complet",
      "\u00c9valuation quantitative et qualitative des risques",
      "Alignement avec les standards internationaux",
      "Facilite la communication avec la direction",
    ];
    this.pdf.addBulletList(benefits, { bulletStyle: "✓" });
  }

  /**
   * Generate KPI dashboard
   */
  async generateKPIDashboard() {
    this.pdf.addSectionHeading("Tableau de Bord des Indicateurs", 1);

    // Risk metrics section
    this.pdf.addSectionHeading("M\u00e9triques de Risque", 2);
    this.generateRiskMetricsCards();

    // Control metrics section
    this.pdf.addSectionHeading("M\u00e9triques de Contr\u00f4le", 2);
    this.generateControlMetricsCards();

    // Health score section
    this.pdf.addSectionHeading("Score de Sant\u00e9 de l'Analyse", 2);
    this.generateHealthScoreCards();

    // Add health score radar chart if available
    if (this.chartData?.healthMetrics?.healthScoreRadarChart) {
      await this.chartIntegration.addRadarChart(
        this.chartData.healthMetrics.healthScoreRadarChart,
        { width: 100, height: 100 },
      );
    }
  }

  /**
   * Generate risk metrics cards
   */
  generateRiskMetricsCards() {
    const riskMetrics = this.kpiData?.riskMetrics || {};

    let currentX = this.pdf.margin;
    const cardSpacing = 5;

    // Total risks
    const card1Width = this.pdf.addKPICard(
      "Total Risques",
      riskMetrics.totalRiskScenarios || 0,
      { color: "#3b82f6" },
    );
    currentX += card1Width + cardSpacing;

    // High risks
    const card2Width = this.pdf.addKPICard(
      "Risques \u00c9lev\u00e9s",
      riskMetrics.highRiskScenarios || 0,
      { color: "#ef4444" },
    );
    currentX += card2Width + cardSpacing;

    // Critical risks
    const card3Width = this.pdf.addKPICard(
      "Risques Critiques",
      riskMetrics.criticalRiskScenarios || 0,
      { color: "#dc2626" },
    );
    currentX += card3Width + cardSpacing;

    // Average severity
    this.pdf.addKPICard(
      "S\u00e9v\u00e9rit\u00e9 Moyenne",
      riskMetrics.averageRiskSeverity || "N/A",
      { color: "#f59e0b", unit: "/5" },
    );

    this.pdf.addSpace(15);
  }

  /**
   * Generate control metrics cards
   */
  generateControlMetricsCards() {
    const controlMetrics = this.kpiData?.controlMetrics || {};

    let currentX = this.pdf.margin;
    const cardSpacing = 5;

    // Implemented controls
    const card1Width = this.pdf.addKPICard(
      "Contr\u00f4les Impl\u00e9ment\u00e9s",
      controlMetrics.implementedControls || 0,
      { color: "#10b981" },
    );
    currentX += card1Width + cardSpacing;

    // Planned controls
    const card2Width = this.pdf.addKPICard(
      "Contr\u00f4les Planifi\u00e9s",
      controlMetrics.plannedControls || 0,
      { color: "#3b82f6" },
    );
    currentX += card2Width + cardSpacing;

    // Implementation coverage
    this.pdf.addKPICard(
      "Couverture",
      controlMetrics.implementationCoveragePercentage?.replace("%", "") || 0,
      { color: "#8b5cf6", unit: "%" },
    );

    this.pdf.addSpace(15);
  }

  /**
   * Generate health score cards
   */
  generateHealthScoreCards() {
    const healthScore = this.kpiData?.healthScore || {};

    let currentX = this.pdf.margin;
    const cardSpacing = 5;

    // Overall health
    const card1Width = this.pdf.addKPICard(
      "Score Global",
      parseFloat(healthScore.overallHealthScore || 0).toFixed(1),
      {
        color: this.getHealthColor(
          parseFloat(healthScore.overallHealthScore || 0),
        ),
        unit: "%",
      },
    );
    currentX += card1Width + cardSpacing;

    // Completeness
    const card2Width = this.pdf.addKPICard(
      "Compl\u00e9tude",
      parseFloat(healthScore.completenessScore || 0).toFixed(1),
      { color: "#3b82f6", unit: "%" },
    );
    currentX += card2Width + cardSpacing;

    // Quality
    this.pdf.addKPICard(
      "Qualit\u00e9",
      parseFloat(healthScore.qualityScore || 0).toFixed(1),
      { color: "#10b981", unit: "%" },
    );

    this.pdf.addSpace(15);

    // Health grade badge
    if (healthScore.healthGrade) {
      this.pdf.addParagraph("Note de Sant\u00e9: ", {
        fontStyle: "bold",
        fontSize: 12,
      });
      this.pdf.addStatusBadge(
        `Grade ${healthScore.healthGrade}`,
        this.getGradeStatus(healthScore.healthGrade),
      );
    }
  }

  /**
   * Generate detailed workshop analysis
   */
  async generateWorkshopAnalysis() {
    this.pdf.addSectionHeading("Analyse D\u00e9taill\u00e9e des Ateliers", 1);

    const workshops = this.reportData.workshops || {};

    // Workshop 1 Analysis
    if (workshops.workshop1) {
      await this.generateWorkshop1Analysis(workshops.workshop1);
    }

    // Workshop 2 Analysis
    if (workshops.workshop2) {
      await this.generateWorkshop2Analysis(workshops.workshop2);
    }

    // Workshop 3 Analysis
    if (workshops.workshop3) {
      await this.generateWorkshop3Analysis(workshops.workshop3);
    }

    // Workshop 4 Analysis
    if (workshops.workshop4) {
      await this.generateWorkshop4Analysis(workshops.workshop4);
    }

    // Workshop 5 Analysis
    if (workshops.workshop5) {
      await this.generateWorkshop5Analysis(workshops.workshop5);
    }
  }

  /**
   * Generate Workshop 1 analysis
   */
  async generateWorkshop1Analysis(workshop1Data) {
    this.pdf.addSectionHeading("Atelier 1 : Cadrage et Contexte", 2);

    // Status badge
    this.pdf.addStatusBadge(
      workshop1Data.completionStatus || "Non d\u00e9marr\u00e9",
      this.getCompletionStatus(workshop1Data.completionStatus),
    );
    this.pdf.addSpace(5);

    // Business values analysis
    if (workshop1Data.businessValues?.length > 0) {
      this.pdf.addSectionHeading("Valeurs M\u00e9tier Identifi\u00e9es", 3);
      this.pdf.addParagraph(
        `${workshop1Data.businessValues.length} valeurs m\u00e9tier ont \u00e9t\u00e9 identifi\u00e9es et analys\u00e9es.`,
      );

      // Business values table
      const headers = [
        "Valeur M\u00e9tier",
        "Niveau de Criticit\u00e9",
        "Description",
      ];
      const rows = workshop1Data.businessValues
        .slice(0, 10)
        .map((bv) => [
          bv.name || "Non sp\u00e9cifi\u00e9",
          bv.criticalityLevel || "Non d\u00e9fini",
          (bv.description || "Aucune description").substring(0, 50) + "...",
        ]);

      this.pdf.addTable(headers, rows, { fontSize: 8 });

      // Add business value chart if available
      if (this.chartData?.businessValue?.criticalityDistributionChart) {
        await this.chartIntegration.addRiskDistributionChart(
          this.chartData.businessValue.criticalityDistributionChart,
          { width: 80, height: 60 },
        );
      }
    }

    // Dreaded events analysis
    if (workshop1Data.dreadedEvents?.length > 0) {
      this.pdf.addSectionHeading("\u00c9v\u00e9nements Redout\u00e9s", 3);
      this.pdf.addParagraph(
        `${workshop1Data.dreadedEvents.length} \u00e9v\u00e9nements redout\u00e9s ont \u00e9t\u00e9 identifi\u00e9s.`,
      );

      const headers = [
        "\u00c9v\u00e9nement Redout\u00e9",
        "S\u00e9v\u00e9rit\u00e9",
        "Valeur M\u00e9tier Impact\u00e9e",
      ];
      const rows = workshop1Data.dreadedEvents
        .slice(0, 10)
        .map((de) => [
          de.name || "Non sp\u00e9cifi\u00e9",
          de.severity || "Non d\u00e9finie",
          de.businessValueId || "Non li\u00e9e",
        ]);

      this.pdf.addTable(headers, rows, { fontSize: 8 });
    }

    // AI insights for Workshop 1
    if (this.aiInsights?.patterns?.correlations) {
      this.pdf.addSectionHeading(
        "Analyse IA - Corr\u00e9lations Identifi\u00e9es",
        3,
      );
      const correlations = this.aiInsights.patterns.correlations.filter((c) =>
        c.elements.some(
          (e) => e.includes("Valeur") || e.includes("\u00c9v\u00e9nement"),
        ),
      );

      if (correlations.length > 0) {
        correlations.forEach((correlation) => {
          this.pdf.addInfoBox(correlation.type, correlation.description, {
            type: "info",
          });
        });
      }
    }
  }

  /**
   * Generate Workshop 2 analysis
   */
  async generateWorkshop2Analysis(workshop2Data) {
    this.pdf.addSectionHeading("Atelier 2 - Sources de Risque", 2);

    this.pdf.addStatusBadge(
      workshop2Data.completionStatus || "Non d\u00e9marr\u00e9",
      this.getCompletionStatus(workshop2Data.completionStatus),
    );
    this.pdf.addSpace(5);

    // Risk sources analysis
    if (workshop2Data.riskSources?.length > 0) {
      this.pdf.addSectionHeading("Sources de Risque Identifi\u00e9es", 3);
      this.pdf.addParagraph(
        `${workshop2Data.riskSources.length} sources de risque ont \u00e9t\u00e9 analys\u00e9es.`,
      );

      const headers = [
        "Source de Risque",
        "Cat\u00e9gorie",
        "Niveau de Menace",
      ];
      const rows = workshop2Data.riskSources
        .slice(0, 10)
        .map((rs) => [
          rs.name || "Non sp\u00e9cifi\u00e9e",
          rs.category || "Non d\u00e9finie",
          rs.threatLevel || "Non \u00e9valu\u00e9",
        ]);

      this.pdf.addTable(headers, rows, { fontSize: 8 });

      // Add threat sources chart if available
      if (this.chartData?.threatLandscape?.threatSourcesChart) {
        await this.chartIntegration.addBarChart(
          this.chartData.threatLandscape.threatSourcesChart,
          { width: 120, height: 70 },
        );
      }
    }

    // Stakeholders analysis
    if (workshop2Data.stakeholders?.length > 0) {
      this.pdf.addSectionHeading(
        "Parties Prenantes de l'\u00c9cosyst\u00e8me",
        3,
      );
      this.pdf.addParagraph(
        `${workshop2Data.stakeholders.length} parties prenantes ont \u00e9t\u00e9 identifi\u00e9es.`,
      );

      const headers = [
        "Partie Prenante",
        "Cat\u00e9gorie",
        "Niveau de Confiance",
        "Niveau de Menace",
      ];
      const rows = workshop2Data.stakeholders
        .slice(0, 10)
        .map((sh) => [
          sh.name || "Non sp\u00e9cifi\u00e9e",
          sh.category || "Non d\u00e9finie",
          sh.trustLevel?.toString() || "N/A",
          sh.threatLevel?.toString() || "N/A",
        ]);

      this.pdf.addTable(headers, rows, { fontSize: 8 });

      // Add stakeholder threat chart if available
      if (this.chartData?.threatLandscape?.stakeholderThreatChart) {
        await this.chartIntegration.addScatterPlot(
          this.chartData.threatLandscape.stakeholderThreatChart,
          { width: 100, height: 80 },
        );
      }
    }

    // Add Workshop 2 radar visualization if available
    this.pdf.addSpace(10);
    await this.addRadarVisualization();
  }

  /**
   * Generate Workshop 3 analysis
   */
  async generateWorkshop3Analysis(workshop3Data) {
    this.pdf.addSectionHeading(
      "Atelier 3 - Sc\u00e9narios Strat\u00e9giques",
      2,
    );

    this.pdf.addStatusBadge(
      workshop3Data.completionStatus || "Non d\u00e9marr\u00e9",
      this.getCompletionStatus(workshop3Data.completionStatus),
    );
    this.pdf.addSpace(5);

    if (workshop3Data.strategicScenarios?.length > 0) {
      this.pdf.addSectionHeading("Sc\u00e9narios Strat\u00e9giques", 3);
      this.pdf.addParagraph(
        `${workshop3Data.strategicScenarios.length} sc\u00e9narios strat\u00e9giques ont \u00e9t\u00e9 d\u00e9velopp\u00e9s.`,
      );

      const headers = ["Sc\u00e9nario", "Gravit\u00e9", "Description"];
      const rows = workshop3Data.strategicScenarios
        .slice(0, 8)
        .map((ss) => [
          ss.name || "Non sp\u00e9cifi\u00e9",
          ss.gravity?.toString() || ss.severity || "Non \u00e9valu\u00e9e",
          (ss.description || "Aucune description").substring(0, 60) + "...",
        ]);

      this.pdf.addTable(headers, rows, { fontSize: 8 });
    }

    if (workshop3Data.attackPaths?.length > 0) {
      this.pdf.addSectionHeading("Chemins d'Attaque", 3);
      this.pdf.addParagraph(
        `${workshop3Data.attackPaths.length} chemins d'attaque ont \u00e9t\u00e9 mod\u00e9lis\u00e9s.`,
      );
    }

    // Add stakeholder radar visualization if available
    this.pdf.addSpace(10);
    await this.addStakeholderRadarVisualization();
  }

  /**
   * Generate Workshop 4 analysis
   */
  async generateWorkshop4Analysis(workshop4Data) {
    this.pdf.addSectionHeading(
      "Atelier 4 - Sc\u00e9narios Op\u00e9rationnels",
      2,
    );

    this.pdf.addStatusBadge(
      workshop4Data.completionStatus || "Non d\u00e9marr\u00e9",
      this.getCompletionStatus(workshop4Data.completionStatus),
    );
    this.pdf.addSpace(5);

    if (workshop4Data.operationalScenarios?.length > 0) {
      this.pdf.addSectionHeading("Sc\u00e9narios Op\u00e9rationnels", 3);
      this.pdf.addParagraph(
        `${workshop4Data.operationalScenarios.length} sc\u00e9narios op\u00e9rationnels ont \u00e9t\u00e9 d\u00e9taill\u00e9s.`,
      );

      const headers = [
        "Sc\u00e9nario",
        "S\u00e9v\u00e9rit\u00e9",
        "Vraisemblance",
        "Score de Risque",
      ];
      const rows = workshop4Data.operationalScenarios.slice(0, 8).map((os) => {
        const severity = this.mapSeverityToNumeric(os.severity);
        const likelihood = this.mapSeverityToNumeric(os.likelihood);
        const riskScore = severity * likelihood;

        return [
          os.name || "Non sp\u00e9cifi\u00e9",
          os.severity || "Non \u00e9valu\u00e9e",
          os.likelihood || "Non \u00e9valu\u00e9e",
          riskScore.toString(),
        ];
      });

      this.pdf.addTable(headers, rows, { fontSize: 8 });

      // Add risk matrix if available
      if (this.chartData?.riskDistribution?.riskMatrixScatter) {
        await this.chartIntegration.addScatterPlot(
          this.chartData.riskDistribution.riskMatrixScatter,
          { width: 100, height: 80 },
        );
      }
    }

    // Vraisemblance analysis
    if (workshop4Data.vraisemblanceAnalysis) {
      this.pdf.addSectionHeading("Analyse de Vraisemblance", 3);
      const analysis = workshop4Data.vraisemblanceAnalysis;
      this.pdf.addParagraph(
        `Vraisemblance moyenne: ${analysis.average || "N/A"}`,
      );
    }
  }

  /**
   * Generate Workshop 5 analysis
   */
  async generateWorkshop5Analysis(workshop5Data) {
    this.pdf.addSectionHeading("Atelier 5 - Traitement du Risque", 2);

    this.pdf.addStatusBadge(
      workshop5Data.completionStatus || "Non d\u00e9marr\u00e9",
      this.getCompletionStatus(workshop5Data.completionStatus),
    );
    this.pdf.addSpace(5);

    if (workshop5Data.riskTreatmentPlans?.length > 0) {
      this.pdf.addSectionHeading("Plans de Traitement des Risques", 3);
      this.pdf.addParagraph(
        `${workshop5Data.riskTreatmentPlans.length} plans de traitement ont \u00e9t\u00e9 d\u00e9finis.`,
      );

      // Add control effectiveness chart if available
      if (this.chartData?.controlEffectiveness?.implementationBarChart) {
        await this.chartIntegration.addBarChart(
          this.chartData.controlEffectiveness.implementationBarChart,
          { width: 120, height: 70 },
        );
      }
    }

    if (workshop5Data.residualRisks?.length > 0) {
      this.pdf.addSectionHeading("Risques R\u00e9siduels", 3);
      this.pdf.addParagraph(
        `${workshop5Data.residualRisks.length} risques r\u00e9siduels ont \u00e9t\u00e9 identifi\u00e9s.`,
      );

      const headers = [
        "Contr\u00f4le",
        "D\u00e9cision",
        "Niveau de Risque R\u00e9siduel",
      ];
      const rows = workshop5Data.residualRisks
        .slice(0, 8)
        .map((rr) => [
          rr.controlName || "Non sp\u00e9cifi\u00e9",
          rr.decision || "Non d\u00e9finie",
          rr.residualRiskLevel || "Non \u00e9valu\u00e9",
        ]);

      this.pdf.addTable(headers, rows, { fontSize: 8 });
    }

    // Workshop 2 radar is now included in Workshop 2 analysis section
  }

  /**
   * Add stakeholder radar visualization from Workshop 3 to the report
   * @param {boolean} skipHeading - Skip adding section heading (for when called from Workshop 5)
   */
  async addStakeholderRadarVisualization(skipHeading = false) {
    if (!this.analysisId) {
      console.warn(
        "⚠️ No analysisId available for stakeholder radar visualization retrieval",
      );
      return;
    }

    try {
      console.log(
        "📸 Attempting to retrieve saved stakeholder radar visualization from database...",
      );
      console.log("🔍 Using analysisId:", this.analysisId);

      // Import the service dynamically to avoid circular dependencies
      const stakeholderService = await import("../services/stakeholderService");
      const service = stakeholderService.default || stakeholderService;

      if (service && service.getStakeholderRadarImage) {
        console.log("🚀 Calling getStakeholderRadarImage...");
        const response = await service.getStakeholderRadarImage(
          this.analysisId,
        );

        if (
          response &&
          response.success &&
          response.data &&
          response.data.imageData
        ) {
          const imageDataUrl = response.data.imageData;
          console.log(
            "✅ Stakeholder radar visualization retrieved from database successfully",
          );
          console.log("📊 Image data length:", imageDataUrl.length);
          console.log("📊 Image data prefix:", imageDataUrl.substring(0, 100));

          // Add section heading only if not skipped
          if (!skipHeading) {
            const L = this.L.viz;
            this.pdf.addSectionHeading(L.stakeholderTitle, 3);
            this.pdf.addParagraph(L.stakeholderDesc);
          }

          console.log(
            "🎯 About to call addImageFromDataUrl for stakeholder radar...",
          );

          // Add the image to the PDF
          try {
            await this.pdf.addImageFromDataUrl(imageDataUrl, {
              width: 160,
              height: 120,
              align: "center",
              caption: this.L.viz.stakeholderCaption,
            });
            console.log(
              "✅ Stakeholder radar visualization added to PDF report successfully",
            );
          } catch (imageError) {
            console.error(
              "❌ Error adding stakeholder radar image to PDF:",
              imageError,
            );
            this.pdf.addParagraph(this.L.viz.stakeholderError);
          }
        } else {
          console.log(
            "ℹ️ No saved stakeholder radar visualization found in database",
          );
        }
      } else {
        console.error("❌ Stakeholder service or method not available");
      }
    } catch (error) {
      console.error(
        "❌ Error retrieving stakeholder radar visualization:",
        error,
      );
    }
  }

  /**
   * Add radar visualization from Workshop 2 to the report
   * @param {boolean} skipHeading - Skip adding section heading (for when called from Workshop 5)
   */
  async addRadarVisualization(skipHeading = false) {
    if (!this.analysisId) {
      console.warn(
        "⚠️ No analysisId available for radar visualization retrieval",
      );
      console.warn("📋 Available data:", {
        hasOptions: !!this.options,
        optionsAnalysisId: this.options?.analysisId,
        reportDataAnalysisId: this.reportData?.metadata?.analysisId,
      });
      return;
    }

    try {
      console.log(
        "📸 Attempting to retrieve saved radar visualization from database...",
      );
      console.log("🔍 Using analysisId:", this.analysisId);

      // Import the service dynamically to avoid circular dependencies
      const atelier2Service = await import("../services/atelier2Service");
      const service = atelier2Service.default || atelier2Service;

      console.log("📦 Service imported:", {
        hasService: !!service,
        hasMethod: !!(service && service.getRadarVisualization),
        serviceType: typeof service,
        methodType: typeof service?.getRadarVisualization,
      });

      if (service && service.getRadarVisualization) {
        console.log("🚀 Calling getRadarVisualization...");
        const imageDataUrl = await service.getRadarVisualization(
          this.analysisId,
        );

        console.log("📊 Service response:", {
          hasImageData: !!imageDataUrl,
          imageDataType: typeof imageDataUrl,
          imageDataLength: imageDataUrl ? imageDataUrl.length : 0,
          imageDataPreview: imageDataUrl
            ? imageDataUrl.substring(0, 50) + "..."
            : "null",
        });

        if (imageDataUrl) {
          console.log(
            "✅ Radar visualization retrieved from database successfully",
          );

          // Add section heading only if not skipped
          if (!skipHeading) {
            const L = this.L.viz;
            this.pdf.addSectionHeading(L.riskTitle, 3);
            this.pdf.addParagraph(L.riskDesc);
          }

          // Add the image to the PDF
          await this.pdf.addImageFromDataUrl(imageDataUrl, {
            width: 200,
            height: 150,
            align: "center",
            caption: this.L.viz.riskCaption,
          });

          console.log("✅ Radar visualization added to PDF report");
        } else {
          console.log("ℹ️ No saved radar visualization found in database");
        }
      } else {
        console.error("❌ Service or method not available");
      }
    } catch (error) {
      console.error("❌ Error retrieving radar visualization:", error);
      console.error("📋 Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack?.split("\n").slice(0, 3),
      });
    }
  }

  /**
   * Generate 2. CADRAGE ET CONTEXTE (Atelier 1)
   */
  async generateAtelier1Cadrage() {
    const L = this.L.ch2;
    const H = this.L.hlp;
    this.pdf.addSectionHeading(L.heading, 1);
    this.pdf.addSectionIntro(L.introTitle, L.introText);

    const workshop1Data = this.reportData.workshops?.workshop1 || {};
    const metadata = this.reportData.metadata || {};

    // 2.1 Fiche d'identit\u00e9
    this.pdf.addSectionHeading(L.s21, 2);

    const identityHeaders = L.identityHeaders;
    const identityRows = [
      [L.rOrganisation, metadata.companyName || L.notSpecifiedF],
      [L.rPerimetre, workshop1Data.scope || L.notDefinedM],
      [
        L.rPeriode,
        metadata.startDate && metadata.endDate
          ? `${metadata.startDate} - ${metadata.endDate}`
          : L.notDefinedF,
      ],
      [L.rVersion, metadata.version || "1.0"],
      [
        L.rReference,
        metadata.reference ||
          `REF-${this.analysisId?.substring(0, 8) || "####"}`,
      ],
    ];

    this.pdf.addTable(identityHeaders, identityRows, {
      columnWidths: [40, 120],
      fontSize: 10,
    });

    // 2.2 Missions Principales
    this.pdf.addSpace(5);
    this.pdf.addSectionHeading(L.s22, 2);

    const missions = workshop1Data.missions || metadata.missions || [];

    if (missions && missions.length > 0) {
      // Check if missions are strings or objects
      const missionTexts = missions.map((m) =>
        typeof m === "string"
          ? m
          : m.description || m.name || L.missionNoDesc,
      );
      missionTexts.forEach((m) => this.pdf.addParagraph(`• ${m}`));
    } else if (metadata.description) {
      this.pdf.addParagraph(metadata.description);
    } else {
      this.pdf.addParagraph(L.noneDocumented);
    }

    // 2.3 Contexte M\u00e9tier & Enjeux (Business Values)
    this.pdf.addSpace(5);
    this.pdf.addSectionHeading(L.s23, 2);

    if (workshop1Data.businessValues?.length > 0) {
      this.pdf.addParagraph(
        L.bvCountIntro.replace(
          "{n}",
          workshop1Data.businessValues.length,
        ),
      );
      this.pdf.addSpace(3);

      const bvHeaders = L.bvHeaders;
      const bvRows = workshop1Data.businessValues.map((bv) => {
        const deCount = (workshop1Data.dreadedEvents || []).filter(
          (de) => de.businessValueId === bv.id,
        ).length;
        const criticality = this.pdf.formatCriticality
          ? this.pdf.formatCriticality(bv.criticality)
          : bv.criticality || L.notDefinedF;

        return [
          bv.name || L.unnamed,
          (bv.description || "").substring(0, 120) +
            (bv.description?.length > 120 ? "..." : ""),
          criticality,
          deCount.toString(),
        ];
      });

      this.pdf.addTable(bvHeaders, bvRows, {
        fontSize: 9,
        columnWidths: [40, 80, 20, 20],
      });
    } else {
      this.pdf.addParagraph(L.noneBv);
    }

    // 2.4 Socle de S\u00e9curit\u00e9 (Security Baseline)
    this.pdf.addSpace(5);
    this.pdf.addSectionHeading(L.s24, 2);

    // Add summary dashboard if available
    const securityBaseline = workshop1Data.securityBaseline || {};
    const standards = securityBaseline.standards || [];

    if (standards.length > 0) {
      this.pdf.addParagraph(
        L.complianceIntro.replace("{n}", standards.length),
      );
      this.pdf.addSpace(2);

      const stdHeaders = L.stdHeaders;
      const stdRows = standards.map((std) => [
        std.name || std.id || L.unknown,
        std.progress ? `${Math.round(std.progress)}%` : "0%",
        std.status || L.inProgress,
      ]);

      this.pdf.addTable(stdHeaders, stdRows, {
        fontSize: 9,
        columnWidths: [100, 30, 30],
      });
    } else {
      this.pdf.addParagraph(L.noStandards);
    }

    // Fallback to detailed table if relevant info exists
    if (workshop1Data.dreadedEvents?.length > 0) {
      this.pdf.addSectionHeading(L.deSynthesis, 3);
      const deHeaders = L.deHeaders;
      const deRows = workshop1Data.dreadedEvents
        .slice(0, 15)
        .map((de) => [
          de.name || H.unspecified,
          de.severity || L.notDefinedF,
          (de.impacts || []).join(", ") || L.impactsUndefined,
        ]);
      this.pdf.addTable(deHeaders, deRows, { fontSize: 8 });
    }
  }

  /**
   * Generate 2. SOURCES DE RISQUE ET OBJECTIFS VIS\u00c9S section
   */
  async generateChapitre2() {
    const L = this.L.ch3;
    const H = this.L.hlp;
    this.pdf.addSectionHeading(L.heading, 1);
    this.pdf.addSectionIntro(L.introTitle, L.introText);

    const workshops = this.reportData.workshops || {};
    const workshop2Data = workshops.workshop2 || {};
    const riskSources = workshop2Data.riskSources || [];
    const couplesSROV = workshop2Data.mappings || [];
    const dreadedEvents = workshops.workshop1?.dreadedEvents || [];

    // 3.1. SOURCES DE RISQUE & OBJECTIFS VIS\u00c9S
    this.pdf.addSectionHeading(L.s31, 2);
    if (riskSources.length > 0) {
      // Create table for risk sources
      const riskSourcesHeaders = L.rsHeaders;
      const riskSourcesRows = riskSources.map((rs, index) => [
        (index + 1).toString(),
        rs.name || H.unspecified,
        this.formatRiskSourceCategory(rs.category) || "-",
        rs.objectifVise || "-",
        this.formatLevel(rs.motivation) || "-",
        this.formatLevel(rs.activite) || "-",
        this.formatResources(rs.ressources) || "-",
        rs.selected ? L.selected : L.notSelected,
      ]);

      this.pdf.addTable(riskSourcesHeaders, riskSourcesRows, {
        fontSize: 9,
        columnWidths: [8, 22, 18, 22, 28, 28, 28, 26], // Total 160mm - Keep Motivation/Activit\u00e9/Ressources large
      });

      // Add additional details for selected sources
      const selectedSources = riskSources.filter((rs) => rs.selected);
      if (selectedSources.length > 0) {
        this.pdf.addSpace(2);
        this.pdf.addParagraph(L.detailsIntro);
        selectedSources.forEach((rs, index) => {
          // Source identification and basic information
          this.pdf.addParagraph(
            `• ${index + 1}. ${rs.name || L.unnamedSource}`,
          );

          if (rs.description) {
            this.pdf.addParagraph(`${L.fldDescription} ${rs.description}`);
          }

          if (rs.category || rs.type) {
            this.pdf.addParagraph(
              `${L.fldCategory} ${rs.category || rs.type}`,
            );
          }

          if (rs.actor) {
            this.pdf.addParagraph(`${L.fldActor} ${rs.actor}`);
          }

          if (rs.motivation) {
            this.pdf.addParagraph(`${L.fldMotivation} ${rs.motivation}`);
          }

          if (rs.capabilities && rs.capabilities.length > 0) {
            this.pdf.addParagraph(
              `${L.fldCapabilities} ${rs.capabilities.join(", ")}`,
            );
          }

          if (rs.impact) {
            this.pdf.addParagraph(`${L.fldImpact} ${rs.impact}`);
          }

          if (rs.geographicScope) {
            this.pdf.addParagraph(
              `${L.fldGeo} ${rs.geographicScope}`,
            );
          }

          if (rs.timeline) {
            this.pdf.addParagraph(`${L.fldTimeline} ${rs.timeline}`);
          }

          if (rs.justification) {
            this.pdf.addParagraph(
              `${L.fldJustification} ${rs.justification}`,
            );
          }

          // Add risk level if available
          if (rs.riskLevel || rs.level) {
            this.pdf.addParagraph(
              `${L.fldRiskLevel} ${rs.riskLevel || rs.level}`,
            );
          }

          // Add any associated objectives
          if (rs.targets && rs.targets.length > 0) {
            this.pdf.addParagraph(
              `${L.fldTargets} ${rs.targets.join(", ")}`,
            );
          }

          this.pdf.addSpace(1);
        });
      }
    } else {
      this.pdf.addParagraph(L.noneSources);
    }

    // 3.2. SOURCES DE RISQUE PAR CAT\u00c9GORIE
    this.pdf.addSectionHeading(L.s32, 2);
    this.generateRiskSourcesByCategory(riskSources);

    // 3.3. \u00c9VALUATION DES COUPLES DE SOURCES DE RISQUES ET OBJECTIFS VIS\u00c9S
    this.pdf.addSectionHeading(L.s33, 2);
    if (couplesSROV.length > 0) {
      // Enhanced table with niveau and justification
      const couplesHeaders = L.coupleHeaders;
      const couplesRows = couplesSROV.map((couple, index) => {
        const source = riskSources.find((s) => s.id === couple.sourceId);
        const sourceName = source ? source.name : L.unknownSource;
        const dreadedEventName = this.getDreadedEventName(
          couple.dreadedEventId,
          dreadedEvents,
        );

        // Get objective vis\u00e9 from the source (matching 3.1 logic)
        const targetedObjective =
          source?.objectifVise || source?.objective || L.notSpecifiedM;
        const status = source?.selected ? L.retainedM : L.notRetainedM;

        // Calculate risk level (niveau) based on motivation, activity, resources
        const niveau = this.calculateCoupleNiveau(source, couple);

        // Get justification
        const justification =
          couple.justification || source?.justification || "-";

        return [
          (index + 1).toString(),
          sourceName,
          targetedObjective,
          dreadedEventName,
          niveau.toString(),
          status,
          justification.length > 40
            ? justification.substring(0, 40) + "..."
            : justification,
        ];
      });

      this.pdf.addTable(couplesHeaders, couplesRows, {
        fontSize: 8,
        columnWidths: [8, 28, 28, 30, 12, 18, 36],
      });
    } else {
      this.pdf.addParagraph(L.noneCouples);
    }

    // 3.4. ASSOCIATIONS SOURCES DE RISQUE / \u00c9V\u00c9NEMENTS REDOUT\u00c9S (SR/ER)
    this.pdf.addSectionHeading(L.s34, 2);
    this.generateSRERMappingsSection(riskSources, dreadedEvents, couplesSROV);

    // Add Workshop 2 radar visualization if available
    this.pdf.addSpace(10);
    await this.addRadarVisualization();
  }

  /**
   * Generate Risk Sources grouped by category
   * @param {Array} riskSources - Array of risk sources
   */
  generateRiskSourcesByCategory(riskSources) {
    const L = this.L.ch3;
    if (riskSources.length === 0) {
      this.pdf.addParagraph(L.noneToCategorize);
      return;
    }

    // Group sources by category
    const sourcesByCategory = {};
    riskSources.forEach((source) => {
      const category = source.category || "non-categorise";
      if (!sourcesByCategory[category]) {
        sourcesByCategory[category] = [];
      }
      sourcesByCategory[category].push(source);
    });

    // Category labels (bilingual)
    const categoryLabels = this.L.cats;

    // Create summary table
    const categoryHeaders = L.catHeaders;
    const categoryRows = Object.entries(sourcesByCategory).map(
      ([category, sources]) => {
        const retained = sources.filter((s) => s.selected).length;
        const threats = sources
          .slice(0, 2)
          .map((s) => s.name)
          .join(", ");

        return [
          categoryLabels[category] || category,
          sources.length.toString(),
          retained.toString(),
          threats + (sources.length > 2 ? "..." : ""),
        ];
      },
    );

    this.pdf.addTable(categoryHeaders, categoryRows, {
      fontSize: 8,
      columnWidths: [40, 30, 30, 60],
    });
  }

  /**
   * Generate SR/ER Mappings section
   * @param {Array} riskSources - Array of risk sources
   * @param {Array} dreadedEvents - Array of dreaded events
   * @param {Array} mappings - Array of SR/OV mappings
   */
  generateSRERMappingsSection(riskSources, dreadedEvents, mappings) {
    const L = this.L.ch3;
    if (mappings.length === 0) {
      this.pdf.addParagraph(L.srerNone);
      return;
    }

    this.pdf.addParagraph(
      L.srerCountIntro.replace("{n}", mappings.length),
    );

    const srerHeaders = L.srerHeaders;
    const srerRows = mappings.map((mapping) => {
      const source = riskSources.find((s) => s.id === mapping.sourceId);
      const event = dreadedEvents.find(
        (e) => String(e.id) === String(mapping.dreadedEventId),
      );

      // Format capacities
      const capacities = [];
      if (source?.motivation)
        capacities.push(`M:${this.formatLevel(source.motivation)}`);
      if (source?.activite)
        capacities.push(`A:${this.formatLevel(source.activite)}`);
      if (source?.ressources)
        capacities.push(`R:${this.formatResources(source.ressources)}`);

      return [
        source?.name || L.unknownF,
        (source?.objectifVise || "-").substring(0, 20) +
          ((source?.objectifVise || "").length > 20 ? "..." : ""),
        event?.name || L.unknownEvent,
        this.formatSecurityPillar(event?.securityPillar) || "-",
        this.formatSeverity(event?.severity) || "-",
        capacities.join(" ") || "-",
      ];
    });

    this.pdf.addTable(srerHeaders, srerRows, {
      fontSize: 7,
      columnWidths: [30, 25, 35, 20, 25, 25],
    });

    // Add impacts summary if available
    const eventsWithImpacts = dreadedEvents.filter(
      (e) =>
        mappings.some((m) => m.dreadedEventId === e.id) &&
        e.impacts &&
        e.impacts.length > 0,
    );

    if (eventsWithImpacts.length > 0) {
      this.pdf.addSpace(5);
      this.pdf.addParagraph(L.impactsIntro);

      const impactSummary = eventsWithImpacts
        .slice(0, 5)
        .map((event) => {
          const impactList = event.impacts
            .map((i) => (typeof i === "string" ? i : i.impact || String(i)))
            .slice(0, 3)
            .join(", ");
          return `• ${event.name}: ${impactList}`;
        })
        .join("\n");

      this.pdf.addParagraph(impactSummary);
    }
  }

  /**
   * Calculate risk level (niveau) for a couple SR/OV
   * @param {Object} source - Risk source
   * @param {Object} couple - Couple mapping
   * @returns {number} Risk level 1-4
   */
  calculateCoupleNiveau(source, couple) {
    if (!source) return 1;

    // Get level values (1-4)
    const getLevelValue = (level) => {
      const levelMap = {
        negligeable: 1,
        faible: 2,
        significatif: 3,
        important: 4,
        negligible: 1,
        low: 2,
        significant: 3,
        high: 4,
        1: 1,
        2: 2,
        3: 3,
        4: 4,
      };
      return levelMap[level] || levelMap[String(level)?.toLowerCase()] || 2;
    };

    const motivation = getLevelValue(source.motivation || couple.motivation);
    const activite = getLevelValue(source.activite || couple.activite);
    const ressources = getLevelValue(source.ressources || couple.ressources);

    // Average of the three, rounded
    return Math.round((motivation + activite + ressources) / 3);
  }

  /**
   * Calculate threat level for a stakeholder
   * Formula: (dependency × penetration) / (cyberMaturity × trust)
   * @param {Object} stakeholder - The stakeholder object
   * @returns {number} Calculated threat level
   */
  calculateStakeholderThreatLevel(stakeholder) {
    if (!stakeholder) return 0;

    const dependency = Number(stakeholder.dependency) || 1;
    const penetration = Number(stakeholder.penetration) || 1;
    const cyberMaturity = Number(stakeholder.cyberMaturity) || 1;
    const trust =
      Number(stakeholder.trustLevel) || Number(stakeholder.trust) || 1;

    // Ensure we don't divide by zero
    const denominator = cyberMaturity * trust;
    if (denominator === 0) return 0;

    return (dependency * penetration) / denominator;
  }

  /**
   * Get French translation for stakeholder category
   * @param {string} category - The category in English
   * @returns {string} French translation
   */
  getStakeholderCategoryInFrench(category) {
    if (!category) return "-";

    const categoryMap = this.L.hlp.stakeholderCategories;

    return categoryMap[category.toLowerCase()] || category;
  }

  /**
   * Generate 3. SC\u00c9NARIOS STRAT\u00c9GIQUES section
   */
  async generateChapitre3() {
    const L = this.L.ch4;
    const H = this.L.hlp;
    this.pdf.addSectionHeading(L.heading, 1);
    this.pdf.addSectionIntro(L.introTitle, L.introText);

    const workshops = this.reportData.workshops || {};
    const workshop3Data = workshops.workshop3 || {};
    const strategicScenarios = workshop3Data.strategicScenarios || [];
    const stakeholders = workshop3Data.stakeholders || [];
    const ecosystemMeasures = workshop3Data.ecosystemMeasures || [];

    // 4.1. \u00c9LABORATION DE LA CARTOGRAPHIE DE LA MENACE NUM\u00c9RIQUE ET S\u00c9LECTION DES PARTIES PRENANTES CRITIQUES
    this.pdf.addSectionHeading(L.s41, 2);

    this.pdf.addSectionHeading(L.s411, 3);
    if (stakeholders.length > 0) {
      // Create table for stakeholder descriptions
      const descriptionHeaders = L.descHeaders;
      const descriptionRows = stakeholders.map((sh, index) => [
        (index + 1).toString(),
        sh.name || "Non sp\u00e9cifi\u00e9",
        this.getStakeholderCategoryInFrench(sh.category) || "-",
        sh.type || sh.rank || "-",
        (sh.description || "-").substring(0, 100) +
          (sh.description && sh.description.length > 100 ? "..." : ""),
      ]);

      this.pdf.addTable(descriptionHeaders, descriptionRows, {
        fontSize: 8,
        columnWidths: [15, 35, 30, 25, 55], // Total 160mm - Reduced by 10mm distributed
      });
    } else {
      this.pdf.addParagraph(L.noneDescribed);
    }

    this.pdf.addSectionHeading(L.s412, 3);
    if (stakeholders.length > 0) {
      // Create table for stakeholder evaluations
      const evaluationHeaders = L.evalHeaders;
      const evaluationRows = stakeholders.map((sh) => {
        // Calculate threat level using the formula: (dependency × penetration) / (cyberMaturity × trust)
        const calculatedThreat = this.calculateStakeholderThreatLevel(sh);
        const threatDisplay =
          calculatedThreat > 0
            ? calculatedThreat.toFixed(2)
            : this.getDefaultThreatLevel(sh);

        // Get trust level using the same logic as threat calculation
        const trustValue = Number(sh.trustLevel) || Number(sh.trust) || null;
        const trustDisplay =
          trustValue !== null
            ? this.formatNumericLevel(trustValue)
            : this.getDefaultTrustLevel(sh);

        return [
          sh.name || H.unspecified,
          threatDisplay,
          this.formatNumericLevel(sh.dependency) ||
            this.getDefaultNumericLevel(sh.dependency),
          this.formatNumericLevel(sh.penetration) ||
            this.getDefaultNumericLevel(sh.penetration),
          this.formatNumericLevel(sh.cyberMaturity) ||
            this.getDefaultNumericLevel(sh.cyberMaturity),
          trustDisplay,
          sh.retained ? L.oui : L.non,
        ];
      });

      this.pdf.addTable(evaluationHeaders, evaluationRows, {
        fontSize: 8,
        columnWidths: [30, 18, 25, 25, 23, 23, 20], // Total 160mm - Added 5mm distributed
      });
    } else {
      this.pdf.addParagraph(L.noneEval);
    }

    // Add stakeholder radar visualization after 4.1.2. \u00c9VALUATION DES PARTIES PRENANTES
    this.pdf.addSpace(10);
    await this.addStakeholderRadarVisualization();

    this.pdf.addSectionHeading(L.s413, 3);

    // Generate comprehensive threat cartography analysis for PDF synchronously
    await this.generateInitialThreatCartographyForPDF(
      stakeholders,
      ecosystemMeasures,
    );

    // 4.2. SC\u00c9NARIOS STRAT\u00c9GIQUES ET MESURES DE S\u00c9CURIT\u00c9 (LANDSCAPE)
    // Generate comprehensive integrated view of scenarios with their attack paths and measures
    await this.generateIntegratedScenariosAndMeasures(
      strategicScenarios,
      ecosystemMeasures,
    );

    // 4.3. SUIVI DE PROGRESSION DES \u00c9V\u00c9NEMENTS REDOUT\u00c9S
    const dreadedEvents =
      this.reportData.workshops?.workshop1?.dreadedEvents || [];
    if (dreadedEvents.length > 0) {
      this.pdf.addSectionHeading(this.L.prog.heading43, 2);
      this.generateEventProgressionSection(
        dreadedEvents,
        ecosystemMeasures,
        strategicScenarios,
      );
    }
  }

  /**
   * Generate integrated view of strategic scenarios with their attack paths
   * Uses landscape orientation for better readability
   * @param {Array} scenarios - Strategic scenarios
   * @param {Array} measures - Ecosystem measures
   */
  async generateIntegratedScenariosAndMeasures(scenarios, measures) {
    const L = this.L.s42;
    const H = this.L.hlp;
    if (scenarios.length === 0) {
      this.pdf.addSectionHeading(L.s42Title, 2);
      this.pdf.addParagraph(L.s42Empty);
      return;
    }

    // Switch to landscape first, then add heading and content together
    this.pdf.addNewPage("landscape");

    // Add heading and description on the landscape page
    this.pdf.addSectionHeading(L.s42Title, 2);
    this.pdf.addParagraph(
      L.s42CountIntro.replace("{n}", scenarios.length),
    );
    this.pdf.addSpace(5);

    // Build map of measures by attack path ID (with fallback to reference)
    const measuresByAttackPath = {};
    measures.forEach((m) => {
      const pathKey = m.attackPathId || m.attackPathReference;
      if (pathKey) {
        if (!measuresByAttackPath[pathKey]) {
          measuresByAttackPath[pathKey] = [];
        }
        measuresByAttackPath[pathKey].push({
          title: m.title || L.untitled,
          category: this.formatMeasureCategory(m.category) || "-",
          priority: this.formatPriority(m.priority) || "-",
          effectiveness: m.effectiveness ? `${m.effectiveness}/5` : "-",
          status: this.formatImplementationStatus(m.implementation) || "-",
        });
      }
    });

    // Build attack paths by source risk
    const attackPathsBySource = {};
    measures.forEach((m) => {
      const pathKey = m.attackPathId || m.attackPathReference;
      if (pathKey) {
        const sourceKey =
          m.sourceRiskName || m.attackPathDetails?.sourceRiskName || "general";
        if (!attackPathsBySource[sourceKey]) {
          attackPathsBySource[sourceKey] = [];
        }
        if (!attackPathsBySource[sourceKey].includes(pathKey)) {
          attackPathsBySource[sourceKey].push(pathKey);
        }
      }
    });

    // Get all unique attack path keys
    const allAttackPaths = [
      ...new Set(
        measures
          .map((m) => m.attackPathId || m.attackPathReference)
          .filter(Boolean),
      ),
    ];

    // Headers with full measure details
    const scenarioHeaders = L.s42Headers;

    // Build rows - multiple rows per scenario (one per measure)
    const integratedRows = [];

    scenarios.forEach((scenario, scenarioIndex) => {
      const sourceRisk = scenario.sourceRiskName || "";
      let matchedPaths = attackPathsBySource[sourceRisk] || [];

      // If no match by source, distribute attack paths among scenarios
      if (matchedPaths.length === 0 && allAttackPaths.length > 0) {
        const pathsPerScenario = Math.ceil(
          allAttackPaths.length / scenarios.length,
        );
        const startIdx = scenarioIndex * pathsPerScenario;
        const endIdx = Math.min(
          startIdx + pathsPerScenario,
          allAttackPaths.length,
        );
        matchedPaths = allAttackPaths.slice(startIdx, endIdx);
      }

      // Collect all measures for this scenario's attack paths
      const scenarioMeasures = [];
      matchedPaths.forEach((pathRef) => {
        const pathMeasures = measuresByAttackPath[pathRef] || [];
        pathMeasures.forEach((m) => {
          // ensure we display the human readable ref, but track by the real key under the hood
          const displayRef = m.attackPathReference || pathRef;
          scenarioMeasures.push({ pathRef: displayRef, ...m });
        });
      });

      // Build scenario info
      const scenarioName = this.sanitizeScenarioName(
        scenario.name || H.unspecified,
      );
      const objectifVise = scenario.objectifVise || "-";
      const dreadedEvent = scenario.dreadedEventName || "-";

      // Number of rows this scenario will span
      const rowsToSpan = Math.max(1, scenarioMeasures.length);

      if (scenarioMeasures.length > 0) {
        // Add rows with rowSpan for scenario columns
        scenarioMeasures.forEach((measure, measureIndex) => {
          if (measureIndex === 0) {
            // First row - use rowSpan for scenario columns
            integratedRows.push([
              { content: (scenarioIndex + 1).toString(), rowSpan: rowsToSpan },
              { content: scenarioName, rowSpan: rowsToSpan },
              { content: sourceRisk || "-", rowSpan: rowsToSpan },
              { content: objectifVise, rowSpan: rowsToSpan },
              { content: dreadedEvent, rowSpan: rowsToSpan },
              measure.pathRef,
              measure.title,
              measure.category,
              measure.priority,
              measure.effectiveness,
              measure.status,
            ]);
          } else {
            // Subsequent rows - only measure columns (autoTable handles merged cells)
            integratedRows.push([
              measure.pathRef,
              measure.title,
              measure.category,
              measure.priority,
              measure.effectiveness,
              measure.status,
            ]);
          }
        });
      } else {
        // Scenario without measures - single row
        integratedRows.push([
          (scenarioIndex + 1).toString(),
          scenarioName,
          sourceRisk || "-",
          objectifVise,
          dreadedEvent,
          "-",
          L.noMeasure,
          "-",
          "-",
          "-",
          "-",
        ]);
      }
    });

    this.pdf.addTable(scenarioHeaders, integratedRows, {
      fontSize: 6,
      columnWidths: [8, 30, 22, 22, 28, 14, 60, 22, 16, 12, 23],
    });

    // Switch back to portrait for remaining sections
    this.pdf.addNewPage("portrait");
  }

  /**
   * Generate detailed strategic scenario information
   * @param {Array} scenarios - Strategic scenarios
   * @param {Array} stakeholders - Stakeholder list
   */
  generateStrategicScenarioDetails(scenarios, stakeholders) {
    // Create a comprehensive table for ALL scenarios
    const detailHeaders = [
      "#",
      "Sc\u00e9nario",
      "Parties Prenantes",
      "Valeur M\u00e9tier",
      "Mesures",
      "Description",
    ];

    const detailRows = scenarios.map((scenario, index) => {
      // Get stakeholder names
      const scenarioStakeholders =
        scenario.stakeholders || scenario.selectedStakeholders || [];
      let stakeholderNames = "-";
      if (scenarioStakeholders.length > 0) {
        stakeholderNames = scenarioStakeholders
          .map((shId) => {
            if (typeof shId === "string") {
              const sh = stakeholders.find((s) => s.id === shId);
              return sh ? sh.name : shId;
            }
            return shId.name || shId;
          })
          .slice(0, 3)
          .join(", ");
        if (scenarioStakeholders.length > 3) {
          stakeholderNames += ` (+${scenarioStakeholders.length - 3})`;
        }
      }

      // Get controls count
      const controls = scenario.controls || scenario.customMeasures || [];
      const controlsText =
        controls.length > 0 ? `${controls.length} mesure(s)` : "-";

      // Get description (truncated)
      const description = scenario.description
        ? scenario.description.substring(0, 60) +
          (scenario.description.length > 60 ? "..." : "")
        : "-";

      return [
        (index + 1).toString(),
        this.sanitizeScenarioName(scenario.name || "Non nomm\u00e9"),
        stakeholderNames,
        scenario.businessValueName || "-",
        controlsText,
        description,
      ];
    });

    this.pdf.addTable(detailHeaders, detailRows, {
      fontSize: 7,
      columnWidths: [8, 35, 35, 30, 20, 32],
    });
  }

  /**
   * Generate detailed ecosystem measure information
   * @param {Array} measures - Ecosystem measures
   */
  generateEcosystemMeasureDetails(measures) {
    // Create detailed table
    const detailHeaders = [
      "Mesure",
      "Responsable",
      "\u00c9ch\u00e9ance",
      "Chemins d'attaque associ\u00e9s",
    ];
    const detailRows = measures.slice(0, 10).map((measure) => {
      // Format attack paths
      const attackPaths =
        measure.attackPathsDetails || measure.attackPaths || [];
      let pathsText = "-";
      if (attackPaths.length > 0) {
        pathsText = attackPaths
          .slice(0, 2)
          .map((p) =>
            typeof p === "string"
              ? p
              : `[${p.referenceCode || "CA"}] ${p.sourceRiskName || ""}`,
          )
          .join(", ");
        if (attackPaths.length > 2) pathsText += "...";
      }

      return [
        measure.title || "Sans titre",
        measure.responsibleParty || measure.responsible || "-",
        measure.deadline
          ? new Date(measure.deadline).toLocaleDateString("fr-FR")
          : "-",
        pathsText,
      ];
    });

    this.pdf.addTable(detailHeaders, detailRows, {
      fontSize: 8,
      columnWidths: [50, 35, 25, 50],
    });

    if (measures.length > 10) {
      this.pdf.addParagraph(`... et ${measures.length - 10} autres mesures.`);
    }
  }

  /**
   * Generate event progression tracking section
   * @param {Array} dreadedEvents - Dreaded events
   * @param {Array} ecosystemMeasures - Ecosystem measures
   * @param {Array} strategicScenarios - Strategic scenarios
   */
  generateEventProgressionSection(
    dreadedEvents,
    ecosystemMeasures,
    strategicScenarios,
  ) {
    const L = this.L.prog;
    this.pdf.addParagraph(L.intro);

    // Get data from workshops
    const workshop1 = this.reportData.workshops?.workshop1 || {};
    const workshop3 = this.reportData.workshops?.workshop3 || {};

    // IMPORTANT: The analysisControls data is at the TOP LEVEL of reportData, not inside workshop1!
    // Structure: this.reportData.analysisControls = { plan: { "ruleKey_eventId": { controls: [...], dreadedEventId: "..." } } }
    const analysisControlsData = this.reportData.analysisControls || {};

    console.log("========== PDF EVENT PROGRESSION DEBUG ==========");
    console.log(
      "[PDF Debug] analysisControlsData keys:",
      Object.keys(analysisControlsData),
    );
    console.log(
      "[PDF Debug] analysisControlsData.plan keys:",
      Object.keys(analysisControlsData.plan || {}),
    );
    console.log("==================================================");

    // The dataProcessor expects: { planData: { plan: {...} } }
    // reportData.analysisControls is already { plan: {...} }
    // So we wrap it as { planData: analysisControlsData }
    const controlPlan = { planData: analysisControlsData };

    // Get attack paths from Workshop 3
    const attackPaths = workshop3.attackPaths || [];

    // Get business values data - ensure proper structure for dataProcessor
    const businessValuesData =
      workshop1.businessValues?.length > 0
        ? { businessValues: workshop1.businessValues }
        : null;

    // Log final data being passed to processor
    console.log("[PDF Report] Event Progression - Final Data:", {
      dreadedEventsCount: dreadedEvents.length,
      dreadedEventIds: dreadedEvents.slice(0, 3).map((e) => e.id),
      controlPlanPlanKeys: Object.keys(analysisControlsData.plan || {}),
      ecosystemMeasuresCount: ecosystemMeasures.length,
      attackPathsCount: attackPaths.length,
      businessValuesCount: businessValuesData?.businessValues?.length || 0,
    });

    // Use the same data processor as the UI component
    const processedData = processEventProgressionData(
      dreadedEvents,
      controlPlan,
      ecosystemMeasures,
      attackPaths,
      businessValuesData,
    );

    const { eventProgression, globalStats } = processedData;

    // Log processed results
    console.log("[PDF Report] Processed Event Progression:", {
      totalEvents: globalStats.totalEvents,
      enrichedEvents: globalStats.enrichedEvents,
      partiallyTreatedEvents: globalStats.partiallyTreatedEvents,
      notTreatedEvents: globalStats.notTreatedEvents,
      totalA1Measures: globalStats.totalA1Measures,
      sampleEvent: eventProgression[0]
        ? {
            name: eventProgression[0].name,
            a1Count: eventProgression[0].a1Measures.length,
            a3Count: eventProgression[0].a3Measures.length,
            status: eventProgression[0].status,
          }
        : null,
    });

    // Create progression table
    const progressionHeaders = L.progHeaders;
    const progressionRows = eventProgression.map((event) => [
      event.name || L.undefinedEvent,
      event.gravity || "-",
      event.a1Measures.length.toString(),
      event.a3Measures.length.toString(),
      `${event.progressionRate}%`,
      event.status === "enriched"
        ? L.enriched
        : event.status === "partially-treated"
          ? L.partial
          : L.notTreated,
    ]);

    this.pdf.addTable(progressionHeaders, progressionRows, {
      fontSize: 8,
      columnWidths: [50, 22, 20, 20, 22, 26],
    });

    // Summary statistics using globalStats from data processor
    this.pdf.addSpace(5);
    this.pdf.addParagraph(
      L.synthesis
        .replace("{e}", globalStats.enrichedEvents)
        .replace("{t}", globalStats.totalEvents)
        .replace("{p}", globalStats.partiallyTreatedEvents)
        .replace("{n}", globalStats.notTreatedEvents)
        .replace("{rate}", globalStats.enrichmentRate),
    );
  }

  /**
   * Generate 4. SC\u00c9NARIOS OP\u00c9RATIONNELS section
   */
  async generateChapitre4() {
    const L = this.L.ch5;
    const H = this.L.hlp;
    this.pdf.addSectionHeading(L.heading, 1);
    this.pdf.addSectionIntro(L.introTitle, L.introText);

    const workshops = this.reportData.workshops || {};
    const workshop4Data = workshops.workshop4 || {};
    const operationalScenarios = workshop4Data.operationalScenarios || [];

    // 5.1. \u00c9LABORATION DES SC\u00c9NARIOS OP\u00c9RATIONNELS
    this.pdf.addSectionHeading(L.s51, 2);
    if (operationalScenarios.length > 0) {
      // Create table for operational scenarios
      const operationalScenariosHeaders = L.osHeaders;
      const operationalScenariosRows = operationalScenarios.map((os, index) => [
        (index + 1).toString(),
        this.sanitizeScenarioName(os.name) || H.unspecified,
        os.attackPathReference || "N/A",
        os.severity || L.toAssess,
        os.likelihood || L.toAssess,
        os.detectionDifficulty || L.toAssess,
        os.requiredSkills || L.toDefineSkills,
        os.timeline || L.toEstimate,
        (os.operationalSteps?.length || 0).toString(),
      ]);

      this.pdf.addTable(operationalScenariosHeaders, operationalScenariosRows, {
        fontSize: 8,
        columnWidths: [8, 28, 18, 16, 16, 20, 20, 16, 18], // Total 160mm - Reduced by 12mm distributed
      });
    } else {
      this.pdf.addParagraph(L.noneElab);
    }

    // 5.1.1. MOD\u00c9LISATION ET \u00c9VALUATION DES MODES OP\u00c9RATOIRES DES CHEMINS D'ATTAQUE
    this.pdf.addSectionHeading(L.s511, 3);

    if (operationalScenarios.length > 0) {
      // Define phases for organizing actions
      const phases = ["CONNAITRE", "RENTRER", "TROUVER", "EXPLOITER"];
      const phaseLabels = this.L.phases;

      operationalScenarios.forEach((os, index) => {
        // Header for each scenario's mode op\u00e9ratoire
        this.pdf.addSectionHeading(
          `${L.modeOpPrefix} ${os.attackPathReference || `MO${index + 1}`}: ${os.name || H.unspecified}`,
          4,
        );

        // Extract elementary actions using the normalizer
        const elementaryActions = extractElementaryActions(os);

        if (elementaryActions && elementaryActions.length > 0) {
          // Add scenario context summary
          this.pdf.addParagraph(
            L.actionsIntro.replace("{n}", elementaryActions.length),
          );
          this.pdf.addSpace(3);

          // Organize actions by phase
          phases.forEach((phase) => {
            const phaseActions = elementaryActions.filter(
              (ea) => (ea.phase || "").toUpperCase() === phase,
            );

            if (phaseActions.length > 0) {
              // Phase sub-header
              this.pdf.addParagraph(
                `▸ ${phaseLabels[phase] || phase} (${phaseActions.length} action${phaseActions.length > 1 ? "s" : ""})`,
              );

              // Create table for phase actions
              const actionHeaders = L.actionHeaders;
              const actionRows = phaseActions.map((action, actionIndex) => [
                (actionIndex + 1).toString(),
                action.technique || action.name || H.unspecified,
                (action.description || L.noDescription).substring(
                  0,
                  80,
                ) + ((action.description || "").length > 80 ? "..." : ""),
                this.formatProbabilityLevel(
                  action.successProbability || action.stepProbability,
                ),
                this.formatDifficultyLevel(
                  action.technicalDifficulty || action.difficulty,
                ),
              ]);

              this.pdf.addTable(actionHeaders, actionRows, {
                fontSize: 7,
                columnWidths: [8, 35, 65, 22, 22], // Total ~152mm
              });

              this.pdf.addSpace(2);
            }
          });

          // Add actions not matching standard phases
          const otherActions = elementaryActions.filter(
            (ea) => !phases.includes((ea.phase || "").toUpperCase()),
          );

          if (otherActions.length > 0) {
            this.pdf.addParagraph(
              `${L.othersLabel} (${otherActions.length})`,
            );
            const otherHeaders = L.otherHeaders;
            const otherRows = otherActions.map((action, idx) => [
              (idx + 1).toString(),
              action.phase || "N/A",
              action.technique || H.unspecified,
              (action.description || "").substring(0, 50) +
                ((action.description || "").length > 50 ? "..." : ""),
              action.successProbability || "-",
              action.technicalDifficulty || "-",
            ]);

            this.pdf.addTable(otherHeaders, otherRows, {
              fontSize: 7,
              columnWidths: [8, 20, 30, 60, 15, 15],
            });
          }
        } else {
          // Fallback to operationalSteps if no elementary actions
          if (os.operationalSteps?.length > 0) {
            const stepsHeaders = L.stepsHeaders;
            const stepsRows = os.operationalSteps.map((step) => [
              step.phase || "N/A",
              step.technique || "N/A",
              (step.description || L.toComplete).substring(0, 60) +
                ((step.description || "").length > 60 ? "..." : ""),
              step.tools?.join(", ") || "-",
            ]);

            this.pdf.addTable(stepsHeaders, stepsRows, {
              fontSize: 8,
              columnWidths: [25, 35, 70, 30],
            });
          } else {
            this.pdf.addParagraph(L.noneElementary);
          }
        }

        this.pdf.addSpace(5);
      });
    } else {
      this.pdf.addParagraph(L.noneModes);
    }

    // 5.2. CHEMINS D'ATTAQUE IDENTIFI\u00c9S
    const attackPaths = workshop4Data.attackPaths || [];
    if (attackPaths.length > 0) {
      this.pdf.addSectionHeading(L.s52, 2);
      this.pdf.addParagraph(L.apCount.replace("{n}", attackPaths.length));

      const attackPathsHeaders = L.apHeaders;
      const attackPathsRows = attackPaths.map((ap, index) => [
        ap.referenceCode || `${L.apPrefix}${index + 1}`,
        (ap.name || H.unspecified).substring(0, 30) +
          ((ap.name || "").length > 30 ? "..." : ""),
        ap.sourceRiskName || "-",
        ap.objectifVise || "-",
        (ap.stakeholders || [])
          .slice(0, 2)
          .map((s) => (typeof s === "string" ? s : s.name))
          .join(", ") || "-",
        ap.gravity?.toString() || "-",
      ]);

      this.pdf.addTable(attackPathsHeaders, attackPathsRows, {
        fontSize: 8,
        columnWidths: [15, 35, 30, 30, 30, 20],
      });
      this.pdf.addSpace(5);
    }
  }

  /**
   * Generate Threat Intelligence (CTI) section with CVE vulnerabilities
   * @param {Object} workshop4Data - Workshop 4 data containing threat intelligence
   */
  async generateThreatIntelligenceSection(workshop4Data) {
    const threatIntelligence = workshop4Data.threatIntelligence || {};
    const assetThreats = threatIntelligence.assetThreats || {};

    // Check if we have any threat intelligence data
    const hasThreats = Object.keys(assetThreats).length > 0;

    if (!hasThreats) {
      // Try to get from alternate data structure
      const supportAssets =
        this.reportData.workshops?.workshop1?.businessValues?.flatMap(
          (bv) => bv.supportAssets || [],
        ) || [];
      if (supportAssets.length === 0) {
        return; // No CTI data available
      }
    }

    this.pdf.addSectionHeading("5.3. INTELLIGENCE DES MENACES (CTI)", 2);
    this.pdf.addParagraph(
      "Cette section pr\u00e9sente l'analyse des vuln\u00e9rabilit\u00e9s (CVE) identifi\u00e9es pour les biens supports de l'organisation.",
    );

    // Summary statistics
    const summary = threatIntelligence.summary || {};
    if (summary.totalAssets || summary.totalCVEs) {
      this.pdf.addSectionHeading(
        "5.3.1. Synth\u00e8se de l'Intelligence des Menaces",
        3,
      );

      const summaryHeaders = ["M\u00e9trique", "Valeur"];
      const summaryRows = [
        [
          "Biens Supports analys\u00e9s",
          (
            summary.totalAssets ||
            Object.keys(assetThreats).length ||
            0
          ).toString(),
        ],
        ["CVEs identifi\u00e9es", (summary.totalCVEs || 0).toString()],
        ["Techniques MITRE ATT&CK", (summary.totalTechniques || 0).toString()],
      ];

      this.pdf.addTable(summaryHeaders, summaryRows, {
        fontSize: 9,
        columnWidths: [100, 60],
      });
      this.pdf.addSpace(5);
    }

    // Detailed CVE analysis by asset
    if (Object.keys(assetThreats).length > 0) {
      this.pdf.addSectionHeading(
        "5.3.2. Vuln\u00e9rabilit\u00e9s par Bien Support",
        3,
      );

      for (const [assetKey, assetData] of Object.entries(assetThreats)) {
        // Asset header
        this.pdf.addSectionHeading(`Bien Support: ${assetKey}`, 4);

        // Attack path reference code
        if (assetData.referenceCode || assetData.attackPathReference) {
          this.pdf.addParagraph(
            `R\u00e9f\u00e9rence du chemin d'attaque: ${assetData.referenceCode || assetData.attackPathReference}`,
          );
        }

        // Asset details
        const assetDetails = [];
        if (assetData.vendor)
          assetDetails.push(`Fournisseur: ${assetData.vendor}`);
        if (assetData.product)
          assetDetails.push(`Produit: ${assetData.product}`);
        if (assetData.version)
          assetDetails.push(`Version: ${assetData.version}`);
        if (assetData.cpe) assetDetails.push(`CPE: ${assetData.cpe}`);

        if (assetDetails.length > 0) {
          this.pdf.addParagraph(assetDetails.join(" | "));
        }

        if (assetData.riskScore) {
          this.pdf.addParagraph(
            `Score de risque global: ${assetData.riskScore}/10`,
          );
        }

        // Vulnerabilities table
        const vulnerabilities = assetData.vulnerabilities || [];
        if (vulnerabilities.length > 0) {
          const cveHeaders = [
            "CVE ID",
            "Gravit\u00e9",
            "Score",
            "Statut",
            "Description",
          ];
          const cveRows = vulnerabilities.slice(0, 10).map((cve) => {
            // Get severity properly
            let severity = "UNKNOWN";
            if (cve.severity && typeof cve.severity === "object") {
              severity =
                cve.severity.severity || cve.severity.baseSeverity || "UNKNOWN";
            } else if (cve.severity) {
              severity = cve.severity;
            }

            // Get score properly
            let score = "N/A";
            if (
              cve.severity &&
              typeof cve.severity === "object" &&
              cve.severity.score
            ) {
              score = cve.severity.score;
            } else if (cve.score || cve.cvssScore || cve.baseScore) {
              score = cve.score || cve.cvssScore || cve.baseScore;
            }

            // Get status label
            const status = STATUS_LABELS[cve.status] || "Non Corrig\u00e9";

            return [
              cve.cveId || cve.id || "N/A",
              severity,
              score.toString(),
              status,
              (cve.description || "N/A").substring(0, 50) +
                ((cve.description || "").length > 50 ? "..." : ""),
            ];
          });

          this.pdf.addTable(cveHeaders, cveRows, {
            fontSize: 8,
            columnWidths: [28, 20, 15, 25, 72],
          });

          if (vulnerabilities.length > 10) {
            this.pdf.addParagraph(
              `... et ${vulnerabilities.length - 10} autres vuln\u00e9rabilit\u00e9s.`,
            );
          }
        } else {
          this.pdf.addParagraph(
            "Aucune vuln\u00e9rabilit\u00e9 CVE identifi\u00e9e pour ce bien support.",
          );
        }

        // MITRE ATT&CK / ATLAS Techniques table
        const techniques =
          assetData.attackTechniques || assetData.techniques || [];
        if (techniques.length > 0) {
          this.pdf.addParagraph("Techniques d'attaque MITRE ATT&CK / ATLAS:");
          const techHeaders = [
            "ID",
            "Technique",
            "Tactique",
            "Plateformes",
            "Source",
          ];
          const techRows = techniques.slice(0, 10).map((tech) => {
            // Get technique ID
            const techId = tech.id || tech.techniqueId || "N/A";

            // Get technique name
            const techName = tech.name || tech.techniqueName || "N/A";

            // Get tactic
            const tactic = tech.tactic || tech.tactics?.join(", ") || "N/A";

            // Get platforms
            const platforms = Array.isArray(tech.platforms)
              ? tech.platforms.slice(0, 3).join(", ") +
                (tech.platforms.length > 3 ? "..." : "")
              : "N/A";

            // Get source (MITRE ATT&CK or ATLAS)
            let source = "MITRE ATT&CK";
            if (
              tech.isAtlas ||
              tech.source?.includes("ATLAS") ||
              techId.startsWith("AML.")
            ) {
              source = "ATLAS";
            } else if (tech.source) {
              source = tech.source.includes("Local")
                ? "MITRE ATT&CK"
                : tech.source;
            }

            return [
              techId,
              techName.length > 30
                ? techName.substring(0, 30) + "..."
                : techName,
              tactic.length > 25 ? tactic.substring(0, 25) + "..." : tactic,
              platforms,
              source,
            ];
          });

          this.pdf.addTable(techHeaders, techRows, {
            fontSize: 8,
            columnWidths: [22, 45, 40, 35, 28],
          });

          if (techniques.length > 10) {
            this.pdf.addParagraph(
              `... et ${techniques.length - 10} autres techniques.`,
            );
          }
        }

        // Recommendations
        const recommendations = assetData.recommendations || [];
        if (recommendations.length > 0) {
          this.pdf.addParagraph("Recommandations:");
          const recHeaders = ["Priorit\u00e9", "Titre", "Description"];
          const recRows = recommendations
            .slice(0, 5)
            .map((rec) => [
              rec.priority || "N/A",
              rec.title || "N/A",
              (rec.description || "N/A").substring(0, 60) +
                ((rec.description || "").length > 60 ? "..." : ""),
            ]);

          this.pdf.addTable(recHeaders, recRows, {
            fontSize: 8,
            columnWidths: [25, 50, 85],
          });
        }

        this.pdf.addSpace(5);
      }
    }
  }

  /**
   * Generate 5. TRAITEMENT DU RISQUE section
   */
  async generateChapitre5() {
    const L = this.L.ch6;
    const H = this.L.hlp;
    this.pdf.addSectionHeading(L.heading, 1);
    this.pdf.addSectionIntro(L.introTitle, L.introText);

    const workshops = this.reportData.workshops || {};
    const workshop5Data = workshops.workshop5 || {};
    const strategicScenarios = workshop5Data.strategicScenarios || [];
    const riskTreatmentPlans = workshop5Data.riskTreatmentPlans || [];
    const residualRisks = workshop5Data.residualRisks || [];

    // 6.1. STRAT\u00c9GIE ET APP\u00c9TENCE AU RISQUE ET OBJECTIFS DE S\u00c9CURIT\u00c9
    this.pdf.addSectionHeading(L.s61, 2);
    this.pdf.addParagraph(L.balancedStrategy);

    this.pdf.addParagraph(L.secObjTitle);
    this.pdf.addParagraph(L.secObj1);
    this.pdf.addParagraph(L.secObj2);
    this.pdf.addParagraph(L.secObj3);
    this.pdf.addParagraph(L.secObj4);

    this.pdf.addParagraph(L.thresholdsTitle);
    this.pdf.addParagraph(L.thr1);
    this.pdf.addParagraph(L.thr2);
    this.pdf.addParagraph(L.thr3);
    this.pdf.addParagraph(L.thr4);

    // 6.2. SYNTH\u00c8SE DES SC\u00c9NARIOS DE RISQUES
    this.pdf.addSectionHeading(L.s62, 2);
    if (strategicScenarios.length > 0) {
      // Create table for risk scenarios synthesis
      const synthesisHeaders = L.synHeaders;
      const synthesisRows = strategicScenarios.map((ss, index) => {
        const gravity = ss.gravity || 3;
        const vraisemblance =
          ss.operationalScenarios?.[0]?.scenarioVraisemblance || 2;
        const initialRisk = gravity * vraisemblance;

        return [
          (index + 1).toString(),
          ss.referenceCode
            ? `${ss.referenceCode}: ${this.sanitizeScenarioName(ss.name)}`
            : this.sanitizeScenarioName(ss.name),
          `${gravity}/5`,
          `${vraisemblance}/4`,
          `${initialRisk}/20`,
          ss.riskTreatment?.option || L.toDefine,
          `${ss.residualRisk?.riskReduction?.overallReduction || 0}%`,
        ];
      });

      this.pdf.addTable(synthesisHeaders, synthesisRows, { fontSize: 8 });
    } else {
      this.pdf.addParagraph(L.noneSynth);
    }

    // 6.3. IDENTIFICATION DES MESURES DE S\u00c9CURIT\u00c9 DE TRAITEMENT DU RISQUE \u2013 PLAN D'AM\u00c9LIORATION CONTINUE DE LA S\u00c9CURIT\u00c9 (PACS)
    this.pdf.addSectionHeading(L.s63, 2);

    // 6.3.1. COUVERTURE DES RISQUES PAR LES MESURES
    this.pdf.addSectionHeading(L.s631, 3);

    // 6.3.1. COUVERTURE DES RISQUES PAR LES MESURES
    this.pdf.addSectionHeading(
      "6.3.1. COUVERTURE DES RISQUES PAR LES MESURES",
      3,
    );

    // Create table for coverage analysis
    const allMeasures = strategicScenarios.flatMap((s) => [
      ...(s.customMeasures || []),
      ...(s.dreadedEventMeasures || []),
      ...(s.ecosystemMeasures || []),
    ]);
    const measuresPerScenario =
      strategicScenarios.length > 0
        ? Math.round(allMeasures.length / strategicScenarios.length)
        : 0;
    const treatmentStrategies = this.getTreatmentStrategies(strategicScenarios);
    const avgEffectiveness =
      this.calculateAverageEffectiveness(strategicScenarios);

    const coverageHeaders = L.covHeaders;
    const coverageRows = [
      [
        L.covScenarios,
        strategicScenarios.length.toString(),
        L.uTotal,
      ],
      [L.covMeasures, allMeasures.length.toString(), L.uTotal],
      [L.covMeasuresPerScenario, measuresPerScenario.toString(), L.uAverage],
      [
        L.covMeasureTypes,
        Object.keys(treatmentStrategies).length.toString(),
        L.uCategories,
      ],
      [L.covAvgEffectiveness, avgEffectiveness.toString(), "/5"],
    ];

    this.pdf.addTable(coverageHeaders, coverageRows, { fontSize: 8 });

    // 6.3.2. SUIVI DE LA MISE EN ŒUVRE DES MESURES DE S\u00c9CURIT\u00c9
    this.pdf.addSectionHeading(L.s632, 3);
    const implementationStatus =
      this.generateImplementationStatus(strategicScenarios);
    this.pdf.addParagraph(L.implStatusLabel);
    this.pdf.addParagraph(implementationStatus);

    // Create implementation status table
    const statusHeaders = L.statusHeaders;
    const allCustomMeasures = strategicScenarios.flatMap(
      (s) => s.customMeasures || [],
    );
    const allEcosystemMeasures = strategicScenarios.flatMap(
      (s) => s.ecosystemMeasures || [],
    );
    const allDreadedEventMeasures = strategicScenarios.flatMap(
      (s) => s.dreadedEventMeasures || [],
    );

    const implementationRows = [
      [
        L.rowCustom,
        allCustomMeasures
          .filter((m) => m.implementation === "implemented")
          .length.toString(),
        allCustomMeasures
          .filter((m) => m.implementation?.includes("progress"))
          .length.toString(),
        allCustomMeasures
          .filter((m) => !m.implementation || m.implementation === "planned")
          .length.toString(),
        allCustomMeasures.length.toString(),
      ],
      [
        L.rowEcosystem,
        allEcosystemMeasures
          .filter((m) => m.implementation === "implemented")
          .length.toString(),
        allEcosystemMeasures
          .filter((m) => m.implementation?.includes("progress"))
          .length.toString(),
        allEcosystemMeasures
          .filter((m) => !m.implementation || m.implementation === "planned")
          .length.toString(),
        allEcosystemMeasures.length.toString(),
      ],
      [
        "Mesures \u00e9v\u00e9nements redout\u00e9s",
        allDreadedEventMeasures
          .filter((m) => m.implementation === "implemented")
          .length.toString(),
        allDreadedEventMeasures
          .filter((m) => m.implementation?.includes("progress"))
          .length.toString(),
        allDreadedEventMeasures
          .filter((m) => !m.implementation || m.implementation === "planned")
          .length.toString(),
        allDreadedEventMeasures.length.toString(),
      ],
    ];

    this.pdf.addTable(statusHeaders, implementationRows, { fontSize: 8 });

    // 6.3.3. TABLEAU DES RISQUES R\u00c9SIDUELS
    this.pdf.addSectionHeading(L.s633, 3);
    const scenariosWithResidual = strategicScenarios.filter(
      (s) => s.residualRisk,
    );

    if (scenariosWithResidual.length > 0) {
      // Create residual risks table
      const residualHeaders = L.resHeaders;
      const residualRows = scenariosWithResidual.map((ss) => [
        this.sanitizeScenarioName(ss.name || H.unspecified),
        `${ss.residualRisk?.initialRisk?.riskScore || 0}/20`,
        `${ss.residualRisk?.residualRisk?.riskScore || 0}/20`,
        `${ss.residualRisk?.riskReduction?.overallReduction || 0}%`,
        ss.residualRisk?.acceptanceStatus === "accepted"
          ? L.accepted
          : L.toTreat,
        (
          ss.residualRisk?.acceptanceJustification || L.toCompleteShort
        ).substring(0, 30) +
          ((ss.residualRisk?.acceptanceJustification || "").length > 30
            ? "..."
            : ""),
      ]);

      this.pdf.addTable(residualHeaders, residualRows, { fontSize: 7 });
    } else {
      this.pdf.addParagraph(L.noneResidual);
    }
  }

  /**
   * Generate 6. PLAN DE TRAITEMENT DES CONTR\u00d4LES section
   */
  async generateChapitre6() {
    // Removed entire chapter 7 (PLAN DE TRAITEMENT DES CONTR\u00d4LES) as requested
    // This includes sections 7, 7.1, 7.2, 6.3, 6.4, 6.5, and 6.6
  }

  /**
   * Generate RACI Matrix section for Atelier 1
   * @param {Array} participants - List of participants
   */
  generateRaciMatrixSection(participants) {
    this.pdf.addSectionHeading("2.5. MATRICE RACI DE L'ATELIER 1", 2);
    this.pdf.addParagraph(
      "Cette matrice d\u00e9finit les responsabilit\u00e9s de chaque participant pour les activit\u00e9s cl\u00e9s de l'Atelier 1.",
    );

    if (participants.length === 0) {
      this.pdf.addParagraph(
        "Aucun participant d\u00e9fini - matrice RACI non g\u00e9n\u00e9r\u00e9e.",
      );
      return;
    }

    // Define Atelier 1 tasks
    const atelier1Tasks = [
      "D\u00e9finition du p\u00e9rim\u00e8tre d'analyse",
      "Identification des missions de l'organisation",
      "Constitution et organisation de l'\u00e9quipe projet",
      "D\u00e9finition des objectifs et contraintes",
      "Identification des parties prenantes",
      "\u00c9tablissement des hypoth\u00e8ses de travail",
      "Validation finale du cadre d'analyse",
    ];

    // Generate RACI assignments based on participant roles
    const raciHeaders = [
      "Activit\u00e9",
      ...participants
        .slice(0, 5)
        .map((p) => (p.name ? p.name.split(" ")[0] : "P")),
    ];
    const raciRows = atelier1Tasks.map((task) => {
      const row = [task];

      participants.slice(0, 5).forEach((participant, index) => {
        const role = this.assignRaciRoleForTask(
          task,
          participant,
          index,
          participants.length,
        );
        row.push(role);
      });

      return row;
    });

    // Calculate column widths based on number of participants
    const taskColumnWidth = 60;
    const participantCount = Math.min(participants.length, 5);
    const participantColumnWidth = Math.floor(
      (160 - taskColumnWidth) / participantCount,
    );
    const columnWidths = [
      taskColumnWidth,
      ...Array(participantCount).fill(participantColumnWidth),
    ];

    this.pdf.addTable(raciHeaders, raciRows, {
      fontSize: 7,
      columnWidths: columnWidths,
    });

    // Add RACI legend
    this.pdf.addSpace(5);
    this.pdf.addParagraph("L\u00e9gende RACI:");
    this.pdf.addParagraph("  R = Responsable (effectue le travail)");
    this.pdf.addParagraph("  A = Approbateur (valide les d\u00e9cisions)");
    this.pdf.addParagraph("  C = Consult\u00e9 (donne son avis)");
    this.pdf.addParagraph("  I = Inform\u00e9 (est tenu au courant)");
  }

  /**
   * Assign RACI role based on task and participant characteristics
   * @param {string} task - The task name
   * @param {Object} participant - The participant object
   * @param {number} index - Participant index
   * @param {number} totalParticipants - Total number of participants
   * @returns {string} RACI role (R, A, C, I)
   */
  assignRaciRoleForTask(task, participant, index, totalParticipants) {
    const rawPosition = participant.position === 'OTHER' ? (participant.customPosition || '') : (participant.position || '');
    const position = rawPosition.toLowerCase();
    const role = participant.role || "";

    // Identify participant types
    const isRSSI =
      position.includes("rssi") ||
      position.includes("ciso") ||
      position.includes("s\u00e9curit\u00e9");
    const isRiskManager =
      position.includes("risk") || position.includes("risque");
    const isManager =
      position.includes("directeur") ||
      position.includes("manager") ||
      position.includes("responsable");
    const isApprover = role === "A" || role === "Approbateur";

    // Task-specific RACI assignment logic
    if (task.includes("p\u00e9rim\u00e8tre")) {
      if (isRSSI || (index === 0 && !isApprover)) return "R";
      if (isApprover || isManager) return "A";
      return index < 3 ? "C" : "I";
    }

    if (task.includes("missions")) {
      if (isManager || isApprover) return "R";
      if (index === 0) return "A";
      return "I";
    }

    if (task.includes("\u00e9quipe")) {
      if (isApprover) return "A";
      if (isRSSI || index === 0) return "R";
      return "C";
    }

    if (task.includes("objectifs") || task.includes("contraintes")) {
      if (isApprover) return "A";
      if (isRSSI || isRiskManager) return "R";
      return "C";
    }

    if (task.includes("parties prenantes")) {
      if (isApprover) return "A";
      if (index === 0) return "R";
      return "C";
    }

    if (task.includes("hypoth\u00e8ses")) {
      if (isRiskManager || isRSSI) return "R";
      if (isApprover) return "A";
      return "C";
    }

    if (task.includes("validation finale")) {
      if (isApprover || isManager) return "A";
      if (isRSSI || isRiskManager) return "C";
      return "I";
    }

    // Default logic
    if (isApprover) return "A";
    if (index === 0) return "R";
    return index < Math.ceil(totalParticipants / 2) ? "C" : "I";
  }

  /**
   * Generate risk assessment matrices
   */
  async generateRiskMatrices() {
    this.pdf.addSectionHeading("Matrices d'\u00c9valuation des Risques", 1);

    // Risk matrix scatter plot
    if (this.chartData?.riskDistribution?.riskMatrixScatter) {
      this.pdf.addSectionHeading("Matrice Impact vs Vraisemblance", 2);
      await this.chartIntegration.addScatterPlot(
        this.chartData.riskDistribution.riskMatrixScatter,
        { width: 120, height: 100 },
      );
    }

    // Risk distribution charts
    if (this.chartData?.riskDistribution?.severityPieChart) {
      this.pdf.addSectionHeading("Distribution par S\u00e9v\u00e9rit\u00e9", 2);
      await this.chartIntegration.addRiskDistributionChart(
        this.chartData.riskDistribution.severityPieChart,
        { width: 90, height: 70 },
      );
    }

    // Risk treatment matrix
    this.generateRiskTreatmentMatrix();
  }

  /**
   * Generate risk treatment matrix
   */
  generateRiskTreatmentMatrix() {
    this.pdf.addSectionHeading("Matrice de Traitement des Risques", 2);

    const treatmentOptions = [
      {
        risk: "Critique",
        action: "Traiter imm\u00e9diatement",
        color: "danger",
      },
      {
        risk: "\u00c9lev\u00e9",
        action: "Traiter en priorit\u00e9",
        color: "warning",
      },
      { risk: "Moyen", action: "Traiter selon les ressources", color: "info" },
      { risk: "Faible", action: "Surveiller", color: "success" },
    ];

    const headers = [
      "Niveau de Risque",
      "Action Recommand\u00e9e",
      "D\u00e9lai",
    ];
    const rows = treatmentOptions.map((option) => [
      option.risk,
      option.action,
      option.risk === "Critique"
        ? "< 1 mois"
        : option.risk === "\u00c9lev\u00e9"
          ? "< 3 mois"
          : option.risk === "Moyen"
            ? "< 6 mois"
            : "< 12 mois",
    ]);

    this.pdf.addTable(headers, rows);
  }

  /**
   * Generate control recommendations
   */
  async generateControlRecommendations() {
    this.pdf.addSectionHeading("Recommandations de Contr\u00f4les", 1);

    // AI-generated control suggestions
    if (this.aiInsights?.controlSuggestions) {
      this.pdf.addSectionHeading("Contr\u00f4les Sugg\u00e9r\u00e9s par IA", 2);

      // Technical controls
      if (this.aiInsights.controlSuggestions.controles_techniques?.length > 0) {
        this.pdf.addSectionHeading("Contr\u00f4les Techniques", 3);
        const headers = [
          "Contr\u00f4le",
          "Complexit\u00e9",
          "Co\u00fbt",
          "Efficacit\u00e9",
        ];
        const rows =
          this.aiInsights.controlSuggestions.controles_techniques.map(
            (control) => [
              control.nom,
              control.complexite_implementation,
              control.cout_estime,
              control.efficacite_attendue,
            ],
          );
        this.pdf.addTable(headers, rows, { fontSize: 8 });
      }

      // Organizational controls
      if (
        this.aiInsights.controlSuggestions.controles_organisationnels?.length >
        0
      ) {
        this.pdf.addSectionHeading("Contr\u00f4les Organisationnels", 3);
        const headers = [
          "Contr\u00f4le",
          "Complexit\u00e9",
          "Co\u00fbt",
          "Efficacit\u00e9",
        ];
        const rows =
          this.aiInsights.controlSuggestions.controles_organisationnels.map(
            (control) => [
              control.nom,
              control.complexite_implementation,
              control.cout_estime,
              control.efficacite_attendue,
            ],
          );
        this.pdf.addTable(headers, rows, { fontSize: 8 });
      }
    }

    // Control effectiveness chart
    if (this.chartData?.controlEffectiveness?.implementationBarChart) {
      this.pdf.addSectionHeading(
        "\u00c9tat d'Impl\u00e9mentation des Contr\u00f4les",
        2,
      );
      await this.chartIntegration.addBarChart(
        this.chartData.controlEffectiveness.implementationBarChart,
        { width: 120, height: 70 },
      );
    }
  }

  /**
   * Generate visualization dashboard
   */
  async generateVisualizationDashboard() {
    this.pdf.addSectionHeading("Tableaux de Bord et Visualisations", 1);

    // Check if chart data is available
    if (!this.chartData || Object.keys(this.chartData).length === 0) {
      this.pdf.addInfoBox(
        "Donn\u00e9es de visualisation indisponibles",
        "Aucune donn\u00e9e de graphique n'est disponible pour ce rapport.",
        { type: "warning" },
      );
      return;
    }

    // Use the comprehensive chart dashboard from chart integration
    try {
      await this.chartIntegration.addChartDashboard(this.chartData);
    } catch (error) {
      console.error(
        "[ReportContentGenerator] Error generating visualization dashboard:",
        error,
      );
      this.pdf.addInfoBox(
        "Erreur de g\u00e9n\u00e9ration des visualisations",
        "Une erreur s'est produite lors de la g\u00e9n\u00e9ration des graphiques.",
        { type: "danger" },
      );
    }
  }

  /**
   * Generate residual risk analysis
   */
  async generateResidualRiskAnalysis() {
    this.pdf.addSectionHeading("Analyse des Risques R\u00e9siduels", 1);

    const workshop5Data = this.reportData.workshops?.workshop5;

    if (workshop5Data?.residualRisks?.length > 0) {
      this.pdf.addParagraph(
        "Apr\u00e8s application des mesures de traitement, les risques r\u00e9siduels suivants subsistent:",
      );

      const headers = [
        "Risque",
        "Contr\u00f4le",
        "D\u00e9cision",
        "Niveau R\u00e9siduel",
        "Justification",
      ];
      const rows = workshop5Data.residualRisks.map((risk) => [
        risk.controlName || "Non sp\u00e9cifi\u00e9",
        risk.controlId || "N/A",
        risk.decision || "Non d\u00e9finie",
        risk.residualRiskLevel || "Non \u00e9valu\u00e9",
        risk.decision === "accept"
          ? "Risque accept\u00e9"
          : "Risque transf\u00e9r\u00e9",
      ]);

      this.pdf.addTable(headers, rows, { fontSize: 8 });
    } else {
      this.pdf.addInfoBox(
        "Aucun risque r\u00e9siduel",
        "Aucun risque r\u00e9siduel n'a \u00e9t\u00e9 identifi\u00e9 ou tous les risques ont \u00e9t\u00e9 trait\u00e9s.",
        { type: "success" },
      );
    }

    // AI insights on residual risks
    if (this.aiInsights?.riskEvolution) {
      this.pdf.addSectionHeading(
        "\u00c9volution Pr\u00e9visionnelle des Risques",
        2,
      );

      if (this.aiInsights.riskEvolution.evolution_court_terme) {
        const evolution = this.aiInsights.riskEvolution.evolution_court_terme;

        if (evolution.risques_emergents?.length > 0) {
          this.pdf.addSectionHeading(
            "Risques \u00c9mergents \u00e0 Court Terme",
            3,
          );
          this.pdf.addBulletList(evolution.risques_emergents, {
            bulletStyle: "⚠️",
          });
        }

        if (evolution.opportunites_amelioration?.length > 0) {
          this.pdf.addSectionHeading(
            "Opportunit\u00e9s d'Am\u00e9lioration",
            3,
          );
          this.pdf.addBulletList(evolution.opportunites_amelioration, {
            bulletStyle: "💡",
          });
        }
      }
    }
  }

  /**
   * Generate implementation roadmap
   */
  async generateImplementationRoadmap() {
    this.pdf.addSectionHeading("Feuille de Route d'Impl\u00e9mentation", 1);

    const workshop5Data = this.reportData.workshops?.workshop5;

    if (workshop5Data?.implementationPlan) {
      const plan = workshop5Data.implementationPlan;

      // Immediate actions
      if (plan.immediate?.length > 0) {
        this.pdf.addSectionHeading("Actions Imm\u00e9diates (0-3 mois)", 2);
        const headers = ["Contr\u00f4le", "Responsable", "Effort Estim\u00e9"];
        const rows = plan.immediate.map((item) => [
          item.controlName || "Non sp\u00e9cifi\u00e9",
          item.responsiblePerson || "\u00c0 d\u00e9finir",
          item.estimatedEffort || "TBD",
        ]);
        this.pdf.addTable(headers, rows, { fontSize: 8 });
      }

      // Short-term actions
      if (plan.shortTerm?.length > 0) {
        this.pdf.addSectionHeading("Actions \u00e0 Court Terme (3-6 mois)", 2);
        const headers = ["Contr\u00f4le", "Responsable", "Effort Estim\u00e9"];
        const rows = plan.shortTerm.map((item) => [
          item.controlName || "Non sp\u00e9cifi\u00e9",
          item.responsiblePerson || "\u00c0 d\u00e9finir",
          item.estimatedEffort || "TBD",
        ]);
        this.pdf.addTable(headers, rows, { fontSize: 8 });
      }

      // Long-term actions
      if (plan.longTerm?.length > 0) {
        this.pdf.addSectionHeading("Actions \u00e0 Long Terme (6+ mois)", 2);
        const headers = ["Contr\u00f4le", "Responsable", "Effort Estim\u00e9"];
        const rows = plan.longTerm.map((item) => [
          item.controlName || "Non sp\u00e9cifi\u00e9",
          item.responsiblePerson || "\u00c0 d\u00e9finir",
          item.estimatedEffort || "TBD",
        ]);
        this.pdf.addTable(headers, rows, { fontSize: 8 });
      }
    }

    // Timeline chart if available
    if (this.chartData?.timeline?.workshopProgressChart) {
      this.pdf.addSectionHeading("Chronologie de Mise en Œuvre", 2);
      await this.chartIntegration.addLineChart(
        this.chartData.timeline.workshopProgressChart,
        { width: 120, height: 70 },
      );
    }
  }

  /**
   * Generate workshop 5 specific appendices
   */
  generateWorkshop5Appendices() {
    const L = this.L.w5app;
    this.pdf.addSectionHeading(L.annexes, 1);

    // APPENDICE VII : GLOSSAIRE M\u00c9THODE EBIOS RISK MANAGER
    this.pdf.addSectionHeading(L.a7Title, 2);
    this.pdf.addParagraph(L.g1);
    this.pdf.addSpace(1);
    this.pdf.addParagraph(L.g2);
    this.pdf.addSpace(1);
    this.pdf.addParagraph(L.g3);
    this.pdf.addSpace(1);
    this.pdf.addParagraph(L.g4);
    this.pdf.addSpace(1);
    this.pdf.addParagraph(L.g5);
    this.pdf.addSpace(1);
    this.pdf.addParagraph(L.g6);
    this.pdf.addSpace(3);

    // Technical details appendix
    this.generateTechnicalDetails();
  }

  /**
   * Generate appendices
   */
  async generateAppendices() {
    const L = this.L.app;
    this.pdf.addSectionHeading(L.annexes, 1);

    // APPENDICE I : \u00c9CHELLES DES BESOINS DE S\u00c9CURIT\u00c9
    this.pdf.addSectionHeading(L.a1Title, 2);

    const securityScalesHeaders = L.scalesHeaders;
    const securityScalesRows = [L.scaleConf, L.scaleInt, L.scaleDis];

    this.pdf.addTable(securityScalesHeaders, securityScalesRows, {
      fontSize: 9,
      columnWidths: [35, 25, 25, 25, 35], // Balanced columns for security scales
    });

    this.pdf.addSpace(3);

    // APPENDICE II : \u00c9CHELLE DE GRAVIT\u00c9
    this.pdf.addSectionHeading(L.a2Title, 2);
    this.pdf.addParagraph(L.sevIntro);

    const severityHeaders = L.sevHeaders;
    const severityRows = [
      L.sev1,
      L.sev2,
      L.sev3,
      L.sev4,
      L.sev5,
    ];

    this.pdf.addTable(severityHeaders, severityRows, {
      fontSize: 8,
      columnWidths: [15, 30, 55, 35, 25], // Total 160mm - Added 45mm distributed across columns
    });
    this.pdf.addSpace(3);

    // APPENDICE III : CAT\u00c9GORIES DE SOURCES DE RISQUE (SR) ET D'OBJECTIFS VIS\u00c9S (OV)
    this.pdf.addSectionHeading(L.a3Title, 2);

    // Sources de risque table
    this.pdf.addSectionHeading(L.srcSub, 3);
    const sourcesHeaders = L.srcHeaders;
    const sourcesRows = [
      L.src1,
      L.src2,
      L.src3,
      L.src4,
      L.src5,
      L.src6,
    ];

    this.pdf.addTable(sourcesHeaders, sourcesRows, {
      fontSize: 8,
      columnWidths: [12, 30, 55, 30, 33], // Total 160mm - Added 45mm distributed across columns
    });

    this.pdf.addSpace(2);

    // Objectifs vis\u00e9s table
    this.pdf.addSectionHeading(L.objSub, 3);
    const objectivesHeaders = L.objHeaders;
    const objectivesRows = [
      L.obj1,
      L.obj2,
      L.obj3,
      L.obj4,
    ];

    this.pdf.addTable(objectivesHeaders, objectivesRows, {
      fontSize: 8,
      columnWidths: [12, 30, 55, 30, 33], // Total 160mm - Same as sources table
    });
    this.pdf.addSpace(3);

    // APPENDICE IV : \u00c9CHELLE D'\u00c9VALUATION DES COUPLES SR/OV
    this.pdf.addSectionHeading(L.a4Title, 2);
    this.pdf.addParagraph(L.srovIntro);

    const srovHeaders = L.srovHeaders;
    const srovRows = [
      L.srovRes,
      L.srovAct,
      L.srovMot,
      L.srovGlob,
    ];

    // Create colored table for SR/OV scale
    this.pdf.addTable(srovHeaders, srovRows, {
      fontSize: 8,
      columnWidths: [25, 22, 22, 22, 22, 47], // Total 160mm - Added 20mm distributed
      theme: "grid",
    });
    this.pdf.addSpace(3);

    // APPENDICE V : \u00c9CHELLE D'\u00c9VALUATION DES PARTIES PRENANTES
    this.pdf.addSectionHeading(L.a5Title, 2);
    this.pdf.addParagraph(L.stkIntro);

    const stakeholdersHeaders = L.srovHeaders;
    const stakeholdersRows = [
      L.stkThreat,
      L.stkDep,
      L.stkPen,
      L.stkMat,
      L.stkTrust,
    ];

    // Create colored table for stakeholders scale
    this.pdf.addTable(stakeholdersHeaders, stakeholdersRows, {
      fontSize: 8,
      columnWidths: [28, 22, 22, 22, 22, 44], // Total 160mm - Added 18mm distributed
      theme: "grid",
    });
    this.pdf.addSpace(3);

    // APPENDICE VI : \u00c9CHELLES POUR LE CALCUL DE LA VRAISEMBLANCE D'UN SC\u00c9NARIO OP\u00c9RATIONNEL
    this.pdf.addSectionHeading(L.a6Title, 2);
    this.pdf.addParagraph(L.lklIntro);

    // Technical skills required table
    this.pdf.addSectionHeading(L.c1Title, 3);
    const technicalSkillsHeaders = L.skillHeaders;
    const technicalSkillsRows = [
      L.skill1,
      L.skill2,
      L.skill3,
      L.skill4,
    ];

    this.pdf.addTable(technicalSkillsHeaders, technicalSkillsRows, {
      fontSize: 8,
      columnWidths: [18, 70, 72], // Total 160mm - Added 45mm distributed
    });

    this.pdf.addSpace(2);

    // Access required table
    this.pdf.addSectionHeading(L.c2Title, 3);
    const accessHeaders = L.accessHeaders;
    const accessRows = [
      L.acc1,
      L.acc2,
      L.acc3,
      L.acc4,
    ];

    this.pdf.addTable(accessHeaders, accessRows, {
      fontSize: 8,
      columnWidths: [18, 65, 77], // Total 160mm - Added 40mm distributed
    });

    this.pdf.addSpace(2);

    // Time required table
    this.pdf.addSectionHeading(L.c3Title, 3);
    const timeHeaders = L.timeHeaders;
    const timeRows = [
      L.time1,
      L.time2,
      L.time3,
      L.time4,
    ];

    this.pdf.addTable(timeHeaders, timeRows, {
      fontSize: 8,
      columnWidths: [18, 65, 77], // Total 160mm - Same as access table
    });

    this.pdf.addSpace(2);

    // Detection difficulty table
    this.pdf.addSectionHeading(L.c4Title, 3);
    const detectionHeaders = L.detHeaders;
    const detectionRows = [
      L.det1,
      L.det2,
      L.det3,
      L.det4,
    ];

    this.pdf.addTable(detectionHeaders, detectionRows, {
      fontSize: 8,
      columnWidths: [18, 70, 72], // Total 160mm - Same as technical skills table
    });

    this.pdf.addSpace(3);

    // APPENDICE VII : GLOSSAIRE M\u00c9THODE EBIOS RISK MANAGER
    this.pdf.addSectionHeading(L.a7Title, 2);
    this.pdf.addParagraph(L.gloIntro);

    const glossaryHeaders = L.gloHeaders;
    const glossaryRows = [
      L.glo1,
      L.glo2,
      L.glo3,
      L.glo4,
      L.glo5,
      L.glo6,
      L.glo7,
      L.glo8,
      L.glo9,
      L.glo10,
    ];

    this.pdf.addTable(glossaryHeaders, glossaryRows, {
      fontSize: 7,
      columnWidths: [25, 55, 35],
    });
    this.pdf.addSpace(3);
  }

  /**
   * Generate detailed scenarios appendix
   */
  generateDetailedScenarios() {
    const workshop4Data = this.reportData.workshops?.workshop4;

    if (workshop4Data?.operationalScenarios?.length > 0) {
      workshop4Data.operationalScenarios
        .slice(0, 5)
        .forEach((scenario, index) => {
          this.pdf.addSectionHeading(
            `Sc\u00e9nario ${index + 1}: ${scenario.name}`,
            3,
          );

          if (scenario.description) {
            this.pdf.addParagraph(scenario.description);
          }

          // Scenario details table
          const details = [
            [
              "S\u00e9v\u00e9rit\u00e9",
              scenario.severity || "Non \u00e9valu\u00e9e",
            ],
            ["Vraisemblance", scenario.likelihood || "Non \u00e9valu\u00e9e"],
            [
              "Difficult\u00e9 de d\u00e9tection",
              scenario.detectionDifficulty || "Non \u00e9valu\u00e9e",
            ],
            ["Chronologie", scenario.timeline || "Non d\u00e9finie"],
          ];

          this.pdf.addTable(["Attribut", "Valeur"], details, { fontSize: 8 });
          this.pdf.addSpace(5);
        });
    } else {
      this.pdf.addParagraph(
        "Aucun sc\u00e9nario d\u00e9taill\u00e9 disponible.",
      );
    }
  }

  /**
   * Generate control matrix appendix
   */
  generateControlMatrix() {
    const workshop5Data = this.reportData.workshops?.workshop5;

    if (workshop5Data?.riskTreatmentPlans?.length > 0) {
      const headers = [
        "Risque",
        "Contr\u00f4le",
        "Type",
        "Statut",
        "Efficacit\u00e9",
      ];
      const rows = [];

      workshop5Data.riskTreatmentPlans.forEach((plan) => {
        if (plan.controls) {
          plan.controls.forEach((control) => {
            rows.push([
              plan.dreadedEventId || "Non sp\u00e9cifi\u00e9",
              control.name || "Non sp\u00e9cifi\u00e9",
              control.controlType || "Non d\u00e9fini",
              control.decision || "Non d\u00e9finie",
              control.effectiveness || "Non \u00e9valu\u00e9e",
            ]);
          });
        }
      });

      if (rows.length > 0) {
        this.pdf.addTable(headers, rows, { fontSize: 7 });
      } else {
        this.pdf.addParagraph("Aucune matrice de contr\u00f4les disponible.");
      }
    } else {
      this.pdf.addParagraph("Aucune matrice de contr\u00f4les disponible.");
    }
  }

  /**
   * Generate risk register appendix
   */
  generateRiskRegister() {
    const allRisks = [];

    // Collect risks from all workshops
    const workshops = this.reportData.workshops || {};

    if (workshops.workshop1?.dreadedEvents) {
      workshops.workshop1.dreadedEvents.forEach((de) => {
        allRisks.push({
          id: de.id,
          name: de.name,
          type: "\u00c9v\u00e9nement redout\u00e9",
          severity: de.severity,
          source: "Atelier 1",
        });
      });
    }

    if (workshops.workshop4?.operationalScenarios) {
      workshops.workshop4.operationalScenarios.forEach((os) => {
        allRisks.push({
          id: os.id,
          name: os.name,
          type: "Sc\u00e9nario op\u00e9rationnel",
          severity: os.severity,
          likelihood: os.likelihood,
          source: "Atelier 4",
        });
      });
    }

    if (allRisks.length > 0) {
      const headers = [
        "ID",
        "Nom",
        "Type",
        "S\u00e9v\u00e9rit\u00e9",
        "Vraisemblance",
        "Source",
      ];
      const rows = allRisks.map((risk) => [
        risk.id || "N/A",
        risk.name || "Non sp\u00e9cifi\u00e9",
        risk.type,
        risk.severity || "N/A",
        risk.likelihood || "N/A",
        risk.source,
      ]);

      this.pdf.addTable(headers, rows, { fontSize: 7 });
    } else {
      this.pdf.addParagraph("Aucun registre des risques disponible.");
    }
  }

  /**
   * Generate technical details appendix
   */
  generateTechnicalDetails() {
    const L = this.L.w5app;
    const H = this.L.hlp;
    this.pdf.addSectionHeading(L.metaTitle, 3);

    const metadata = this.reportData.metadata || {};
    const technicalDetails = [
      [L.mId, metadata.analysisId || H.notSpecified],
      [L.mName, metadata.analysisName || H.notSpecified],
      [L.mCompany, metadata.companyName || H.notSpecified],
      [L.mCreatedBy, metadata.createdBy || H.notSpecified],
      [L.mCreatedAt, this.formatDate(metadata.createdAt)],
      [L.mModified, this.formatDate(metadata.lastModified)],
      [L.mVersion, metadata.version || "1.0"],
      [L.mStatus, metadata.status || H.notDefined],
    ];

    this.pdf.addTable(L.attrValueHeaders, technicalDetails, {
      fontSize: 8,
    });

    // Generation statistics
    this.pdf.addSectionHeading(L.statsTitle, 3);
    const stats = [
      [L.sDate, new Date().toLocaleString(H.dateLocale)],
      [L.sPages, L.sPagesValue],
      [L.sServices, L.sServicesValue],
      [L.sGenVersion, "2.0.0"],
    ];

    this.pdf.addTable(L.statHeaders, stats, { fontSize: 8 });
  }

  /**
   * Generate glossary
   */
  generateGlossary() {
    this.pdf.addSectionHeading("Glossaire", 1);

    const glossaryTerms = [
      {
        term: "ANSSI",
        definition:
          "Agence nationale de la s\u00e9curit\u00e9 des syst\u00e8mes d'information",
      },
      {
        term: "EBIOS RM",
        definition:
          "Expression des Besoins et Identification des Objectifs de S\u00e9curit\u00e9 - Risk Manager",
      },
      {
        term: "\u00c9v\u00e9nement redout\u00e9",
        definition:
          "\u00c9v\u00e9nement que l'organisation souhaite \u00e9viter car il porterait atteinte \u00e0 ses valeurs m\u00e9tier",
      },
      {
        term: "Source de risque",
        definition:
          "Entit\u00e9 susceptible de porter atteinte aux valeurs m\u00e9tier de l'organisation",
      },
      {
        term: "Sc\u00e9nario strat\u00e9gique",
        definition:
          "Chemin d'attaque g\u00e9n\u00e9rique montrant comment une source de risque peut porter atteinte \u00e0 une valeur m\u00e9tier",
      },
      {
        term: "Sc\u00e9nario op\u00e9rationnel",
        definition:
          "D\u00e9tail technique d'un sc\u00e9nario strat\u00e9gique avec les \u00e9tapes concr\u00e8tes d'attaque",
      },
      {
        term: "Valeur m\u00e9tier",
        definition:
          "\u00c9l\u00e9ment essentiel au fonctionnement de l'organisation qu'il convient de prot\u00e9ger",
      },
      {
        term: "Vraisemblance",
        definition:
          "Probabilit\u00e9 qu'un sc\u00e9nario de risque se r\u00e9alise dans un contexte donn\u00e9",
      },
    ];

    glossaryTerms.forEach((item) => {
      this.pdf.addSectionHeading(item.term, 3);
      this.pdf.addParagraph(item.definition);
      this.pdf.addSpace(3);
    });
  }

  // Helper methods

  /**
   * Get health color based on score
   */
  getHealthColor(score) {
    if (score >= 80) return "#10b981";
    if (score >= 60) return "#3b82f6";
    if (score >= 40) return "#f59e0b";
    return "#ef4444";
  }

  /**
   * Get grade status for badges
   */
  getGradeStatus(grade) {
    if (["A+", "A", "A-"].includes(grade)) return "success";
    if (["B+", "B", "B-"].includes(grade)) return "info";
    if (["C+", "C", "C-"].includes(grade)) return "warning";
    return "danger";
  }

  /**
   * Get completion status for badges
   */
  getCompletionStatus(status) {
    switch (status) {
      case "completed":
        return "success";
      case "in-progress":
        return "warning";
      case "not-started":
        return "danger";
      default:
        return "info";
    }
  }

  /**
   * Map severity to numeric value
   */
  mapSeverityToNumeric(severity) {
    const mapping = {
      "Tr\u00e8s faible": 1,
      "Very Low": 1,
      Faible: 2,
      Low: 2,
      Moyen: 3,
      Medium: 3,
      "\u00c9lev\u00e9": 4,
      High: 4,
      "Tr\u00e8s \u00e9lev\u00e9": 5,
      "Very High": 5,
      Critique: 5,
      Critical: 5,
    };
    return mapping[severity] || 3;
  }

  /**
   * Format date for display
   */
  formatDate(date) {
    if (!date) return this.L.hlp.notSpecified;
    return new Date(date).toLocaleDateString(this.L.hlp.dateLocale);
  }

  /**
   * Get unique security pillars from business values
   */
  getUniqueSecurityPillars(businessValues) {
    const pillars = new Set();
    businessValues.forEach((bv) => {
      if (bv.securityPillars && Array.isArray(bv.securityPillars)) {
        bv.securityPillars.forEach((pillar) => pillars.add(pillar));
      }
    });
    return Array.from(pillars);
  }

  /**
   * Format security pillar name
   */
  formatSecurityPillar(pillar) {
    const pillarMap = this.L.hlp.pillars;
    return pillarMap[pillar] || pillar || this.L.hlp.notDefined;
  }

  /**
   * Format severity level
   */
  formatSeverity(severity) {
    const severityMap = this.L.hlp.severities;
    return severityMap[severity] || severity || this.L.hlp.notDefined;
  }

  /**
   * Format control status
   */
  formatControlStatus(status) {
    const statusMap = {
      implemented: "Impl\u00e9ment\u00e9",
      planned: "Planifi\u00e9",
      "not-implemented": "Non impl\u00e9ment\u00e9",
      "not-applied": "Non appliqu\u00e9",
      partial: "Partiellement impl\u00e9ment\u00e9",
      "partially-applied": "Partiellement appliqu\u00e9",
      applied: "Appliqu\u00e9",
    };
    return statusMap[status] || status || "Non d\u00e9fini";
  }

  /**
   * Get business value name by ID
   */
  getBusinessValueName(businessValueId, businessValues) {
    if (!businessValueId || !businessValues || !Array.isArray(businessValues)) {
      return "Non identifi\u00e9e";
    }

    // Handle different data structures for business value reference
    let bvId = businessValueId;

    // If businessValueId is an object, extract the ID
    if (typeof businessValueId === "object") {
      bvId = businessValueId.id || businessValueId._id || businessValueId.name;
    }

    // Try to find business value by ID, _id, or name
    const bv = businessValues.find(
      (bv) =>
        bv.id === bvId ||
        bv._id === bvId ||
        bv.id?.toString() === bvId?.toString() ||
        bv._id?.toString() === bvId?.toString() ||
        bv.name === bvId,
    );

    return bv?.name || "Non identifi\u00e9e";
  }

  /**
   * Get total security controls
   */
  getTotalSecurityControls(securityControls) {
    if (!securityControls || !securityControls.summary) return 0;
    return securityControls.summary.length;
  }

  /**
   * Format risk source category
   */
  formatRiskSourceCategory(category) {
    const categoryMap = this.L.hlp.sourceCategories;
    return categoryMap[category] || category;
  }

  /**
   * Format objective category
   */
  formatObjectiveCategory(category) {
    const categoryMap = {
      lucratif: "Lucratif",
      entrave: "Entrave",
      influence: "Influence",
      defi: "D\u00e9fi",
      espionnage: "Espionnage",
    };
    return categoryMap[category] || category;
  }

  /**
   * Format level
   */
  formatLevel(level) {
    const levelMap = this.L.hlp.levels;
    return levelMap[level] || level;
  }

  /**
   * Format resources
   */
  formatResources(resources) {
    const resourcesMap = this.L.hlp.resources;
    return resourcesMap[resources] || resources;
  }

  /**
   * Get unique objectives from risk sources
   */
  getUniqueObjectives(riskSources) {
    const objectivesMap = {};
    riskSources.forEach((source) => {
      if (source.objectifVise && source.selected) {
        const key = `${source.objectifVise}-${source.objectifViseCategory}`;
        if (!objectivesMap[key]) {
          objectivesMap[key] = {
            name: source.objectifVise,
            category: source.objectifViseCategory,
            frequency: 0,
          };
        }
        objectivesMap[key].frequency++;
      }
    });
    return Object.values(objectivesMap);
  }

  /**
   * Get dreaded event name by ID
   */
  getDreadedEventName(dreadedEventId, dreadedEvents) {
    // Handle case where eventId might be an object with _id, id, or other properties
    const eventId =
      typeof dreadedEventId === "object"
        ? dreadedEventId.id ||
          dreadedEventId._id ||
          dreadedEventId.name ||
          JSON.stringify(dreadedEventId)
        : dreadedEventId;

    const event = dreadedEvents.find((de) => {
      return (
        de.id === eventId ||
        de._id === eventId ||
        de.id?.toString() === eventId?.toString() ||
        de._id?.toString() === eventId?.toString() ||
        de.name === eventId
      );
    });

    return event?.name || `\u00c9v\u00e9nement ${eventId}`;
  }

  /**
   * Get treatment strategies
   */
  getTreatmentStrategies(strategicScenarios) {
    const strategies = {};
    strategicScenarios.forEach((scenario) => {
      if (scenario.riskTreatment?.option) {
        strategies[scenario.riskTreatment.option] =
          (strategies[scenario.riskTreatment.option] || 0) + 1;
      }
    });
    return strategies;
  }

  /**
   * Calculate average effectiveness
   */
  calculateAverageEffectiveness(strategicScenarios) {
    const allMeasures = strategicScenarios.flatMap((s) => [
      ...(s.customMeasures || []),
      ...(s.dreadedEventMeasures || []),
      ...(s.ecosystemMeasures || []),
    ]);
    if (allMeasures.length === 0) return 0;
    const totalEffectiveness = allMeasures.reduce(
      (sum, m) => sum + (m.effectiveness || 3),
      0,
    );
    return Math.round(totalEffectiveness / allMeasures.length);
  }

  /**
   * Generate implementation status
   */
  generateImplementationStatus(strategicScenarios) {
    const L = this.L.impl;
    const allMeasures = strategicScenarios.flatMap((s) => [
      ...(s.customMeasures || []),
      ...(s.dreadedEventMeasures || []),
      ...(s.ecosystemMeasures || []),
    ]);

    const statusCount = allMeasures.reduce((acc, measure) => {
      const status = measure.implementation || measure.decision || "planned";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const notPlanned =
      allMeasures.length -
      (statusCount.implemented || 0) -
      (statusCount.in_progress || 0) -
      (statusCount.planned || 0);

    return `
${L.implemented.replace("{n}", statusCount.implemented || 0)}
${L.inProgress.replace("{n}", statusCount.in_progress || 0)}
${L.planned.replace("{n}", statusCount.planned || 0)}
${L.notPlanned.replace("{n}", notPlanned)}
    `;
  }

  /**
   * Get total controls in plan
   */
  getTotalControlsInPlan(planData) {
    if (!planData || typeof planData !== "object") return 0;

    let total = 0;

    // Check if planData has a 'plan' key with control objects
    if (planData.plan && typeof planData.plan === "object") {
      Object.values(planData.plan).forEach((planEntry) => {
        if (Array.isArray(planEntry)) {
          // Direct array of controls
          total += planEntry.length;
        } else if (planEntry.controls && Array.isArray(planEntry.controls)) {
          // Controls nested in a 'controls' property
          total += planEntry.controls.length;
        }
      });
      return total;
    }

    // Fallback: check for arrays in the planData (direct structure)
    Object.values(planData).forEach((controls) => {
      if (Array.isArray(controls)) {
        total += controls.length;
      }
    });
    return total;
  }

  /**
   * Get controls by category summary
   */
  getControlsByCategory(planData) {
    if (!planData || !planData.plan || typeof planData.plan !== "object")
      return "Non disponible";

    const categoryCount = {};
    Object.values(planData.plan).forEach((planEntry) => {
      if (planEntry.controls && Array.isArray(planEntry.controls)) {
        planEntry.controls.forEach((control) => {
          const category = control.category || "Non d\u00e9fini";
          categoryCount[category] = (categoryCount[category] || 0) + 1;
        });
      }
    });

    const categories = Object.entries(categoryCount)
      .map(([category, count]) => `${category}: ${count}`)
      .join(", ");

    return categories || "Aucune cat\u00e9gorie";
  }

  /**
   * Get overall implementation status
   */
  getOverallImplementationStatus(planData) {
    if (!planData || !planData.plan || typeof planData.plan !== "object")
      return "Non disponible";

    let total = 0;
    let implemented = 0;
    let inProgress = 0;
    let planned = 0;

    Object.values(planData.plan).forEach((planEntry) => {
      if (planEntry.controls && Array.isArray(planEntry.controls)) {
        planEntry.controls.forEach((control) => {
          total++;
          const status = control.status || "planned";

          if (status === "implemented" || status === "applied") implemented++;
          else if (status === "in_progress" || status === "in-progress")
            inProgress++;
          else planned++;
        });
      }
    });

    if (total === 0) return "Aucun contr\u00f4le";

    const implementedPercent = Math.round((implemented / total) * 100);
    const inProgressPercent = Math.round((inProgress / total) * 100);

    if (implementedPercent >= 80)
      return `Excellent (${implementedPercent}% impl\u00e9ment\u00e9)`;
    if (implementedPercent >= 60)
      return `Bon (${implementedPercent}% impl\u00e9ment\u00e9)`;
    if (implementedPercent >= 40)
      return `Moyen (${implementedPercent}% impl\u00e9ment\u00e9)`;
    if (implementedPercent >= 20)
      return `Faible (${implementedPercent}% impl\u00e9ment\u00e9)`;
    return `Critique (${implementedPercent}% impl\u00e9ment\u00e9)`;
  }

  /**
   * Group controls by dreaded event ID
   */
  groupControlsByDreadedEvent(planData) {
    const controlsByEvent = {};

    if (!planData || !planData.plan || typeof planData.plan !== "object") {
      return controlsByEvent;
    }

    // Iterate through each control plan entry
    Object.entries(planData.plan).forEach(([planId, planEntry]) => {
      const { dreadedEventId, treatmentOption, residualSeverity, controls } =
        planEntry;

      if (dreadedEventId && controls && Array.isArray(controls)) {
        if (!controlsByEvent[dreadedEventId]) {
          controlsByEvent[dreadedEventId] = {
            controls: [],
            treatmentOption,
            residualSeverity,
          };
        }

        // Add controls to the event group
        controlsByEvent[dreadedEventId].controls.push(...controls);
      }
    });

    return controlsByEvent;
  }

  /**
   * Get dreaded event name by ID
   */
  getDreadedEventNameById(eventId, dreadedEvents) {
    if (!dreadedEvents || !Array.isArray(dreadedEvents))
      return `\u00c9v\u00e9nement ${eventId}`;

    const event = dreadedEvents.find((de) => {
      // Try different ID matching approaches
      return (
        de.id === eventId ||
        de._id === eventId ||
        de.id?.toString() === eventId?.toString() ||
        de._id?.toString() === eventId?.toString()
      );
    });

    return event?.name || `\u00c9v\u00e9nement ${eventId}`;
  }

  /**
   * Format treatment option
   */
  formatTreatmentOption(option) {
    const optionMap = {
      reduce: "R\u00e9duction",
      avoid: "\u00c9vitement",
      transfer: "Transfert",
      accept: "Acceptation",
      reduce_residual: "R\u00e9duction des risques r\u00e9siduels",
    };
    return optionMap[option] || option || "Non d\u00e9fini";
  }

  /**
   * Format control decision
   */
  formatControlDecision(decision) {
    const decisionMap = {
      implemented: "Impl\u00e9ment\u00e9",
      applied: "Appliqu\u00e9",
      apply_before: "\u00c0 appliquer avant",
      planned: "Planifi\u00e9",
      partial: "Partiellement impl\u00e9ment\u00e9",
      abandoned: "Abandonn\u00e9",
      "not-applicable": "Non applicable",
    };
    return decisionMap[decision] || decision || "Non d\u00e9fini";
  }

  /**
   * Calculate residual threats after applying ecosystem measures
   * @param {Array} stakeholders - List of stakeholders
   * @param {Array} ecosystemMeasures - List of ecosystem measures
   * @returns {Array} Residual threat data
   */
  calculateResidualThreats(stakeholders, ecosystemMeasures) {
    if (!Array.isArray(stakeholders) || stakeholders.length === 0) {
      return [];
    }

    return stakeholders.map((stakeholder) => {
      const initialThreat =
        stakeholder.threatLevel || stakeholder.menace || "low";
      const appliedMeasuresCount = Array.isArray(ecosystemMeasures)
        ? ecosystemMeasures.filter(
            (measure) =>
              measure.targetStakeholders?.includes(stakeholder.id) ||
              measure.scope === "global",
          ).length
        : 0;

      // Calculate residual threat based on applied measures
      let residualThreat = initialThreat;
      let riskReduction = 0;

      if (appliedMeasuresCount > 0 && typeof initialThreat === "string") {
        const threatLevels = [
          "low",
          "faible",
          "medium",
          "mod\u00e9r\u00e9",
          "high",
          "\u00e9lev\u00e9",
          "critical",
          "critique",
        ];
        const initialIndex = threatLevels.indexOf(initialThreat.toLowerCase());

        if (initialIndex > 0) {
          // Reduce threat level based on number of measures (max 2 levels reduction)
          const reductionLevels = Math.min(
            2,
            Math.floor(appliedMeasuresCount / 2),
          );
          const newIndex = Math.max(0, initialIndex - reductionLevels);
          residualThreat = threatLevels[newIndex];
          riskReduction = ((initialIndex - newIndex) / initialIndex) * 100;
        }
      }

      return {
        id: stakeholder.id,
        name: stakeholder.name || "Partie prenante sans nom",
        initialThreat,
        appliedMeasures: appliedMeasuresCount,
        residualThreat,
        riskReduction: Math.round(riskReduction),
      };
    });
  }

  /**
   * Generate threat cartography summary
   * @param {Array} residualThreats - Residual threat data
   * @returns {Object} Threat summary statistics
   */
  generateThreatCartographySummary(residualThreats) {
    const summary = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      totalReduction: 0,
    };

    if (!Array.isArray(residualThreats) || residualThreats.length === 0) {
      return summary;
    }

    let totalReductionSum = 0;
    let threatsWithReduction = 0;

    residualThreats.forEach((threat) => {
      const level =
        threat.residualThreat && typeof threat.residualThreat === "string"
          ? threat.residualThreat.toLowerCase()
          : "low";

      if (level.includes("critical") || level.includes("critique")) {
        summary.critical++;
      } else if (level.includes("high") || level.includes("\u00e9lev\u00e9")) {
        summary.high++;
      } else if (
        level.includes("medium") ||
        level.includes("mod\u00e9r\u00e9")
      ) {
        summary.medium++;
      } else {
        summary.low++;
      }

      if (threat.riskReduction && threat.riskReduction > 0) {
        totalReductionSum += threat.riskReduction;
        threatsWithReduction++;
      }
    });

    if (threatsWithReduction > 0) {
      summary.totalReduction = totalReductionSum / threatsWithReduction;
    }

    return summary;
  }

  /**
   * Format threat level for display
   * @param {string} threatLevel - Threat level
   * @returns {string} Formatted threat level
   */
  formatThreatLevel(threatLevel) {
    const H = this.L.hlp;
    // Handle null, undefined, or non-string values
    if (!threatLevel || typeof threatLevel !== "string") {
      return H.notDefined;
    }

    const levelMap = H.threatLevels;

    return (
      levelMap[threatLevel?.toLowerCase?.()] || threatLevel || H.notDefined
    );
  }

  /**
   * Format numeric level for display (1-5 scale)
   * @param {number|string} level - The numeric level to format
   * @returns {string} Formatted numeric level
   */
  formatNumericLevel(level) {
    // Handle null, undefined, or empty values
    if (level === null || level === undefined || level === "") {
      return this.L.hlp.notDefined;
    }

    const numLevel = Number(level);

    // Handle invalid numbers
    if (isNaN(numLevel)) {
      return this.L.hlp.notDefined;
    }

    // Format based on 1-5 scale with descriptive labels
    const H = this.L.hlp.numLevels;
    if (numLevel >= 5) return H[5];
    if (numLevel >= 4) return H[4];
    if (numLevel >= 3) return H[3];
    if (numLevel >= 2) return H[2];
    if (numLevel >= 1) return H[1];

    return `${numLevel} - ${this.L.hlp.outOfScaleSuffix}`;
  }

  /**
   * Format priority level for display
   * @param {string} priority - Priority level
   * @returns {string} Formatted priority level
   */
  formatPriority(priority) {
    const priorityMap = this.L.hlp.priorities;
    return (
      priorityMap[priority?.toLowerCase()] || priority || this.L.hlp.notDefined
    );
  }

  /**
   * Format implementation status for display
   * @param {string} status - Implementation status
   * @returns {string} Formatted implementation status
   */
  formatImplementationStatus(status) {
    const statusMap = this.L.hlp.implStatus;
    return statusMap[status?.toLowerCase()] || status || this.L.hlp.notDefined;
  }

  /**
   * Format measure category for display
   * @param {string} category - Measure category
   * @returns {string} Formatted category
   */
  formatMeasureCategory(category) {
    const categoryMap = this.L.hlp.measureCategories;
    return (
      categoryMap[category?.toLowerCase()] || category || this.L.hlp.notDefined
    );
  }

  /**
   * Generate comprehensive initial threat cartography analysis for PDF
   * @param {Array} stakeholders - List of stakeholders
   * @param {Array} ecosystemMeasures - List of ecosystem measures
   */
  async generateInitialThreatCartographyForPDF(
    stakeholders,
    ecosystemMeasures,
  ) {
    const L = this.L.carto;
    console.log("[generateInitialThreatCartographyForPDF] Called with:", {
      stakeholdersCount: Array.isArray(stakeholders)
        ? stakeholders.length
        : "not array",
      stakeholders: stakeholders,
      ecosystemMeasuresCount: Array.isArray(ecosystemMeasures)
        ? ecosystemMeasures.length
        : "not array",
    });

    if (!Array.isArray(stakeholders) || stakeholders.length === 0) {
      console.log(
        "[generateInitialThreatCartographyForPDF] No stakeholders found, returning early",
      );
      this.pdf.addParagraph(L.noStakeholdersCarto);
      this.pdf.addSpace(1);
      this.pdf.addParagraph(L.recoList);
      this.pdf.addParagraph(L.recoItem1);
      this.pdf.addParagraph(L.recoItem2);
      this.pdf.addParagraph(L.recoItem3);
      return;
    }

    console.log(
      "[generateInitialThreatCartographyForPDF] Processing stakeholders:",
      stakeholders.map((s) => ({ name: s.name, threatLevel: s.threatLevel })),
    );

    // Analyze threat distribution
    const threatAnalysis = this.analyzeThreatDistributionPDF(stakeholders);
    const retainedAnalysis = this.analyzeRetainedStakeholdersPDF(stakeholders);

    // Main analysis paragraph
    this.pdf.addParagraph(
      L.cartoIntro
        .replace("{n}", stakeholders.length)
        .replace("{r}", retainedAnalysis.retained),
    );
    this.pdf.addSpace(1);

    // Threat distribution - Pie Chart
    this.pdf.addSectionHeading(L.threatDistTitle, 3);

    // Prepare chart data
    const total = stakeholders.length;
    const chartData = {
      title: L.threatDistTitle,
      data: [],
      explanation: L.chartTotal.replace("{n}", total),
    };

    // Add data points for each threat level
    if (threatAnalysis.critical > 0) {
      chartData.data.push({
        label: L.lblCritical.replace("{n}", threatAnalysis.critical),
        value: threatAnalysis.critical,
        color: "#dc2626", // Red
      });
    }

    if (threatAnalysis.high > 0) {
      chartData.data.push({
        label: L.lblHigh.replace("{n}", threatAnalysis.high),
        value: threatAnalysis.high,
        color: "#ea580c", // Orange
      });
    }

    if (threatAnalysis.medium > 0) {
      chartData.data.push({
        label: L.lblMedium.replace("{n}", threatAnalysis.medium),
        value: threatAnalysis.medium,
        color: "#ca8a04", // Yellow
      });
    }

    if (threatAnalysis.low > 0) {
      chartData.data.push({
        label: L.lblLow.replace("{n}", threatAnalysis.low),
        value: threatAnalysis.low,
        color: "#16a34a", // Green
      });
    }

    if (threatAnalysis.undefined > 0) {
      chartData.data.push({
        label: L.lblUndefined.replace("{n}", threatAnalysis.undefined),
        value: threatAnalysis.undefined,
        color: "#6b7280", // Gray
      });
    }

    // Check if we have data to display
    if (chartData.data.length > 0) {
      try {
        await this.chartIntegration.addRiskDistributionChart(chartData, {
          width: 80,
          height: 60,
          centered: true,
        });
      } catch (error) {
        console.error("Error adding threat distribution pie chart:", error);
        // Fallback to text if chart fails
        this.pdf.addParagraph(L.chartError);
        chartData.data.forEach((item) => {
          const percentage =
            total > 0 ? Math.round((item.value / total) * 100) : 0;
          this.pdf.addParagraph(`• ${item.label}: ${percentage}%`);
        });
      }
    } else {
      this.pdf.addParagraph(L.noChartData);
    }

    this.pdf.addSpace(1);

    // Critical stakeholders
    this.pdf.addSectionHeading(L.criticalIdentified, 3);
    const critical = stakeholders.filter((sh) => sh.critical);

    if (critical.length === 0) {
      this.pdf.addParagraph(L.noneCritical);
    } else {
      critical.forEach((sh, index) => {
        const justification = sh.criticalJustification
          ? `${L.justificationPrefix}${sh.criticalJustification}`
          : L.noJustification;

        this.pdf.addParagraph(
          `${index + 1}. ${sh.name || L.unnamedStakeholder}`,
        );
        this.pdf.addParagraph(`   ${justification}`);
        this.pdf.addParagraph(
          `${L.statusPrefix}${sh.retained ? L.statusRetained : L.statusNotRetained}`,
        );
      });
    }

    this.pdf.addSpace(1);

    // Threat exposure synthesis
    this.pdf.addSectionHeading(L.exposureTitle, 3);
    const retained = stakeholders.filter((sh) => sh.retained);
    const retainedThreatAnalysis = this.analyzeThreatDistributionPDF(retained);
    const totalRetained = retained.length;

    if (totalRetained === 0) {
      this.pdf.addParagraph(L.noneRetainedMsg);
    } else {
      const highRiskCount =
        retainedThreatAnalysis.critical + retainedThreatAnalysis.high;
      const riskPercentage =
        totalRetained > 0
          ? Math.round((highRiskCount / totalRetained) * 100)
          : 0;

      let riskLevel = L.lvlLow;
      if (riskPercentage >= 70) riskLevel = L.lvlCritical;
      else if (riskPercentage >= 40) riskLevel = L.lvlHigh;
      else if (riskPercentage >= 20) riskLevel = L.lvlModerate;

      this.pdf.addParagraph(`${L.exposureLevel} ${riskLevel}`);
      this.pdf.addParagraph(
        L.exposureDetail
          .replace("{n}", totalRetained)
          .replace("{h}", highRiskCount)
          .replace("{p}", riskPercentage),
      );

      // Recommendations
      this.pdf.addSpace(1);
      this.pdf.addSectionHeading(L.recoStrategicTitle, 3);

      if (riskPercentage >= 70) {
        this.pdf.addParagraph(L.recoHigh1);
        this.pdf.addParagraph(L.recoHigh2);
        this.pdf.addParagraph(L.recoHigh3);
      } else if (riskPercentage >= 40) {
        this.pdf.addParagraph(L.recoMid1);
        this.pdf.addParagraph(L.recoMid2);
        this.pdf.addParagraph(L.recoMid3);
      } else if (riskPercentage >= 20) {
        this.pdf.addParagraph(L.recoLow1);
        this.pdf.addParagraph(L.recoLow2);
      } else {
        this.pdf.addParagraph(L.recoDef1);
        this.pdf.addParagraph(L.recoDef2);
      }

      this.pdf.addParagraph(L.recoAlways1);
      this.pdf.addParagraph(L.recoAlways2);
    }
  }

  /**
   * Analyze threat level distribution for PDF
   */
  analyzeThreatDistributionPDF(stakeholders) {
    const distribution = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      undefined: 0,
    };

    stakeholders.forEach((sh) => {
      const level = this.normalizeThreatLevelPDF(sh.threatLevel);
      distribution[level]++;
    });

    return distribution;
  }

  /**
   * Analyze retained stakeholders for PDF
   */
  analyzeRetainedStakeholdersPDF(stakeholders) {
    const retained = stakeholders.filter((sh) => sh.retained === true).length;
    const notRetained = stakeholders.length - retained;

    return { retained, notRetained, total: stakeholders.length };
  }

  /**
   * Normalize threat level to standard categories for PDF
   */
  normalizeThreatLevelPDF(threatLevel) {
    if (!threatLevel) return "undefined";

    const level = String(threatLevel).toLowerCase();
    if (level.includes("critical") || level.includes("critique") || level.includes("certain"))
      return "critical";
    if (
      level.includes("high") ||
      level.includes("\u00e9lev\u00e9") ||
      level.includes("eleve") ||
      level.includes("tr\u00e8s vrais")
    )
      return "high";
    if (
      level.includes("medium") ||
      level.includes("mod\u00e9r\u00e9") ||
      level.includes("modere") ||
      level.includes("vraisemblable")
    )
      return "medium";
    if (level.includes("low") || level.includes("faible") || level.includes("peu vrais")) return "low";

    // Handle numeric values
    const numLevel = Number(threatLevel);
    if (!isNaN(numLevel)) {
      if (numLevel >= 4) return "critical";
      if (numLevel >= 3) return "high";
      if (numLevel >= 2) return "medium";
      return "low";
    }

    return "undefined";
  }

  /**
   * Generate Workshop 5 controls summary for PDF
   */
  generateWorkshop5ControlsSummaryPDF(strategicScenarios) {
    const totalScenarios = strategicScenarios.length;
    const totalCustomMeasures = strategicScenarios.reduce(
      (total, s) => total + (s.customMeasures?.length || 0),
      0,
    );
    const totalEcosystemMeasures = strategicScenarios.reduce(
      (total, s) => total + (s.ecosystemMeasures?.length || 0),
      0,
    );
    const totalDreadedEventMeasures = strategicScenarios.reduce(
      (total, s) => total + (s.dreadedEventMeasures?.length || 0),
      0,
    );
    const totalMeasures =
      totalCustomMeasures + totalEcosystemMeasures + totalDreadedEventMeasures;

    const scenariosWithRiskTreatment = strategicScenarios.filter(
      (s) => s.riskTreatment,
    ).length;
    const scenariosWithResidualRisk = strategicScenarios.filter(
      (s) => s.residualRisk,
    ).length;

    // Create table for general information
    const infoHeaders = ["Information", "Valeur"];
    const infoRows = [
      ["Nombre de sc\u00e9narios strat\u00e9giques", totalScenarios.toString()],
      ["Nombre total de mesures", totalMeasures.toString()],
      ["Mesures personnalis\u00e9es", totalCustomMeasures.toString()],
      ["Mesures \u00e9cosyst\u00e8me", totalEcosystemMeasures.toString()],
      [
        "Mesures \u00e9v\u00e9nements redout\u00e9s",
        totalDreadedEventMeasures.toString(),
      ],
      [
        "Sc\u00e9narios avec traitement de risque",
        scenariosWithRiskTreatment.toString(),
      ],
      [
        "Sc\u00e9narios avec \u00e9valuation r\u00e9siduelle",
        scenariosWithResidualRisk.toString(),
      ],
    ];

    this.pdf.addTable(infoHeaders, infoRows, {
      fontSize: 9,
      columnWidths: [50, 25], // Balanced column widths for information table
    });
  }

  /**
   * Generate Workshop 5 measures by scenario for PDF
   */
  generateWorkshop5MeasuresByScenarioPDF(strategicScenarios) {
    if (strategicScenarios.length === 0) {
      this.pdf.addParagraph("Aucun sc\u00e9nario strat\u00e9gique disponible.");
      return;
    }

    strategicScenarios.forEach((scenario, index) => {
      const customMeasures = scenario.customMeasures || [];
      const ecosystemMeasures = scenario.ecosystemMeasures || [];
      const dreadedEventMeasures = scenario.dreadedEventMeasures || [];
      const totalMeasures =
        customMeasures.length +
        ecosystemMeasures.length +
        dreadedEventMeasures.length;

      this.pdf.addSectionHeading(
        `SC\u00c9NARIO ${index + 1}: ${scenario.name || "Sans nom"}`,
        3,
      );

      // Create measures overview table
      const overviewHeaders = [
        "Type de mesure",
        "Nombre",
        "Priorit\u00e9 principale",
        "Responsable(s)",
      ];
      const customPriority =
        customMeasures.length > 0
          ? this.getPriorityDistribution(customMeasures)
          : "N/A";
      const ecosystemPriority =
        ecosystemMeasures.length > 0
          ? this.getPriorityDistribution(ecosystemMeasures)
          : "N/A";
      const dreadedEventPriority =
        dreadedEventMeasures.length > 0
          ? this.getPriorityDistribution(dreadedEventMeasures)
          : "N/A";

      const overviewRows = [
        [
          "Mesures personnalis\u00e9es",
          customMeasures.length.toString(),
          customPriority,
          this.getUniqueResponsibles(customMeasures),
        ],
        [
          "Mesures \u00e9cosyst\u00e8me",
          ecosystemMeasures.length.toString(),
          ecosystemPriority,
          "N/A",
        ],
      [
        "Mesures \u00e9v\u00e9nements redout\u00e9s",
          dreadedEventMeasures.length.toString(),
          dreadedEventPriority,
          "N/A",
        ],
      ];

      this.pdf.addTable(overviewHeaders, overviewRows, {
        fontSize: 8,
        columnWidths: [40, 25, 35, 60], // Total 160mm - Added 20mm distributed
      });

      // Detailed measures tables for each type
      if (customMeasures.length > 0) {
        this.pdf.addSectionHeading("Mesures Personnalis\u00e9es", 4);
        const customHeaders = [
          "#",
          "Nom",
          "Cat\u00e9gorie",
          "Priorit\u00e9",
          "Responsable",
          "Statut",
        ];
        const customRows = customMeasures.map((measure, idx) => [
          (idx + 1).toString(),
          measure.name || "Sans nom",
          this.formatMeasureCategory(measure.category) || "Non d\u00e9finie",
          this.formatPriority(measure.priority) || "Non d\u00e9finie",
          measure.responsiblePerson || "Non assign\u00e9",
          this.formatImplementationStatus(measure.implementation) ||
            "Non d\u00e9fini",
        ]);

        this.pdf.addTable(customHeaders, customRows, { fontSize: 7 });
      }

      if (ecosystemMeasures.length > 0) {
        this.pdf.addSectionHeading("Mesures \u00c9cosyst\u00e8me", 4);
        const ecosystemHeaders = [
          "#",
          "Titre",
          "Cat\u00e9gorie",
          "Priorit\u00e9",
          "Efficacit\u00e9",
        ];
        const ecosystemRows = ecosystemMeasures.map((measure, idx) => [
          (idx + 1).toString(),
          measure.title || "Sans titre",
          this.formatMeasureCategory(measure.category) || "Non d\u00e9finie",
          this.formatPriority(measure.priority) || "Non d\u00e9finie",
          measure.effectiveness
            ? `${measure.effectiveness}/5`
            : "Non \u00e9valu\u00e9e",
        ]);

        this.pdf.addTable(ecosystemHeaders, ecosystemRows, { fontSize: 7 });
      }

      if (dreadedEventMeasures.length > 0) {
        this.pdf.addSectionHeading(
          "Mesures \u00c9v\u00e9nements Redout\u00e9s",
          4,
        );
        const dreadedEventHeaders = [
          "#",
          "Nom",
          "Type",
          "Priorit\u00e9",
          "Efficacit\u00e9",
        ];
        const dreadedEventRows = dreadedEventMeasures.map((measure, idx) => [
          (idx + 1).toString(),
          measure.name || "Sans nom",
          measure.type || "Non d\u00e9fini",
          this.formatPriority(measure.priority) || "Non d\u00e9finie",
          measure.effectiveness
            ? `${measure.effectiveness}/5`
            : "Non \u00e9valu\u00e9e",
        ]);

        this.pdf.addTable(dreadedEventHeaders, dreadedEventRows, {
          fontSize: 7,
        });
      }

      this.pdf.addSpace(3); // Add space between scenarios
    });
  }

  /**
   * Get priority distribution for measures
   */
  getPriorityDistribution(measures) {
    if (!Array.isArray(measures) || measures.length === 0) return "Aucune";

    const priorities = {};
    measures.forEach((measure) => {
      const priority = measure.priority || "medium";
      priorities[priority] = (priorities[priority] || 0) + 1;
    });

    const sortedPriorities = Object.entries(priorities)
      .sort(([a], [b]) => {
        const order = { high: 0, urgent: 0, medium: 1, low: 2 };
        return (order[a] || 1) - (order[b] || 1);
      })
      .map(
        ([priority, count]) => `${this.formatPriority(priority)} (${count})`,
      );

    return sortedPriorities.join(", ");
  }

  /**
   * Get unique responsibles from measures
   */
  getUniqueResponsibles(measures) {
    if (!Array.isArray(measures) || measures.length === 0) return "Aucun";

    const responsibles = new Set();
    measures.forEach((measure) => {
      if (measure.responsiblePerson) {
        responsibles.add(measure.responsiblePerson);
      }
    });

    return Array.from(responsibles).join(", ") || "Non assign\u00e9s";
  }

  /**
   * Generate Workshop 5 risk treatment for PDF
   */
  generateWorkshop5RiskTreatmentPDF(strategicScenarios) {
    const scenariosWithTreatment = strategicScenarios.filter(
      (s) => s.riskTreatment,
    );

    if (scenariosWithTreatment.length === 0) {
      this.pdf.addParagraph(
        "Aucun traitement de risque d\u00e9fini pour les sc\u00e9narios.",
      );
      return;
    }

    this.pdf.addParagraph(
      "Le tableau suivant pr\u00e9sente les strat\u00e9gies de traitement des risques d\u00e9finies pour chaque sc\u00e9nario strat\u00e9gique:",
    );

    // Create risk treatment summary table
    const summaryHeaders = [
      "Sc\u00e9nario",
      "Strat\u00e9gie de Traitement",
      "Justification",
      "Responsable",
      "Budget Estim\u00e9",
    ];
    const summaryRows = scenariosWithTreatment.map((scenario) => {
      const treatment = scenario.riskTreatment;
      return [
        this.sanitizeScenarioName(scenario.name || "Sans nom"),
        this.formatTreatmentStrategy(treatment.option) || "Non d\u00e9finie",
        (treatment.justification || "Non fournie").substring(0, 40) +
          ((treatment.justification || "").length > 40 ? "..." : ""),
        treatment.responsiblePerson || "Non assign\u00e9",
        treatment.budget || "Non d\u00e9fini",
      ];
    });

    this.pdf.addTable(summaryHeaders, summaryRows, { fontSize: 8 });

    // Detailed risk treatment analysis
    this.pdf.addSpace(3);
    this.pdf.addSectionHeading(
      "Analyse D\u00e9taill\u00e9e du Traitement des Risques",
      3,
    );

    // Aggregate treatment strategies
    const treatmentStats = scenariosWithTreatment.reduce((acc, scenario) => {
      const treatment = scenario.riskTreatment;
      const strategy = treatment.option || "undefined";
      acc[strategy] = (acc[strategy] || 0) + 1;
      return acc;
    }, {});

    // Create treatment strategy distribution table
    const strategyHeaders = [
      "Strat\u00e9gie",
      "Nombre de sc\u00e9narios",
      "Pourcentage",
      "Description",
    ];
    const totalStrategies = scenariosWithTreatment.length;
    const strategyRows = Object.entries(treatmentStats).map(
      ([strategy, count]) => [
        this.formatTreatmentStrategy(strategy),
        count.toString(),
        totalStrategies > 0
          ? Math.round((count / totalStrategies) * 100) + "%"
          : "0%",
        this.getTreatmentStrategyDescription(strategy),
      ],
    );

    this.pdf.addTable(strategyHeaders, strategyRows, { fontSize: 8 });

    // In-depth analysis for major treatment strategies
    const majorStrategies = Object.entries(treatmentStats)
      .filter(([strategy, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);

    if (majorStrategies.length > 0) {
      this.pdf.addSpace(3);
      this.pdf.addSectionHeading(
        "D\u00e9tail des Strat\u00e9gies Principales",
        3,
      );

      majorStrategies.forEach(([strategy, count], index) => {
        const scenariosWithStrategy = scenariosWithTreatment.filter(
          (s) => s.riskTreatment.option === strategy,
        );

        this.pdf.addSectionHeading(
          `${index + 1}. ${this.formatTreatmentStrategy(strategy)} (${count} sc\u00e9nario(s))`,
          4,
        );

        // Create details table for scenarios using this strategy
        const detailsHeaders = [
          "Sc\u00e9nario",
          "Responsable",
          "Budget",
          "Commentaires",
        ];
        const detailsRows = scenariosWithStrategy.map((scenario) => {
          const treatment = scenario.riskTreatment;
          return [
            this.sanitizeScenarioName(scenario.name || "Sans nom"),
            treatment.responsiblePerson || "Non assign\u00e9",
            treatment.budget || "Non d\u00e9fini",
            (
              treatment.comments ||
              treatment.justification ||
              "Sans commentaire"
            ).substring(0, 30) +
              ((treatment.comments || treatment.justification || "").length > 30
                ? "..."
                : ""),
          ];
        });

        this.pdf.addTable(detailsHeaders, detailsRows, { fontSize: 7 });
        this.pdf.addSpace(2);
      });
    }
  }

  /**
   * Format treatment strategy for display
   */
  formatTreatmentStrategy(strategy) {
    if (!strategy || typeof strategy !== "string") return "Non d\u00e9fini";

    const strategyMap = {
      reduce: "R\u00e9duction",
      avoid: "\u00c9vitement",
      transfer: "Transfert",
      accept: "Acceptation",
      reduce_residual: "R\u00e9duction Risques R\u00e9siduels",
      mitigate: "Att\u00e9nuation",
      undefined: "Strat\u00e9gie Non D\u00e9fini",
      not_defined: "Non D\u00e9fini",
    };

    return strategyMap[strategy.toLowerCase()] || strategy;
  }

  /**
   * Get treatment strategy description
   */
  getTreatmentStrategyDescription(strategy) {
    if (!strategy || typeof strategy !== "string")
      return "Strat\u00e9gie non document\u00e9e";

    const descriptionMap = {
      reduce: "R\u00e9duire la probabilit\u00e9 ou l'impact du risque",
      avoid:
        "\u00c9liminer compl\u00e8tement le risque en \u00e9vitant l'activit\u00e9",
      transfer:
        "Transf\u00e9rer le risque \u00e0 un tiers (assurance, contrat)",
      accept:
        "Accepter le risque r\u00e9siduel sans action suppl\u00e9mentaire",
      reduce_residual:
        "R\u00e9duire sp\u00e9cifiquement les risques r\u00e9siduels",
      mitigate: "Att\u00e9nuer partiellement les cons\u00e9quences du risque",
    };

    return (
      descriptionMap[strategy.toLowerCase()] || "Description non disponible"
    );
  }

  /**
   * Generate Workshop 5 temporal evolution for PDF
   */
  generateWorkshop5TemporalEvolutionPDF(strategicScenarios) {
    const scenariosWithTemporal = strategicScenarios.filter(
      (s) => s.temporalEvolution,
    );

    if (scenariosWithTemporal.length === 0) {
      this.pdf.addParagraph("Aucune \u00e9volution temporelle planifi\u00e9e.");
      return;
    }

    scenariosWithTemporal.forEach((scenario, index) => {
      const temporal = scenario.temporalEvolution;
      const timelineData = temporal.timelineData || {};

      this.pdf.addSectionHeading(
        `SC\u00c9NARIO: ${scenario.name || "Sans nom"}`,
        3,
      );

      if (timelineData.immediate?.measures?.length > 0) {
        this.pdf.addParagraph(
          `MESURES IMM\u00c9DIATES (${timelineData.immediate.measures.length}):`,
        );
        timelineData.immediate.measures.forEach((measure, idx) => {
          this.pdf.addParagraph(
            `  ${idx + 1}. ${measure.name || measure.title || "Sans nom"}`,
          );
        });
      }

      if (timelineData.shortTerm?.measures?.length > 0) {
        this.pdf.addParagraph(
          `MESURES COURT TERME (${timelineData.shortTerm.measures.length}):`,
        );
        timelineData.shortTerm.measures.forEach((measure, idx) => {
          this.pdf.addParagraph(
            `  ${idx + 1}. ${measure.name || measure.title || "Sans nom"}`,
          );
        });
      }

      if (timelineData.mediumTerm?.measures?.length > 0) {
        this.pdf.addParagraph(
          `MESURES MOYEN TERME (${timelineData.mediumTerm.measures.length}):`,
        );
        timelineData.mediumTerm.measures.forEach((measure, idx) => {
          this.pdf.addParagraph(
            `  ${idx + 1}. ${measure.name || measure.title || "Sans nom"}`,
          );
        });
      }

      this.pdf.addSpace(2);
    });
  }

  /**
   * Generate Workshop 5 implementation plan for PDF
   */
  generateWorkshop5ImplementationPlanPDF(strategicScenarios) {
    const allMeasures = [];

    strategicScenarios.forEach((scenario) => {
      const measures = [
        ...(scenario.customMeasures || []),
        ...(scenario.ecosystemMeasures || []),
        ...(scenario.dreadedEventMeasures || []),
      ];

      measures.forEach((measure) => {
        allMeasures.push({
          scenario: scenario.name,
          name: measure.name || measure.title,
          priority: measure.priority,
          implementation: measure.implementation,
          responsiblePerson: measure.responsiblePerson,
          temporalPosition: measure.temporalPosition,
        });
      });
    });

    if (allMeasures.length === 0) {
      this.pdf.addParagraph("Aucune mesure \u00e0 impl\u00e9menter.");
      return;
    }

    // Group by priority
    const byPriority = {
      high: allMeasures.filter((m) => m.priority === "high"),
      medium: allMeasures.filter((m) => m.priority === "medium"),
      low: allMeasures.filter((m) => m.priority === "low"),
    };

    this.pdf.addParagraph(
      `PLAN D'IMPL\u00c9MENTATION (${allMeasures.length} mesures au total):`,
    );

    if (byPriority.high.length > 0) {
      this.pdf.addSectionHeading(
        `PRIORIT\u00c9 \u00c9LEV\u00c9E (${byPriority.high.length} mesures)`,
        3,
      );
      byPriority.high.forEach((measure, idx) => {
        this.pdf.addParagraph(`${idx + 1}. ${measure.name || "Sans nom"}`);
        this.pdf.addParagraph(`   Sc\u00e9nario: ${measure.scenario}`);
        this.pdf.addParagraph(
          `   Responsable: ${measure.responsiblePerson || "Non assign\u00e9"}`,
        );
      });
    }

    if (byPriority.medium.length > 0) {
      this.pdf.addSectionHeading(
        `PRIORIT\u00c9 MOYENNE (${byPriority.medium.length} mesures)`,
        3,
      );
      byPriority.medium.forEach((measure, idx) => {
        this.pdf.addParagraph(`${idx + 1}. ${measure.name || "Sans nom"}`);
        this.pdf.addParagraph(`   Sc\u00e9nario: ${measure.scenario}`);
      });
    }

    if (byPriority.low.length > 0) {
      this.pdf.addSectionHeading(
        `PRIORIT\u00c9 FAIBLE (${byPriority.low.length} mesures)`,
        3,
      );
      byPriority.low.forEach((measure, idx) => {
        this.pdf.addParagraph(`${idx + 1}. ${measure.name || "Sans nom"}`);
      });
    }
  }

  /**
   * Generate Workshop 5 residual risks for PDF
   */
  generateWorkshop5ResidualRisksPDF(strategicScenarios) {
    const scenariosWithResidual = strategicScenarios.filter(
      (s) => s.residualRisk,
    );

    if (scenariosWithResidual.length === 0) {
      this.pdf.addParagraph(
        "Aucune \u00e9valuation de risque r\u00e9siduel disponible.",
      );
      return;
    }

    this.pdf.addParagraph(
      "Le tableau ci-dessous pr\u00e9sente l'\u00e9valuation des risques r\u00e9siduels apr\u00e8s mise en place des mesures de traitement:",
    );

    // Create comprehensive residual risks summary table
    const summaryHeaders = [
      "Sc\u00e9nario",
      "Risque Initial",
      "Risque R\u00e9siduel",
      "R\u00e9duction (%)",
      "Statut d'acceptation",
      "Justification",
    ];
    const summaryRows = scenariosWithResidual.map((scenario) => {
      const residual = scenario.residualRisk;
      const initial = residual.initialRisk || {};
      const residualRisk = residual.residualRisk || {};
      const reduction = residual.riskReduction || {};

      return [
        this.sanitizeScenarioName(scenario.name || "Sans nom"),
        initial.riskScore ? `${initial.riskScore}/20` : "Non \u00e9valu\u00e9",
        residualRisk.riskScore
          ? `${residualRisk.riskScore}/20`
          : "Non \u00e9valu\u00e9",
        `${reduction.overallReduction || 0}%`,
        residual.acceptanceStatus === "accepted"
          ? "Accept\u00e9"
          : residual.acceptanceStatus === "rejected"
            ? "Rejet\u00e9"
            : "\u00c0 d\u00e9cider",
        (residual.acceptanceJustification || "Non justifi\u00e9").substring(
          0,
          25,
        ) + ((residual.acceptanceJustification || "").length > 25 ? "..." : ""),
      ];
    });

    this.pdf.addTable(summaryHeaders, summaryRows, {
      fontSize: 7,
      columnWidths: [35, 20, 20, 20, 25, 30], // Balanced column widths for residual risk analysis
    });

    // Detailed risk reduction analysis
    this.pdf.addSpace(3);
    this.pdf.addSectionHeading(
      "Analyse D\u00e9taill\u00e9e de la R\u00e9duction des Risques",
      3,
    );

    // Create risk reduction breakdown table
    const reductionHeaders = [
      "Sc\u00e9nario",
      "Gravit\u00e9 Initiale",
      "Gravit\u00e9 R\u00e9siduelle",
      "Vraisemblance Initiale",
      "Vraisemblance R\u00e9siduelle",
      "R\u00e9duction Totale",
    ];
    const reductionRows = scenariosWithResidual.map((scenario) => {
      const residual = scenario.residualRisk;
      const initial = residual.initialRisk || {};
      const residualRisk = residual.residualRisk || {};
      const reduction = residual.riskReduction || {};

      return [
        this.sanitizeScenarioName(scenario.name || "Sans nom"),
        initial.gravity ? `${initial.gravity}/5` : "N/A",
        residualRisk.gravity ? `${residualRisk.gravity}/5` : "N/A",
        initial.likelihood ? `${initial.likelihood}/4` : "N/A",
        residualRisk.likelihood ? `${residualRisk.likelihood}/4` : "N/A",
        `${reduction.overallReduction || 0}%`,
      ];
    });

    this.pdf.addTable(reductionHeaders, reductionRows, {
      fontSize: 7,
      columnWidths: [30, 20, 20, 22, 22, 26], // Optimized column widths for risk reduction details
    });

    // Acceptance status analysis
    this.pdf.addSpace(3);
    this.pdf.addSectionHeading(
      "Statut d'Acceptation des Risques R\u00e9siduels",
      3,
    );

    const acceptanceStats = scenariosWithResidual.reduce((acc, scenario) => {
      const status = scenario.residualRisk?.acceptanceStatus || "pending";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Create acceptance status summary table
    const acceptanceHeaders = [
      "Statut d'acceptation",
      "Nombre de sc\u00e9narios",
      "Pourcentage",
      "Actions requises",
    ];
    const totalResidual = scenariosWithResidual.length;
    const acceptanceRows = Object.entries(acceptanceStats).map(
      ([status, count]) => [
        this.formatAcceptanceStatus(status),
        count.toString(),
        totalResidual > 0
          ? Math.round((count / totalResidual) * 100) + "%"
          : "0%",
        this.getAcceptanceActionRequired(status),
      ],
    );

    this.pdf.addTable(acceptanceHeaders, acceptanceRows, { fontSize: 8 });

    // Risk categories after treatment
    this.pdf.addSpace(3);
    this.pdf.addSectionHeading(
      "Cat\u00e9gorisation des Risques R\u00e9siduels",
      3,
    );

    const riskCategories = {
      acceptable: scenariosWithResidual.filter((s) => {
        const score = s.residualRisk?.residualRisk?.riskScore || 0;
        return score <= 8;
      }).length,
      moderate: scenariosWithResidual.filter((s) => {
        const score = s.residualRisk?.residualRisk?.riskScore || 0;
        return score > 8 && score <= 12;
      }).length,
      critical: scenariosWithResidual.filter((s) => {
        const score = s.residualRisk?.residualRisk?.riskScore || 0;
        return score > 12;
      }).length,
      unevaluated: strategicScenarios.length - scenariosWithResidual.length,
    };

    // Create risk categorization table
    const categoryHeaders = [
      "Niveau de Risque R\u00e9siduel",
      "Nombre de sc\u00e9narios",
      "Pourcentage",
      "Strat\u00e9gie recommand\u00e9e",
    ];
    const riskCategoryRows = [
      [
        "Acceptable (≤8/20)",
        riskCategories.acceptable.toString(),
        totalResidual > 0
          ? Math.round((riskCategories.acceptable / totalResidual) * 100) + "%"
          : "0%",
        "Surveillance continue",
      ],
      [
        "Mod\u00e9r\u00e9 (9-12/20)",
        riskCategories.moderate.toString(),
        totalResidual > 0
          ? Math.round((riskCategories.moderate / totalResidual) * 100) + "%"
          : "0%",
        "Surveillance renforc\u00e9e",
      ],
      [
        "Critique (>12/20)",
        riskCategories.critical.toString(),
        totalResidual > 0
          ? Math.round((riskCategories.critical / totalResidual) * 100) + "%"
          : "0%",
        "Action corrective prioritaire",
      ],
    ];

    if (riskCategories.unevaluated > 0) {
      riskCategoryRows.push([
        "Non \u00e9valu\u00e9",
        riskCategories.unevaluated.toString(),
        Math.round(
          (riskCategories.unevaluated / strategicScenarios.length) * 100,
        ) + "%",
        "\u00c9valuation requise",
      ]);
    }

    this.pdf.addTable(categoryHeaders, riskCategoryRows, { fontSize: 8 });

    // Add reformulation analysis if available
    const scenariosWithReformulation = scenariosWithResidual.filter(
      (s) => s.residualRisk?.reformulation,
    );
    if (scenariosWithReformulation.length > 0) {
      this.pdf.addSpace(3);
      this.pdf.addSectionHeading("R\u00e9formulation des Risques", 3);

      this.pdf.addParagraph(
        "Les sc\u00e9narios suivants ont \u00e9t\u00e9 reformul\u00e9s suite \u00e0 l'\u00e9valuation des risques r\u00e9siduels:",
      );

      // Create reformulation table
      const reformHeaders = [
        "Sc\u00e9nario",
        "Formulation initiale",
        "R\u00e9formulation",
        "Raison",
      ];
      const reformRows = scenariosWithReformulation.map((scenario) => {
        const residual = scenario.residualRisk;
        return [
          this.sanitizeScenarioName(scenario.name || "Sans nom"),
          (scenario.description || "Non d\u00e9crite").substring(0, 25) +
            ((scenario.description || "").length > 25 ? "..." : ""),
          (residual.reformulation || "Non reformul\u00e9").substring(0, 25) +
            ((residual.reformulation || "").length > 25 ? "..." : ""),
          residual.reformulationReason || "Non sp\u00e9cifi\u00e9e",
        ];
      });

      this.pdf.addTable(reformHeaders, reformRows, {
        fontSize: 7,
        columnWidths: [25, 25, 25, 25], // Balanced columns for reformulation analysis
      });
    }
  }

  /**
   * Format acceptance status for display
   */
  formatAcceptanceStatus(status) {
    if (!status || typeof status !== "string") return "\u00c0 d\u00e9cider";

    const statusMap = {
      accepted: "Accept\u00e9",
      rejected: "Rejet\u00e9",
      pending: "En attente",
      under_review: "En r\u00e9vision",
      approved: "Approuv\u00e9",
    };

    return statusMap[status.toLowerCase()] || status;
  }

  /**
   * Get acceptance action required based on status
   */
  getAcceptanceActionRequired(status) {
    if (!status || typeof status !== "string")
      return "Validation comit\u00e9 de direction requise";

    const actionMap = {
      accepted: "Surveillance continue",
      rejected: "Mesures correctives suppl\u00e9mentaires",
      pending: "Validation comit\u00e9 de direction requise",
      under_review: "R\u00e9vision en cours",
      approved: "Surveillance continue",
    };

    return actionMap[status.toLowerCase()] || "Action \u00e0 d\u00e9finir";
  }

  /**
   * Get default threat level when calculation fails
   * @param {Object} stakeholder - The stakeholder object
   * @returns {string} Default threat level display
   */
  getDefaultThreatLevel(stakeholder) {
    // Try to infer from available data
    if (
      stakeholder.threatLevel &&
      typeof stakeholder.threatLevel === "string"
    ) {
      return this.formatThreatLevel(stakeholder.threatLevel);
    }

    // Calculate based on other attributes if available
    const dependency = Number(stakeholder.dependency) || 0;
    const penetration = Number(stakeholder.penetration) || 0;
    const cyberMaturity = Number(stakeholder.cyberMaturity) || 3;

    if (dependency >= 4 || penetration >= 4) {
      return this.L.hlp.threatHigh;
    } else if (dependency >= 2 || penetration >= 2) {
      return this.L.hlp.threatModerate;
    } else {
      return this.L.hlp.threatLow;
    }
  }

  /**
   * Get default trust level when calculation fails
   * @param {Object} stakeholder - The stakeholder object
   * @returns {string} Default trust level display
   */
  getDefaultTrustLevel(stakeholder) {
    // Try to infer from available data
    if (stakeholder.trustLevel && typeof stakeholder.trustLevel === "string") {
      return this.formatNumericLevel(stakeholder.trustLevel);
    }

    // Default to medium trust level
    return this.L.hlp.defaultTrust;
  }

  /**
   * Get default numeric level when calculation fails
   * @param {number|string} level - The level value
   * @returns {string} Default numeric level display
   */
  getDefaultNumericLevel(level) {
    if (level !== null && level !== undefined && level !== "") {
      return this.formatNumericLevel(level);
    }

    // Default to medium level
    return this.L.hlp.defaultTrust;
  }

  /**
   * Sanitize scenario names by fixing common text issues
   */
  sanitizeScenarioName(name) {
    if (!name) return this.L.hlp.unspecified;

    // Convert to string first
    name = String(name);

    // Fix spaced characters first (this is the main fix for the issue)
    name = this.fixSpacedText(name);

    // Clean up spaces and encoding issues
    name = name.replace(/\s+/g, " ").trim();

    // Remove problematic encoding artifacts
    name = name.replace(
      /[^\x20-\x7E\u00C0-\u017F\u00C7\u00E7\u00E0-\u00FF]/g,
      "",
    );

    return name || this.L.hlp.unspecified;
  }

  /**
   * Detect and fix text that has been corrupted with spaces between characters
   * Based on the logic from the PDF generator
   */
  fixSpacedText(text) {
    if (!text) return "";

    // Convert to string first
    text = String(text);

    // Only fix if we detect the specific pattern of single characters with spaces
    // Pattern: single char + space + single char (repeated)
    // This matches "S c e n a r i o" but NOT "normal text with spaces"
    const spacedPattern = /^(\w\s){3,}\w*$/;

    if (spacedPattern.test(text.trim())) {
      console.log("Detected spaced text:", text);
      // Remove spaces between single characters only
      const fixed = text.replace(/(\w)\s+(?=\w)/g, "$1");
      console.log("Fixed to:", fixed);
      return fixed;
    }

    // Also check for patterns like "2 0 F a i b l e" (numbers and letters with spaces)
    const mixedSpacedPattern = /^(\w\s){2,}/;
    if (mixedSpacedPattern.test(text) && text.length > 10) {
      // Count spaces vs characters
      const spaceCount = (text.match(/\s/g) || []).length;
      const charCount = (text.match(/\w/g) || []).length;

      // If we have almost as many spaces as characters, it's likely spaced text
      if (spaceCount > charCount * 0.4) {
        console.log("Detected mixed spaced text:", text);
        const fixed = text.replace(/(\w)\s+(?=\w)/g, "$1");
        console.log("Fixed to:", fixed);
        return fixed;
      }
    }

    return text;
  }

  /**
   * Format probability/success level for display
   * @param {number|string} level - Probability level 1-4
   * @returns {string} Formatted level text
   */
  formatProbabilityLevel(level) {
    const numLevel = parseInt(level);
    if (isNaN(numLevel)) return level || "-";

    const labels = this.L.hlp.probLevels;
    return labels[numLevel] || `${numLevel}`;
  }

  /**
   * Format technical difficulty level for display
   * @param {number|string} level - Difficulty level 1-4
   * @returns {string} Formatted level text
   */
  formatDifficultyLevel(level) {
    const numLevel = parseInt(level);
    if (isNaN(numLevel)) return level || "-";

    const labels = this.L.hlp.diffLevels;
    return labels[numLevel] || `${numLevel}`;
  }

  /**
   * Format vraisemblance level for display
   * @param {number|string} level - Vraisemblance level 1-4
   * @returns {string} Formatted level text
   */
  formatVraisemblanceLevel(level) {
    const numLevel = parseInt(level);
    if (isNaN(numLevel)) return level || "-";

    const labels = {
      1: "1 - Faible",
      2: "2 - Mod\u00e9r\u00e9e",
      3: "3 - \u00c9lev\u00e9e",
      4: "4 - Tr\u00e8s \u00e9lev\u00e9e",
    };
    return labels[numLevel] || `${numLevel}`;
  }
}

export default ReportContentGenerator;
